/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  FilePlus, 
  QrCode, 
  Map, 
  Truck, 
  History, 
  CheckCircle, 
  AlertTriangle, 
  RefreshCw, 
  Mail, 
  MessageSquare, 
  FileText, 
  ChevronRight, 
  Printer, 
  Copy, 
  Search, 
  MapPin, 
  Clock, 
  Plus, 
  Trash2,
  DollarSign,
  Undo,
  Camera,
  Upload,
  Edit,
  FileDown,
  ExternalLink,
  Filter,
  X,
  Navigation,
  Route,
  Share2,
  CheckCircle2,
  Receipt,
  Building2,
  Link2,
  AlertCircle,
  Flame,
  FlaskConical,
  Sparkles,
  Hash,
  SlidersHorizontal,
  Eye
} from 'lucide-react';
import { Folio, Client, CostCenter, Destination, GoodsItem, ProcessStage, StageHistory, isClientRole, isClientUser, isClientOperatorUser, shouldHidePricesForUser, isChoferUser, isChoferRole, isAdminRole, DriverLocation, PoRecord, ProcessType } from '../types.ts';
import { downloadFolioPDF as generateCentralFolioPDF } from '../utils/pdfGenerator.ts';
import LogisticsMap from './LogisticsMap.tsx';
import FolioRealtimeMapModal from './FolioRealtimeMapModal.tsx';
import { fcmService } from '../lib/fcmService.ts';
import { dataService } from '../lib/dataService.ts';

interface FolioManagerProps {
  folios: Folio[];
  clients: Client[];
  costCenters: CostCenter[];
  destinations?: Destination[];
  goods: GoodsItem[];
  currentUser: { name: string; role: string; clientCode?: string; assignedClientIds?: string[]; assignedCostCenterIds?: string[] };
  onUpdateFolio: (folio: Folio, previousFolioId?: string) => void;
  onCreateFolio: (folio: Folio) => void;
  onDeleteFolio?: (id: string) => void;
  isOnline?: boolean;
  defaultShowHistory?: boolean;
  isLoading?: boolean;
  isSyncing?: boolean;
  activeClient?: Client | null;
  selectedCostCenter?: CostCenter | null;
}

const STAGE_LABELS: Record<ProcessStage, { label: string; color: string; desc: string }> = {
  'RECOLECCION': { label: 'Recolección', color: 'bg-indigo-100 text-indigo-700 border-indigo-200', desc: 'Chofer recolectando piezas en planta del cliente' },
  'ENTRADA_CEDIS': { label: 'Entrada CEDIS', color: 'bg-blue-100 text-blue-700 border-blue-200', desc: 'Ingreso al Centro de Distribución y pesaje' },
  'DECAPADO_TERMICO_QUIMICO': { label: 'Decapado T/Q', color: 'bg-amber-100 text-amber-700 border-amber-200', desc: 'Tratamiento químico y térmico superficial' },
  'DECAPADO_LAVADO': { label: 'Lavado', color: 'bg-teal-100 text-teal-700 border-teal-200', desc: 'Lavado de alta presión (se salta si tiene granallado)' },
  'INSPECCION': { label: 'Inspección CC', color: 'bg-purple-100 text-purple-700 border-purple-200', desc: 'Inspección de control de calidad superficial' },
  'RETRABAJO': { label: 'Retrabajo', color: 'bg-rose-100 text-rose-700 border-rose-200', desc: 'Falla de inspección: reprocesamiento secundario' },
  'SALIDA_CEDIS': { label: 'Salida CEDIS', color: 'bg-orange-100 text-orange-700 border-orange-200', desc: 'Embarque y traslado en ruta en tiempo real' },
  'ENTREGA_PLANTA': { label: 'Entrega Planta', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', desc: 'Entregado formalmente en planta del cliente' },
  'DEVOLUCION': { label: 'Devolución', color: 'bg-red-100 text-red-700 border-red-200', desc: 'Retorno registrado en entrega por el cliente' }
};

const STAGE_ORDER: ProcessStage[] = [
  'RECOLECCION',
  'ENTRADA_CEDIS',
  'DECAPADO_TERMICO_QUIMICO',
  'DECAPADO_LAVADO', // conditional
  'INSPECCION',
  'SALIDA_CEDIS',
  'ENTREGA_PLANTA'
];

const SIMULATED_PHOTOS = [
  {
    name: 'Pieza de Acero - Decapado Térmico',
    url: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="300" height="200" viewBox="0 0 300 200"><rect width="300" height="200" fill="%23718096"/><rect x="20" y="20" width="260" height="160" rx="10" fill="none" stroke="%23cbd5e0" stroke-width="8"/><line x1="20" y1="20" x2="280" y2="180" stroke="%23cbd5e0" stroke-width="4"/><circle cx="150" cy="100" r="40" fill="none" stroke="%23e2e8f0" stroke-width="6"/><text x="150" y="190" fill="white" font-size="10" font-family="sans-serif" text-anchor="middle">SIMULACION: PIEZA DE ACERO DECAPADO</text></svg>'
  },
  {
    name: 'Tubería Estructural - Limpieza',
    url: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="300" height="200" viewBox="0 0 300 200"><rect width="300" height="200" fill="%234a5568"/><line x1="50" y1="30" x2="250" y2="30" stroke="%23a0aec0" stroke-width="15" stroke-linecap="round"/><line x1="50" y1="70" x2="250" y2="70" stroke="%23a0aec0" stroke-width="15" stroke-linecap="round"/><line x1="50" y1="110" x2="250" y2="110" stroke="%23a0aec0" stroke-width="15" stroke-linecap="round"/><line x1="50" y1="150" x2="250" y2="150" stroke="%23a0aec0" stroke-width="15" stroke-linecap="round"/><text x="150" y="190" fill="white" font-size="10" font-family="sans-serif" text-anchor="middle">SIMULACION: TUBERIA ESTRUCTURAL</text></svg>'
  },
  {
    name: 'Chasis de Remolque',
    url: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="300" height="200" viewBox="0 0 300 200"><rect width="300" height="200" fill="%232d3748"/><path d="M 30,50 L 270,50 L 270,120 L 200,120 L 180,100 L 120,100 L 100,120 L 30,120 Z" fill="none" stroke="%23718096" stroke-width="6"/><circle cx="70" cy="130" r="15" fill="%231a202c"/><circle cx="230" cy="130" r="15" fill="%231a202c"/><text x="150" y="190" fill="white" font-size="10" font-family="sans-serif" text-anchor="middle">SIMULACION: CHASIS DE REMOLQUE</text></svg>'
  },
  {
    name: 'Pieza de Metal en Lavado',
    url: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="300" height="200" viewBox="0 0 300 200"><rect width="300" height="200" fill="%232b6cb0"/><rect x="50" y="50" width="200" height="100" fill="%234a5568" rx="8"/><circle cx="100" cy="100" r="10" fill="white" opacity="0.3"/><circle cx="150" cy="80" r="15" fill="white" opacity="0.2"/><circle cx="200" cy="120" r="8" fill="white" opacity="0.4"/><text x="150" y="190" fill="white" font-size="10" font-family="sans-serif" text-anchor="middle">SIMULACION: PIEZA EN DECAPADO/LAVADO</text></svg>'
  }
];

export default function FolioManager({
  folios,
  clients,
  costCenters,
  destinations = [],
  goods,
  currentUser,
  onUpdateFolio,
  onCreateFolio,
  onDeleteFolio,
  isOnline = true,
  defaultShowHistory = false,
  isLoading = false,
  isSyncing = false,
  activeClient = null,
  selectedCostCenter = null
}: FolioManagerProps) {
  
  // Determine default single active client (Strict Client Isolation)
  const defaultClientId = useMemo(() => {
    if (activeClient?.id) return activeClient.id;
    if (isClientUser(currentUser)) {
      const match = clients.find(c => 
        c.code === currentUser?.clientCode || 
        c.id === currentUser?.clientCode || 
        c.name === currentUser?.clientCode ||
        (currentUser?.assignedClientIds && (currentUser.assignedClientIds.includes(c.id) || currentUser.assignedClientIds.includes(c.code)))
      );
      if (match) return match.id;
    }
    if (clients && clients.length > 0) return clients[0].id;
    return '';
  }, [activeClient, currentUser, clients]);

  const [selectedFolio, setSelectedFolio] = useState<Folio | null>(folios[0] || null);
  const [showCompletedHistory, setShowCompletedHistory] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'ALL' | string>(currentUser?.role === 'CHOFER' ? 'RECOLECCION' : 'ALL');
  const [clientFilter, setClientFilter] = useState<string>(defaultClientId);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (activeClient?.id) {
      setClientFilter(activeClient.id);
    } else if ((!clientFilter || clientFilter === 'ALL') && clients.length > 0) {
      setClientFilter(defaultClientId || clients[0].id);
    }
  }, [activeClient, clients, clientFilter, defaultClientId]);

  const handleManualRefresh = () => {
    setIsRefreshing(true);
    setSearchQuery('');
    setStatusFilter('ALL');
    setTimeout(() => {
      setIsRefreshing(false);
    }, 600);
  };
  
  useEffect(() => {
    if (defaultShowHistory) {
      setShowCompletedHistory(true);
    }
  }, [defaultShowHistory]);

  const [mapViewMode, setMapViewMode] = useState<'interactive' | 'vector'>('interactive');
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [simulatedScanMode, setSimulatedScanMode] = useState(false);
  const [scanInput, setScanInput] = useState('');

  // Form State for creation
  const [formClientId, setFormClientId] = useState('');
  const [formCostCenterId, setFormCostCenterId] = useState('');
  const [formProcessType, setFormProcessType] = useState<ProcessType>('DECAPADO_TERMICO');
  const [formHasGranallado, setFormHasGranallado] = useState(false);
  const [formNotes, setFormNotes] = useState('');
  const [formItems, setFormItems] = useState<{ goodsItemId: string; quantity: number; photoUrl?: string }[]>([]);
  const [formPhotoUrl, setFormPhotoUrl] = useState('');

  // Extended form fields
  const [formFolioId, setFormFolioId] = useState('');
  const [formShipmentDate, setFormShipmentDate] = useState('');
  const [selectedOriginId, setSelectedOriginId] = useState<string>('');
  const [formOrigin, setFormOrigin] = useState<string>('');
  const [formOriginLat, setFormOriginLat] = useState<number | undefined>(undefined);
  const [formOriginLng, setFormOriginLng] = useState<number | undefined>(undefined);
  const [formDestination, setFormDestination] = useState('');
  const [formDestinationSecondary, setFormDestinationSecondary] = useState('');
  const [formDriverName, setFormDriverName] = useState('');
  const [formEconomicNumber, setFormEconomicNumber] = useState('');
  const [selectedDestinationId, setSelectedDestinationId] = useState<string>('');
  const [formDestinationLat, setFormDestinationLat] = useState<number | undefined>(undefined);
  const [formDestinationLng, setFormDestinationLng] = useState<number | undefined>(undefined);
  const [formRouteNotes, setFormRouteNotes] = useState<string>('');
  const [formEstimatedKm, setFormEstimatedKm] = useState<number | undefined>(undefined);

  // Edit Folio States
  const [isEditingFolio, setIsEditingFolio] = useState(false);
  const [editFolioId, setEditFolioId] = useState('');
  const [editShipmentDate, setEditShipmentDate] = useState('');
  const [editDeliveryDate, setEditDeliveryDate] = useState('');
  const [editOrigin, setEditOrigin] = useState('');
  const [editOriginLat, setEditOriginLat] = useState<number | undefined>(undefined);
  const [editOriginLng, setEditOriginLng] = useState<number | undefined>(undefined);
  const [editDestination, setEditDestination] = useState('');
  const [editDestinationSecondary, setEditDestinationSecondary] = useState('');
  const [editProcessType, setEditProcessType] = useState<ProcessType>('DECAPADO_TERMICO');
  const [editHasGranallado, setEditHasGranallado] = useState(false);
  const [editNotes, setEditNotes] = useState('');
  const [editItems, setEditItems] = useState<{ goodsItemId: string; quantity: number; photoUrl?: string }[]>([]);
  const [editPhotoUrl, setEditPhotoUrl] = useState('');
  const [editDriverName, setEditDriverName] = useState('');
  const [editEconomicNumber, setEditEconomicNumber] = useState('');

  // Up to 5 photos state
  const [formPhotoUrls, setFormPhotoUrls] = useState<string[]>([]);
  const [editPhotoUrls, setEditPhotoUrls] = useState<string[]>([]);

  // Double destination toggle
  const [formIsDoubleDestination, setFormIsDoubleDestination] = useState(false);
  const [editIsDoubleDestination, setEditIsDoubleDestination] = useState(false);

  // Unified items search and custom inputs (Creation Form)
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [itemConceptName, setItemConceptName] = useState('');
  const [itemQuantity, setItemQuantity] = useState<number | string>('');
  const [itemUnit, setItemUnit] = useState('PZA (Pieza)');
  const [itemPrice, setItemPrice] = useState(0);
  const [itemWeight, setItemWeight] = useState(0);
  const [itemSelectedCatalogId, setItemSelectedCatalogId] = useState('');

  // Unified items search and custom inputs (Edit Form)
  const [editItemSearchQuery, setEditItemSearchQuery] = useState('');
  const [editItemConceptName, setEditItemConceptName] = useState('');
  const [editItemQuantity, setEditItemQuantity] = useState(1);
  const [editItemUnit, setEditItemUnit] = useState('PZA (Pieza)');
  const [editItemPrice, setEditItemPrice] = useState(0);
  const [editItemWeight, setEditItemWeight] = useState(0);
  const [editItemSelectedCatalogId, setEditItemSelectedCatalogId] = useState('');

  // Temporary selected item inputs for picker
  const [selectedItemToAdd, setSelectedItemToAdd] = useState('');
  const [quantityToAdd, setQuantityToAdd] = useState(1);

  // Chofer Multi-Folio Encapsulation & Live GPS State
  const [selectedFoliosForRoute, setSelectedFoliosForRoute] = useState<string[]>([]);

  // PO Integration State
  const [formPoNumber, setFormPoNumber] = useState('');
  const [editPoNumber, setEditPoNumber] = useState('');
  const [isIntegratePoModalOpen, setIsIntegratePoModalOpen] = useState(false);
  const [targetFolioForPo, setTargetFolioForPo] = useState<Folio | null>(null);
  const [selectedPoOption, setSelectedPoOption] = useState('');
  const [customPoNumberInput, setCustomPoNumberInput] = useState('');
  const [poRecordsList, setPoRecordsList] = useState<PoRecord[]>([]);

  // Subscribe to PO records for integration selection
  useEffect(() => {
    const unsubscribe = dataService.subscribeToPoRecords((records) => {
      setPoRecordsList(records || []);
    });
    return () => unsubscribe();
  }, []);

  // Helpers to resolve cost center linked to client and PO number
  const getCostCenterNameForFolio = (f?: Folio | null): string => {
    if (!f) return 'Sede Principal';
    if (f.costCenterId) {
      const cc = costCenters.find(c => c.id === f.costCenterId);
      if (cc && cc.name && !cc.name.toLowerCase().includes('planta central')) {
        return cc.name;
      }
    }
    if (f.costCenterName && !f.costCenterName.toLowerCase().includes('planta central')) {
      const cc = costCenters.find(c => c.name.toLowerCase() === f.costCenterName.toLowerCase());
      if (cc && !cc.name.toLowerCase().includes('planta central')) return cc.name;
      return f.costCenterName;
    }
    const client = clients.find(c => c.id === f.clientId || c.code === f.clientId || c.name.toLowerCase() === (f.clientName || '').toLowerCase());
    if (client) {
      const clientCCs = costCenters.filter(c => c.clientId === client.id || (c.clientIds && c.clientIds.includes(client.id)) || (client.costCenterIds && client.costCenterIds.includes(c.id)));
      if (clientCCs.length > 0) {
        const cleanCC = clientCCs.find(c => !c.name.toLowerCase().includes('planta central')) || clientCCs[0];
        return cleanCC.name.replace(/Planta Central/gi, 'Planta Principal');
      }
    }
    return `${f.clientName || 'Cliente'} - Sede Principal`;
  };

  const isFolioIntegratedToPo = (f?: Folio | null): boolean => {
    if (!f) return false;
    return Boolean(f.poNumber && f.poNumber.trim().length > 0);
  };

  const getPoNumberForFolio = (f?: Folio | null): string => {
    if (!f) return '';
    if (f.poNumber && f.poNumber.trim().length > 0) return f.poNumber.trim();
    return '';
  };

  // PO Integration Handlers
  const handleOpenIntegratePoModal = (folio: Folio) => {
    setTargetFolioForPo(folio);
    const existingPo = folio.poNumber || '';
    setSelectedPoOption(existingPo || (poRecordsList.length > 0 ? (poRecordsList.find(p => p.clientId === folio.clientId)?.poNumber || poRecordsList[0]?.poNumber || 'CUSTOM') : 'CUSTOM'));
    setCustomPoNumberInput(existingPo || '');
    setIsIntegratePoModalOpen(true);
  };

  const handleSavePoIntegration = async () => {
    if (!targetFolioForPo) return;
    const finalPo = (selectedPoOption === 'CUSTOM' || !selectedPoOption) 
      ? customPoNumberInput.trim() 
      : selectedPoOption.trim();
    
    if (!finalPo) {
      if ((window as any).showAppAlert) {
        await (window as any).showAppAlert('Por favor ingrese o seleccione un número de Orden de Compra (PO).', 'Número de PO requerido');
      } else {
        alert('Por favor ingrese o seleccione un número de Orden de Compra (PO).');
      }
      return;
    }

    const updatedFolio: Folio = {
      ...targetFolioForPo,
      poNumber: finalPo
    };

    await dataService.saveFolio(updatedFolio);
    onUpdateFolio(updatedFolio);
    if (selectedFolio?.id === updatedFolio.id) {
      setSelectedFolio(updatedFolio);
    }
    setIsIntegratePoModalOpen(false);
    setTargetFolioForPo(null);
    if ((window as any).showAppAlert) {
      await (window as any).showAppAlert(`El folio ${updatedFolio.id} ha sido integrado exitosamente a la Orden de Compra ${finalPo}.`, '✓ Folio Integrado a PO');
    }
  };

  const handleRemovePoIntegration = async (folio: Folio) => {
    const confirmed = typeof (window as any).showAppConfirm === 'function'
      ? await (window as any).showAppConfirm(`¿Desea desvincular el folio ${folio.id} de la Orden de Compra "${folio.poNumber}"? Su estado cambiará a 'No integrado a PO'.`, 'Desvincular PO')
      : window.confirm(`¿Desea desvincular el folio ${folio.id} de la Orden de Compra?`);
    if (!confirmed) return;

    const updatedFolio: Folio = {
      ...folio,
      poNumber: undefined
    };
    await dataService.saveFolio(updatedFolio);
    onUpdateFolio(updatedFolio);
    if (selectedFolio?.id === updatedFolio.id) {
      setSelectedFolio(updatedFolio);
    }
    setIsIntegratePoModalOpen(false);
    if ((window as any).showAppAlert) {
      await (window as any).showAppAlert(`El folio ${folio.id} ha sido desvinculado de la PO.`, 'Estado Actualizado');
    }
  };
  const [isChoferTrackingActive, setIsChoferTrackingActive] = useState<boolean>(false);
  const [choferWatchId, setChoferWatchId] = useState<number | null>(null);
  const [showEncapsulatedMapModal, setShowEncapsulatedMapModal] = useState<boolean>(false);

  const getFolioDestKey = (f: Folio) => {
    const raw = f.destination || f.destinationId || f.costCenterName || f.costCenterId || f.clientName || '';
    return raw.trim().toLowerCase();
  };

  const getFolioDestLabel = (f: Folio) => {
    return f.destination || f.costCenterName || f.clientName || 'Destino General';
  };

  const toggleSelectFolioForRoute = (folioId: string) => {
    setSelectedFoliosForRoute(prev => {
      const isRemoving = prev.includes(folioId);
      if (isRemoving) {
        return prev.filter(id => id !== folioId);
      }

      // If adding a new folio and there are already selected folios, validate destination
      if (prev.length > 0) {
        const firstFolio = folios.find(f => f.id === prev[0]);
        const targetFolio = folios.find(f => f.id === folioId);

        if (firstFolio && targetFolio) {
          const firstKey = getFolioDestKey(firstFolio);
          const targetKey = getFolioDestKey(targetFolio);

          if (firstKey !== targetKey) {
            const firstDest = getFolioDestLabel(firstFolio);
            const targetDest = getFolioDestLabel(targetFolio);
            const alertMsg = `⚠️ EL FOLIO #${targetFolio.id} NO CORRESPONDE AL DESTINO\n\n` +
              `• Destino actual de la ruta (Folio #${firstFolio.id}): "${firstDest}"\n` +
              `• Destino del folio seleccionado (#${targetFolio.id}): "${targetDest}"\n\n` +
              `Por favor quita o desmarca el folio #${targetFolio.id} del listado para asegurar que todos los folios en la ruta pertenezcan al mismo destino.`;

            if ((window as any).showAppAlert) {
              (window as any).showAppAlert(alertMsg, `⚠️ Folio #${targetFolio.id} no corresponde al destino`);
            } else {
              alert(alertMsg);
            }
          }
        }
      }

      return [...prev, folioId];
    });
  };

  // Photo & Video states
  const [selectedPhotoView, setSelectedPhotoView] = useState<string | null>(null);
  const [activePhotoIndex, setActivePhotoIndex] = useState<number | null>(null);
  const [webcamStream, setWebcamStream] = useState<MediaStream | null>(null);
  const [cameraFacingMode, setCameraFacingMode] = useState<'user' | 'environment'>('environment');
  const [webcamError, setWebcamError] = useState('');
  const videoRef = React.useRef<HTMLVideoElement | null>(null);

  // Helper useEffect to bind stream to video element when it mounts
  useEffect(() => {
    if (webcamStream && videoRef.current) {
      videoRef.current.srcObject = webcamStream;
    }
  }, [webcamStream]);

  // Workflow states for advanced actions
  const [reworkReport, setReworkReport] = useState('');
  const [reworkPiecesName, setReworkPiecesName] = useState('');
  const [reworkQuantity, setReworkQuantity] = useState(1);
  const [reworkType, setReworkType] = useState<'TOTAL' | 'PARCIAL'>('TOTAL');
  const [reworkPhotos, setReworkPhotos] = useState<string[]>(['', '', '', '', '', '', '']);
  const reworkFileInputRefs = React.useRef<(HTMLInputElement | null)[]>([]);
  const [showReworkModal, setShowReworkModal] = useState(false);

  const handleOpenReworkModal = (folio: Folio) => {
    setSelectedFolio(folio);
    const totalPzas = folio.items?.reduce((a, b) => a + (Number(b.quantity) || 0), 0) || 1;
    const itemsDesc = folio.items?.map(i => i.description).join(', ') || 'Todas las piezas del folio';
    setReworkType('TOTAL');
    setReworkQuantity(totalPzas);
    setReworkPiecesName(`Todo el Folio #${folio.id} (${itemsDesc})`);
    setReworkReport('');
    setReworkPhotos(['', '', '', '', '', '', '']);
    setShowReworkModal(true);
  };
  const [returnDetails, setReturnDetails] = useState('');
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnType, setReturnType] = useState<'TOTAL' | 'PARCIAL'>('TOTAL');
  const [partialReturnItems, setPartialReturnItems] = useState<Record<string, { selected: boolean; quantity: number }>>({});

  const handleOpenReturnModal = (folio: Folio) => {
    setSelectedFolio(folio);
    setReturnType('TOTAL');
    setReturnDetails('');
    const initMap: Record<string, { selected: boolean; quantity: number }> = {};
    folio.items?.forEach((it, idx) => {
      const key = it.goodsItemId || `item-${idx}`;
      initMap[key] = { selected: true, quantity: Number(it.quantity) || 1 };
    });
    setPartialReturnItems(initMap);
    setShowReturnModal(true);
  };
  
  // Notification history log
  const [notificationsSent, setNotificationsSent] = useState<{
    id: string;
    folioId: string;
    stage: ProcessStage;
    type: 'SMS' | 'Email';
    destination: string;
    message: string;
    time: string;
  }[]>([]);

  // Filter cost centers based on client selection strictly
  const filteredCostCentersForForm = useMemo(() => {
    if (!formClientId) return costCenters;
    const client = clients.find(c => c.id === formClientId || c.code === formClientId || c.name === formClientId);
    const clientKeys = new Set([
      formClientId.toLowerCase(),
      client?.id?.toLowerCase(),
      client?.code?.toLowerCase(),
      client?.name?.toLowerCase()
    ].filter(Boolean));

    let matching = costCenters.filter(cc => 
      (cc.clientId && clientKeys.has(cc.clientId.toLowerCase())) ||
      (cc.clientName && clientKeys.has(cc.clientName.toLowerCase())) ||
      (cc.clientIds && cc.clientIds.some(cid => clientKeys.has(cid.toLowerCase()))) ||
      (client?.costCenterIds && client.costCenterIds.includes(cc.id))
    );

    // If user is non-admin and has assigned cost center restrictions, filter strictly to them!
    if (currentUser && !isAdminRole(currentUser.role) && currentUser.role !== 'admin' && currentUser.role !== 'administrador') {
      if (currentUser.assignedCostCenterIds && currentUser.assignedCostCenterIds.length > 0) {
        const assignedSet = new Set(currentUser.assignedCostCenterIds.map(id => id.toLowerCase()));
        matching = matching.filter(cc => 
          (cc.id && assignedSet.has(cc.id.toLowerCase())) || 
          (cc.code && assignedSet.has(cc.code.toLowerCase())) || 
          (cc.name && assignedSet.has(cc.name.toLowerCase()))
        );
      }
    }

    return matching;
  }, [costCenters, formClientId, clients, currentUser]);

  // Filter goods based on client selection
  const filteredGoodsForForm = useMemo(() => {
    if (!formClientId) return goods;
    return goods.filter(g => !g.clientId || g.clientId === formClientId);
  }, [goods, formClientId]);

  // Filter destinations based on selected client
  const clientSpecificDestinations = useMemo(() => {
    if (!destinations || destinations.length === 0) return [];
    if (!formClientId) return [];
    const client = clients.find(c => c.id === formClientId || c.code === formClientId || c.name === formClientId);
    const clientKeys = [
      formClientId.toLowerCase(),
      client?.id?.toLowerCase(),
      client?.code?.toLowerCase(),
      client?.name?.toLowerCase()
    ].filter(Boolean) as string[];

    return destinations.filter(d => {
      if (d.clientId && clientKeys.includes(d.clientId.toLowerCase())) return true;
      if (d.clientName && clientKeys.includes(d.clientName.toLowerCase())) return true;
      if (d.clientIds && d.clientIds.some(cid => clientKeys.includes(cid.toLowerCase()))) return true;
      return false;
    });
  }, [destinations, formClientId, clients]);

  const generalDestinations = useMemo(() => {
    if (!destinations || destinations.length === 0) return [];
    return destinations.filter(d => {
      const hasClientId = Boolean(d.clientId && d.clientId.trim().length > 0);
      const hasClientIds = Boolean(d.clientIds && d.clientIds.length > 0);
      const hasClientName = Boolean(d.clientName && d.clientName.trim().length > 0);
      return !hasClientId && !hasClientIds && !hasClientName;
    });
  }, [destinations]);

  const otherClientDestinations = useMemo(() => {
    if (!destinations || destinations.length === 0) return [];
    return destinations.filter(d => 
      !clientSpecificDestinations.some(cd => cd.id === d.id) &&
      !generalDestinations.some(gd => gd.id === d.id)
    );
  }, [destinations, clientSpecificDestinations, generalDestinations]);

  const prevIsCreatingRef = React.useRef(false);

  // Handle auto-population ONLY when create form is first opened
  useEffect(() => {
    if (isCreating && !prevIsCreatingRef.current) {
      // Auto-generate folio number
      setFormFolioId(`F-1025${Math.floor(10 + Math.random() * 89)}`);
      // Today in YYYY-MM-DD
      setFormShipmentDate(new Date().toISOString().split('T')[0]);
      setFormDestination('');
      setFormProcessType('DECAPADO_TERMICO');
      setFormHasGranallado(false);
      setFormItems([]);
      setSelectedItemToAdd('');
      setQuantityToAdd(1);
      setItemConceptName('');
      setItemQuantity('');
      setItemSearchQuery('');
      setItemSelectedCatalogId('');
      
      // Auto-select client based on activeClient, current clientFilter, user role, or fallback
      if (activeClient) {
        setFormClientId(activeClient.id);
      } else if (clientFilter && clientFilter !== 'ALL') {
        setFormClientId(clientFilter);
      } else if (isClientUser(currentUser) && currentUser.clientCode) {
        const matched = clients.find(c => c.code === currentUser.clientCode || c.name === currentUser.clientCode);
        setFormClientId(matched ? matched.id : (clients[0]?.id || ''));
      } else if (clients && clients.length > 0) {
        setFormClientId(clients[0].id);
      }
    }
    prevIsCreatingRef.current = isCreating;
  }, [isCreating, clients, currentUser, activeClient, clientFilter]);

  // Set default cost center when client changes, avoiding unwanted overwrites
  useEffect(() => {
    if (filteredCostCentersForForm.length > 0) {
      if (!formCostCenterId || !filteredCostCentersForForm.some(c => c.id === formCostCenterId)) {
        setFormCostCenterId(filteredCostCentersForForm[0].id);
        if (!formDestination) {
          setFormDestination(filteredCostCentersForForm[0].address);
        }
      }
    } else if (costCenters.length > 0) {
      if (!formCostCenterId || !costCenters.some(c => c.id === formCostCenterId)) {
        setFormCostCenterId(costCenters[0].id);
      }
    } else {
      setFormCostCenterId('');
    }
  }, [filteredCostCentersForForm, costCenters, formCostCenterId]);

  // Normalize folios without mutating or forced client assignment
  const normalizedFolios = useMemo(() => {
    return folios;
  }, [folios]);

  // Active selected client instance for strict client isolation
  const currentSelectedClient = useMemo(() => {
    if (activeClient) return activeClient;
    if (isClientUser(currentUser) || isChoferUser(currentUser)) {
      return clients.find(c => 
        c.code === currentUser?.clientCode || 
        c.id === currentUser?.clientCode || 
        c.name === currentUser?.clientCode ||
        (currentUser?.assignedClientIds && (currentUser.assignedClientIds.includes(c.id) || currentUser.assignedClientIds.includes(c.code))) ||
        (currentUser?.assignedCostCenterIds && c.costCenterIds && c.costCenterIds.some(ccid => currentUser.assignedCostCenterIds?.includes(ccid)))
      ) || clients[0] || null;
    }
    return clients.find(c => c.id === clientFilter || c.code === clientFilter) || clients[0] || null;
  }, [clients, clientFilter, activeClient, currentUser]);

  // Folios strictly scoped to the active selected client and assigned cost center (Cross-client & driver isolation)
  const clientFolios = useMemo(() => {
    if (!currentSelectedClient && !isChoferUser(currentUser)) return normalizedFolios;

    let result = normalizedFolios;

    if (currentSelectedClient) {
      const targetKeys = new Set([
        currentSelectedClient.id?.toLowerCase(),
        (currentSelectedClient.code || '').toLowerCase(),
        (currentSelectedClient.name || '').toLowerCase()
      ].filter(Boolean));

      result = result.filter(f => {
        if (f.clientId && targetKeys.has(f.clientId.toLowerCase())) return true;
        if (f.clientName && targetKeys.has(f.clientName.toLowerCase())) return true;
        return false;
      });
    }

    // Strict sub-cost-center scoping for Chofer and restricted operators/roles
    if (isChoferUser(currentUser) || (currentUser && !isAdminRole(currentUser.role) && currentUser.role !== 'admin')) {
      if (currentUser.assignedCostCenterIds && currentUser.assignedCostCenterIds.length > 0) {
        const matchedCCKeys = new Set<string>();
        currentUser.assignedCostCenterIds.forEach(accid => {
          matchedCCKeys.add(accid.toLowerCase());
          const cc = costCenters.find(c => c.id.toLowerCase() === accid.toLowerCase() || c.code.toLowerCase() === accid.toLowerCase() || c.name.toLowerCase() === accid.toLowerCase());
          if (cc) {
            matchedCCKeys.add(cc.id.toLowerCase());
            matchedCCKeys.add(cc.code.toLowerCase());
            matchedCCKeys.add(cc.name.toLowerCase());
          }
        });
        result = result.filter(f => 
          (f.costCenterId && matchedCCKeys.has(f.costCenterId.toLowerCase())) || 
          (f.costCenterName && matchedCCKeys.has(f.costCenterName.toLowerCase()))
        );
      }
    }

    return result;
  }, [normalizedFolios, currentSelectedClient, currentUser, costCenters]);

  // Filtered folios based on status and intelligent smart search
  const filteredFolios = useMemo(() => {
    let result = clientFolios;

    // Filter by dropdown status filter
    if (statusFilter !== 'ALL') {
      if (statusFilter === 'NOT_INTEGRATED_PO') {
        result = result.filter(f => !f.poNumber || f.poNumber.trim().length === 0);
      } else if (statusFilter === 'INTEGRATED_PO') {
        result = result.filter(f => Boolean(f.poNumber && f.poNumber.trim().length > 0));
      } else if (statusFilter === 'ENTREGADO') {
        result = result.filter(f => f.status === 'ENTREGA_PLANTA');
      } else if (statusFilter === 'EN_TRANSITO') {
        result = result.filter(f => f.status === 'SALIDA_CEDIS');
      } else if (statusFilter === 'EN_PROCESO') {
        result = result.filter(f => ['DECAPADO_TERMICO_QUIMICO', 'DECAPADO_LAVADO', 'INSPECCION', 'RETRABAJO'].includes(f.status));
      } else if (statusFilter === 'RECOLECCION') {
        result = result.filter(f => ['RECOLECCION', 'ENTRADA_CEDIS'].includes(f.status));
      } else if (statusFilter === 'DEVOLUCION') {
        result = result.filter(f => f.status === 'DEVOLUCION');
      } else {
        result = result.filter(f => f.status === statusFilter);
      }
    } else if (!showCompletedHistory) {
      result = result.filter(f => f.status !== 'ENTREGA_PLANTA' && f.status !== 'DEVOLUCION');
    }

    // Intelligent Folio & Content Search
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      const rawNumber = q.replace(/^#/, '').replace(/^f-?/, '').trim();

      result = result.filter(f => {
        const fid = (f.id || '').toLowerCase();
        const fidClean = fid.replace(/^#/, '').replace(/^f-?/, '').trim();
        
        // Smart Folio ID match: supports "10254", "F-10254", "F10254", "#F-10254"
        const matchFolioId = fid.includes(q) || (rawNumber.length > 0 && fidClean.includes(rawNumber));
        const matchPo = f.poNumber && f.poNumber.toLowerCase().includes(q);
        const matchDest = f.destination && f.destination.toLowerCase().includes(q);
        const matchCostCenter = f.costCenterName && f.costCenterName.toLowerCase().includes(q);
        const matchDriver = f.driverName && f.driverName.toLowerCase().includes(q);
        const matchEconomic = f.economicNumber && f.economicNumber.toLowerCase().includes(q);
        const matchStatus = f.status.toLowerCase().includes(q);
        const matchNotes = f.notes && f.notes.toLowerCase().includes(q);
        const matchItems = f.items && f.items.some(i => 
          (i.description || '').toLowerCase().includes(q) ||
          (i.goodsItemId || '').toLowerCase().includes(q)
        );

        return matchFolioId || matchPo || matchDest || matchCostCenter || matchDriver || matchEconomic || matchStatus || matchNotes || matchItems;
      });
    }

    return result;
  }, [clientFolios, statusFilter, showCompletedHistory, searchQuery]);

  // Keep selectedFolio synchronized with active filtered client folios
  useEffect(() => {
    if (filteredFolios.length > 0) {
      if (!selectedFolio || !filteredFolios.some(f => f.id === selectedFolio.id)) {
        setSelectedFolio(filteredFolios[0]);
      }
    } else {
      setSelectedFolio(null);
    }
  }, [filteredFolios, selectedFolio]);

  const foliosInSalidaCedis = useMemo(() => {
    return filteredFolios.filter(f => f.status === 'SALIDA_CEDIS');
  }, [filteredFolios]);

  const toggleSelectAllSalidaCedis = () => {
    if (selectedFoliosForRoute.length === foliosInSalidaCedis.length && foliosInSalidaCedis.length > 0) {
      setSelectedFoliosForRoute([]);
    } else {
      if (foliosInSalidaCedis.length > 0) {
        const firstFolio = foliosInSalidaCedis[0];
        const firstKey = getFolioDestKey(firstFolio);
        const firstDest = getFolioDestLabel(firstFolio);

        const mismatched = foliosInSalidaCedis.filter(f => getFolioDestKey(f) !== firstKey);
        if (mismatched.length > 0) {
          const mismatchedIdsStr = mismatched.map(m => `#${m.id}`).join(', ');
          const alertMsg = `⚠️ ALGUNOS FOLIOS TIENEN DESTINOS DIFERENTES\n\n` +
            `Los siguientes folios NO corresponden al destino principal ("${firstDest}"):\n👉 ${mismatchedIdsStr}\n\n` +
            `Por favor quítalos del listado desmarcándolos para mantener un único destino por ruta.`;

          if ((window as any).showAppAlert) {
            (window as any).showAppAlert(alertMsg, "⚠️ Destinos Incompatibles Detectados");
          } else {
            alert(alertMsg);
          }
        }
      }
      setSelectedFoliosForRoute(foliosInSalidaCedis.map(f => f.id));
    }
  };

  const handleStartChoferLocationSharing = async () => {
    if (selectedFoliosForRoute.length === 0) {
      alert("Por favor selecciona al menos un folio en Salida CEDIS para iniciar la transmisión GPS de la unidad.");
      return;
    }

    // Validate destination uniformity before starting location sharing
    const selectedFoliosList = folios.filter(f => selectedFoliosForRoute.includes(f.id));
    if (selectedFoliosList.length > 1) {
      const firstFolio = selectedFoliosList[0];
      const firstKey = getFolioDestKey(firstFolio);
      const firstDest = getFolioDestLabel(firstFolio);

      const mismatched = selectedFoliosList.filter(f => getFolioDestKey(f) !== firstKey);
      if (mismatched.length > 0) {
        const mismatchedStr = mismatched.map(f => `• Folio #${f.id} (Destino: "${getFolioDestLabel(f)}")`).join('\n');
        const alertMsg = `❌ NO ES POSIBLE INICIAR RUTA CON DESTINOS DIFERENTES\n\n` +
          `Los siguientes folios NO corresponden al destino principal ("${firstDest}"):\n\n${mismatchedStr}\n\n` +
          `Por favor quita del listado el o los folios que no corresponden (${mismatched.map(f => `#${f.id}`).join(', ')}) antes de compartir tu ubicación.`;

        if ((window as any).showAppAlert) {
          (window as any).showAppAlert(alertMsg, "❌ Folios Incompatibles por Destino");
        } else {
          alert(alertMsg);
        }
        return;
      }
    }

    const userAny = currentUser as any;

    if (isChoferTrackingActive) {
      if (choferWatchId !== null) {
        navigator.geolocation.clearWatch(choferWatchId);
        setChoferWatchId(null);
      }
      setIsChoferTrackingActive(false);
      const driverId = userAny.email || userAny.uid || 'chofer-1';
      await dataService.deleteDriverLocation(driverId);
      if ((window as any).showAppAlert) {
        (window as any).showAppAlert("Se ha finalizado la transmisión de ubicación GPS.", "GPS Chofer Detenido");
      } else {
        alert("GPS Chofer Detenido: Se ha finalizado la transmisión de ubicación GPS.");
      }
      return;
    }

    if (!navigator.geolocation) {
      alert("Tu navegador o dispositivo no soporta geolocalización GPS.");
      return;
    }

    const encapsulated = selectedFoliosList.map(f => {
      const totalPieces = f.items ? f.items.reduce((sum, item) => sum + item.quantity, 0) : 0;
      return {
        folioId: f.id,
        clientName: f.clientName,
        destination: f.destination || f.costCenterName || 'Planta Destino',
        totalPieces,
        status: f.status
      };
    });

    const driverId = userAny.email || userAny.uid || 'chofer-1';

    const updateLocationOnServer = async (lat: number, lng: number, speedKmH: number) => {
      const driverLoc: DriverLocation = {
        id: driverId,
        driverName: userAny.name || 'Chofer Asignado ISN',
        driverEmail: userAny.email || 'chofer@grupoisn.com',
        latitude: lat,
        longitude: lng,
        speed: Math.round(speedKmH),
        lastUpdated: new Date().toISOString(),
        active: true,
        plateNumber: 'TRK-SNF88',
        folioId: selectedFoliosForRoute[0],
        folioIds: selectedFoliosForRoute,
        encapsulatedFolios: encapsulated,
        statusText: `Transportando ${selectedFoliosForRoute.length} folio(s) en ruta a Planta Cliente`
      };
      await dataService.saveDriverLocation(driverLoc);
    };

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const speed = pos.coords.speed ? pos.coords.speed * 3.6 : 65;
        updateLocationOnServer(pos.coords.latitude, pos.coords.longitude, speed);
      },
      (err) => {
        console.warn("GPS position error fallback:", err);
        updateLocationOnServer(25.6866, -100.3161, 72);
      },
      { enableHighAccuracy: true }
    );

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const speed = pos.coords.speed ? pos.coords.speed * 3.6 : 68;
        updateLocationOnServer(pos.coords.latitude, pos.coords.longitude, speed);
      },
      (err) => console.warn("Watch position update error:", err),
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 5000 }
    );

    setChoferWatchId(watchId);
    setIsChoferTrackingActive(true);
    if ((window as any).showAppAlert) {
      (window as any).showAppAlert(`Compartiendo ubicación en vivo de ${selectedFoliosForRoute.length} folios encapsulados en un solo rastreo.`, "🟢 GPS Chofer Transmitiendo");
    } else {
      alert(`🟢 GPS Chofer Transmitiendo: Compartiendo ubicación en vivo de ${selectedFoliosForRoute.length} folios encapsulados.`);
    }
  };

  const handleBulkDeliverSelectedFolios = async () => {
    if (selectedFoliosForRoute.length === 0) return;

    // Validate destination uniformity before confirming delivery
    const selectedFoliosList = folios.filter(f => selectedFoliosForRoute.includes(f.id));
    if (selectedFoliosList.length > 1) {
      const firstFolio = selectedFoliosList[0];
      const firstKey = getFolioDestKey(firstFolio);
      const firstDest = getFolioDestLabel(firstFolio);

      const mismatched = selectedFoliosList.filter(f => getFolioDestKey(f) !== firstKey);
      if (mismatched.length > 0) {
        const mismatchedStr = mismatched.map(f => `• Folio #${f.id} (Destino: "${getFolioDestLabel(f)}")`).join('\n');
        const alertMsg = `❌ NO SE PUEDE REGISTRAR LA ENTREGA\n\n` +
          `Los siguientes folios NO corresponden al destino principal ("${firstDest}"):\n\n${mismatchedStr}\n\n` +
          `Por favor quita del listado el o los folios que no corresponden (${mismatched.map(f => `#${f.id}`).join(', ')}) para procesar la entrega.`;

        if ((window as any).showAppAlert) {
          (window as any).showAppAlert(alertMsg, "❌ Folios con Diferentes Destinos");
        } else {
          alert(alertMsg);
        }
        return;
      }
    }

    if (!confirm(`¿Confirmar entrega en planta de los ${selectedFoliosForRoute.length} folios seleccionados? Esta acción marcará su fase como ENTREGA PLANTA.`)) {
      return;
    }

    const userAny = currentUser as any;
    const newHistoryEntry: StageHistory = {
      stage: 'ENTREGA_PLANTA',
      timestamp: new Date().toISOString(),
      operatorName: userAny.name || 'Chofer Logístico',
      notes: `Entrega en Planta Cliente completada por el chofer ${userAny.name || ''} (Carga Encapsulada)`,
    };

    for (const f of selectedFoliosList) {
      const updatedFolio: Folio = {
        ...f,
        status: 'ENTREGA_PLANTA',
        history: [...f.history, newHistoryEntry]
      };
      await onUpdateFolio(updatedFolio);
      await dataService.deleteDriverLocationByFolioId(f.id);
    }

    if (isChoferTrackingActive && choferWatchId !== null) {
      navigator.geolocation.clearWatch(choferWatchId);
      setChoferWatchId(null);
      setIsChoferTrackingActive(false);
    }
    const driverId = userAny.email || userAny.uid || 'chofer-1';
    await dataService.deleteDriverLocation(driverId);

    setSelectedFoliosForRoute([]);
    if ((window as any).showAppAlert) {
      (window as any).showAppAlert(`Se entregaron exitosamente ${selectedFoliosList.length} folios. Se actualizó el historial y se removió el rastreo de la ruta.`, "✅ Entrega en Planta Completada");
    } else {
      alert(`✅ Entrega en Planta Completada: Se entregaron exitosamente ${selectedFoliosList.length} folios.`);
    }
  };

  // Find selected folio if list updates or default to first
  useEffect(() => {
    if (!selectedFolio && filteredFolios.length > 0) {
      setSelectedFolio(filteredFolios[0]);
    } else if (selectedFolio) {
      const updated = normalizedFolios.find(f => f.id === selectedFolio.id);
      if (updated) {
        setSelectedFolio(updated);
      } else if (filteredFolios.length > 0) {
        setSelectedFolio(filteredFolios[0]);
      }
    }
  }, [normalizedFolios, filteredFolios]);

  // Helper to handle folio deletion
  const handleDeleteClick = async (folioId: string) => {
    if (!onDeleteFolio) return;
    const confirmed = typeof (window as any).showAppConfirm === 'function' 
      ? await (window as any).showAppConfirm(`¿Está seguro de que desea eliminar permanentemente el Folio ${folioId}? Esta acción no se puede deshacer.`, 'Confirmar Eliminación')
      : window.confirm(`¿Está seguro de que desea eliminar permanentemente el Folio ${folioId}?`);
    
    if (confirmed) {
      onDeleteFolio(folioId);
      if (selectedFolio?.id === folioId) {
        setSelectedFolio(filteredFolios.filter(f => f.id !== folioId)[0] || null);
      }
    }
  };

  // Helper to handle manual QR Sim Scan
  const handleQRScan = async (idToScan: string) => {
    const found = folios.find(f => f.id.toUpperCase() === idToScan.toUpperCase().trim());
    if (found) {
      setSelectedFolio(found);
      setSimulatedScanMode(false);
      setScanInput('');
    } else {
      await window.showAppAlert(`Folio "${idToScan}" no encontrado. Use uno existente como: ${folios[0]?.id}`, 'Código de Folio No Registrado');
    }
  };

  // Add Item in Reception Form
  const handleAddFormItem = () => {
    setFormItems([...formItems, { goodsItemId: '', quantity: 1 }]);
  };

  // Remove Item in Reception Form
  const handleRemoveFormItem = (index: number) => {
    if (formItems.length > 0) {
      setFormItems(formItems.filter((_, i) => i !== index));
    }
  };

  // Create Folio Submit
  const handleCreateFolioSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isClientUser(currentUser)) {
      await window.showAppAlert('Los usuarios de perfil Cliente se encuentran en Modo Consulta y no tienen permisos para crear folios. Únicamente pueden consultar el estado y descargar reportes.', 'Acceso Restringido');
      return;
    }

    if (formItems.length === 0) {
      await window.showAppAlert('Por favor agrega al menos una mercancía en recepción a la lista.', 'Lista de Mercancías Vacía');
      return;
    }

    if (!formClientId) {
      await window.showAppAlert('Por favor seleccione un Socio Comercial / Cliente para este folio.', 'Cliente No Seleccionado');
      return;
    }

    const selectedClient = clients.find(c => c.id === formClientId || c.code === formClientId) || activeClient || null;
    if (!selectedClient) {
      await window.showAppAlert('El cliente seleccionado no es válido o no fue encontrado.', 'Error de Cliente');
      return;
    }

    const activeClientId = selectedClient.id;
    const activeCCId = formCostCenterId || (filteredCostCentersForForm.length > 0 ? filteredCostCentersForForm[0].id : '');
    const selectedCC = costCenters.find(c => c.id === activeCCId) || null;

    const clientName = selectedClient.name;
    const clientIdVal = selectedClient.id;
    const costCenterName = selectedCC ? selectedCC.name : (selectedClient ? `${selectedClient.name} - Sede Principal` : 'Sede Principal');
    const resolvedOrigin = formOrigin.trim() || (selectedCC ? `${selectedCC.name} - ${selectedCC.address}` : (selectedClient ? selectedClient.address : '🏢 CEDIS Escobedo'));
    const resolvedOriginLat = formOriginLat !== undefined ? formOriginLat : (selectedCC ? selectedCC.lat : 25.7820);
    const resolvedOriginLng = formOriginLng !== undefined ? formOriginLng : (selectedCC ? selectedCC.lng : -100.4050);
    const destination = formDestination || (selectedCC ? selectedCC.address : 'Destino General');
    const defaultLat = selectedCC ? selectedCC.lat : 22.1565;
    const defaultLng = selectedCC ? selectedCC.lng : -100.9855;
    
    // Process items with correct catalog prices
    const processedItems = formItems.map(it => {
      const catalogItem = goods.find(g => g.id === it.goodsItemId);
      if (catalogItem) {
        const priceMXNVal = parseFloat(catalogItem.priceMXN.toString()) || 0;
        const priceUSDVal = parseFloat(catalogItem.priceUSD.toString()) || 0;
        const totalMXN = priceMXNVal * it.quantity;
        const totalUSD = priceUSDVal * it.quantity;
        return {
          goodsItemId: it.goodsItemId,
          description: catalogItem.description,
          quantity: it.quantity,
          unitPriceMXN: catalogItem.priceMXN,
          unitPriceUSD: catalogItem.priceUSD,
          totalMXN,
          totalUSD,
          photoUrl: it.photoUrl
        };
      } else {
        const priceMXNVal = parseFloat(it.customPrice?.toString() || '0') || 0;
        const priceUSDVal = priceMXNVal / 20;
        const totalMXN = priceMXNVal * it.quantity;
        const totalUSD = priceUSDVal * it.quantity;
        return {
          goodsItemId: it.goodsItemId,
          description: it.customDescription || 'Mercancía Personalizada',
          quantity: it.quantity,
          unitPriceMXN: priceMXNVal,
          unitPriceUSD: priceUSDVal,
          totalMXN,
          totalUSD,
          photoUrl: it.photoUrl
        };
      }
    });

    const finalFolioId = formFolioId.trim() || `F-1025${Math.floor(10 + Math.random() * 89)}`;
    const newFolio: Folio = {
      id: finalFolioId,
      qrCodeUrl: finalFolioId,
      poNumber: formPoNumber.trim() || undefined,
      clientId: clientIdVal,
      clientName: clientName,
      costCenterId: activeCCId,
      costCenterName: costCenterName,
      createdDate: new Date().toISOString(),
      createdBy: currentUser.name,
      status: 'RECOLECCION',
      processType: formProcessType,
      hasGranallado: formProcessType === 'DECAPADO_GRANALLADORA' || formHasGranallado,
      items: processedItems,
      notes: formNotes,
      photoUrl: formPhotoUrls[0] || formPhotoUrl || '',
      photoUrls: formPhotoUrls,
      etaMinutes: 60,
      currentLat: defaultLat,
      currentLng: defaultLng,
      shipmentDate: formShipmentDate,
      destination: destination,
      destinationId: selectedDestinationId || undefined,
      destinationLat: formDestinationLat || undefined,
      destinationLng: formDestinationLng || undefined,
      routeNotes: formRouteNotes || undefined,
      estimatedKm: formEstimatedKm || undefined,
      origin: resolvedOrigin,
      originLat: resolvedOriginLat,
      originLng: resolvedOriginLng,
      driverName: formDriverName || '',
      economicNumber: formEconomicNumber || '',
      history: [
        {
          stage: 'RECOLECCION',
          timestamp: new Date().toISOString(),
          operatorName: currentUser.name,
          notes: `Folio de recepción creado. Envío programado para el ${formShipmentDate}. Origen: ${resolvedOrigin} -> Destino: ${destination}.`
        }
      ]
    };

    onCreateFolio(newFolio);
    setSelectedFolio(newFolio);
    setIsCreating(false);
    
    // Reset filters so the newly created folio immediately appears in the history list
    setStatusFilter('ALL');
    setSearchQuery('');
    setShowCompletedHistory(true);
    
    // Trigger notification
    triggerBackendNotification(newFolio, 'RECOLECCION');

    // Reset Form
    setFormClientId('');
    setFormCostCenterId('');
    setFormHasGranallado(false);
    setFormNotes('');
    setFormItems([]);
    setFormFolioId('');
    setFormPoNumber('');
    setFormShipmentDate('');
    setFormOrigin('');
    setFormOriginLat(undefined);
    setFormOriginLng(undefined);
    setSelectedOriginId('');
    setFormDestination('');
    setFormPhotoUrl('');
    setFormPhotoUrls([]);
    setFormIsDoubleDestination(false);
    setFormDriverName('');
    setFormEconomicNumber('');
  };

  const downloadFolioPDF = (folio: Folio) => {
    generateCentralFolioPDF(folio, { hidePrices: shouldHidePricesForUser(currentUser as any) });
  };

  const startEditingFolio = (folioToEdit?: Folio | any) => {
    if (isClientUser(currentUser)) {
      window.showAppAlert?.('El perfil de Cliente es exclusivamente de consulta y no permite la edición de folios.', 'Acceso Restringido');
      return;
    }
    // Protect against React MouseEvent passed directly as first parameter
    const target: Folio | null = (folioToEdit && typeof folioToEdit === 'object' && 'id' in folioToEdit && !('nativeEvent' in folioToEdit))
      ? (folioToEdit as Folio)
      : selectedFolio;

    if (!target) return;
    setSelectedFolio(target);
    setEditFolioId(target.id || '');
    setEditPoNumber(target.poNumber || '');
    setEditShipmentDate(target.shipmentDate || '');
    
    // Entrega a Planta date extraction and format for input
    const existingDelivery = target.deliveryDate || target.history?.find(h => h.stage === 'ENTREGA_PLANTA' || (h.stage as string) === 'ENTREGA')?.timestamp || '';
    if (existingDelivery) {
      if (existingDelivery.includes('T')) {
        setEditDeliveryDate(existingDelivery.slice(0, 16));
      } else {
        setEditDeliveryDate(`${existingDelivery}T12:00`);
      }
    } else {
      setEditDeliveryDate('');
    }

    setEditOrigin(target.origin || '');
    setEditOriginLat(target.originLat);
    setEditOriginLng(target.originLng);
    setEditDestination(target.destination || '');
    const resolvedProcessType = target.processType || (target.hasGranallado ? 'DECAPADO_GRANALLADORA' : 'DECAPADO_TERMICO');
    setEditProcessType(resolvedProcessType);
    setEditHasGranallado(target.hasGranallado || resolvedProcessType === 'DECAPADO_GRANALLADORA');
    setEditNotes(target.notes || '');
    setEditItems((target.items || []).map(it => ({
      goodsItemId: it.goodsItemId,
      quantity: it.quantity,
      photoUrl: it.photoUrl,
      customDescription: (it as any).description,
      customPrice: (it as any).unitPriceMXN,
      customWeight: (it as any).unitPriceUSD ? parseFloat((it as any).unitPriceUSD) * 20 : 0
    })));
    setEditPhotoUrl(target.photoUrl || '');
    setEditPhotoUrls(target.photoUrls || (target.photoUrl ? [target.photoUrl] : []));
    setEditDriverName(target.driverName || '');
    setEditEconomicNumber(target.economicNumber || '');
    setIsCreating(false);
    setIsEditingFolio(true);
  };

  const handleEditFolioSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isClientUser(currentUser)) {
      await window.showAppAlert('El perfil de Cliente es exclusivamente de consulta y no permite la edición de folios.', 'Acceso Restringido');
      return;
    }
    if (!selectedFolio) return;

    if (editItems.length === 0) {
      await window.showAppAlert('Por favor agrega al menos una mercancía a la lista.', 'Lista de Mercancías Vacía');
      return;
    }

    const previousFolioId = selectedFolio.id;
    const finalFolioId = editFolioId.trim() || previousFolioId;

    // Process items with correct catalog prices
    const processedItems = editItems.map(it => {
      const catalogItem = goods.find(g => g.id === it.goodsItemId);
      if (catalogItem) {
        const priceMXNVal = parseFloat(catalogItem.priceMXN.toString()) || 0;
        const priceUSDVal = parseFloat(catalogItem.priceUSD.toString()) || 0;
        const totalMXN = priceMXNVal * it.quantity;
        const totalUSD = priceUSDVal * it.quantity;
        return {
          goodsItemId: it.goodsItemId,
          description: catalogItem.description,
          quantity: it.quantity,
          unitPriceMXN: catalogItem.priceMXN,
          unitPriceUSD: catalogItem.priceUSD,
          totalMXN,
          totalUSD,
          photoUrl: it.photoUrl
        };
      } else {
        const priceMXNVal = parseFloat((it as any).customPrice?.toString() || '0') || 0;
        const priceUSDVal = priceMXNVal / 20;
        const totalMXN = priceMXNVal * it.quantity;
        const totalUSD = priceUSDVal * it.quantity;
        return {
          goodsItemId: it.goodsItemId,
          description: (it as any).customDescription || 'Mercancía Personalizada',
          quantity: it.quantity,
          unitPriceMXN: priceMXNVal,
          unitPriceUSD: priceUSDVal,
          totalMXN,
          totalUSD,
          photoUrl: it.photoUrl
        };
      }
    });

    // Synchronize deliveryDate and history for ENTREGA_PLANTA
    const formattedDeliveryDate = editDeliveryDate.trim() 
      ? (editDeliveryDate.includes('T') ? editDeliveryDate : `${editDeliveryDate}T12:00:00`)
      : undefined;

    let updatedHistory = [...(selectedFolio.history || [])];
    if (formattedDeliveryDate) {
      const entregaIndex = updatedHistory.findIndex(h => h.stage === 'ENTREGA_PLANTA' || h.stage === 'ENTREGA');
      if (entregaIndex >= 0) {
        updatedHistory[entregaIndex] = {
          ...updatedHistory[entregaIndex],
          timestamp: formattedDeliveryDate,
          notes: updatedHistory[entregaIndex].notes || 'Fecha de entrega a planta actualizada'
        };
      } else {
        updatedHistory.push({
          stage: 'ENTREGA_PLANTA',
          timestamp: formattedDeliveryDate,
          operatorName: currentUser?.name || 'Operador Logística',
          notes: 'Fecha de entrega a planta registrada manualmente'
        });
      }
    }

    const updatedFolio: Folio = {
      ...selectedFolio,
      id: finalFolioId,
      qrCodeUrl: finalFolioId,
      poNumber: editPoNumber.trim() || undefined,
      shipmentDate: editShipmentDate,
      deliveryDate: formattedDeliveryDate,
      history: updatedHistory,
      origin: editOrigin,
      originLat: editOriginLat,
      originLng: editOriginLng,
      destination: editDestination,
      processType: editProcessType,
      hasGranallado: editProcessType === 'DECAPADO_GRANALLADORA' || editHasGranallado,
      notes: editNotes,
      items: processedItems,
      photoUrl: editPhotoUrls[0] || editPhotoUrl || '',
      photoUrls: editPhotoUrls,
      driverName: editDriverName,
      economicNumber: editEconomicNumber,
    };

    onUpdateFolio(updatedFolio, previousFolioId);
    setSelectedFolio(updatedFolio);
    setIsEditingFolio(false);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, itemIdx: number) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target?.result as string;
        if (isEditingFolio) {
          if (itemIdx < 0) {
            const photoIdx = Math.abs(itemIdx) - 1;
            setEditPhotoUrls(prev => {
              const next = [...prev];
              next[photoIdx] = base64;
              return next;
            });
            if (photoIdx === 0) {
              setEditPhotoUrl(base64);
            }
          } else {
            setEditItems(prev => prev.map((item, i) => {
              if (i === itemIdx) {
                return { ...item, photoUrl: base64 };
              }
              return item;
            }));
          }
        } else {
          if (itemIdx < 0) {
            const photoIdx = Math.abs(itemIdx) - 1;
            setFormPhotoUrls(prev => {
              const next = [...prev];
              next[photoIdx] = base64;
              return next;
            });
            if (photoIdx === 0) {
              setFormPhotoUrl(base64);
            }
          } else {
            setFormItems(prev => prev.map((item, i) => {
              if (i === itemIdx) {
                return { ...item, photoUrl: base64 };
              }
              return item;
            }));
          }
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const startWebcam = async (mode: 'user' | 'environment' = cameraFacingMode) => {
    try {
      setWebcamError('');
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: mode } });
      setWebcamStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error(err);
      setWebcamError('No se pudo acceder a la cámara web. Asegúrate de conceder permisos, usar un dominio seguro (HTTPS) o intenta subir un archivo o seleccionar una simulación.');
    }
  };

  const toggleCamera = async () => {
    const nextMode = cameraFacingMode === 'environment' ? 'user' : 'environment';
    setCameraFacingMode(nextMode);
    if (webcamStream) {
      webcamStream.getTracks().forEach(track => track.stop());
      try {
        setWebcamError('');
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: nextMode } });
        setWebcamStream(stream);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error(err);
        setWebcamError('No se pudo acceder a la otra cámara.');
      }
    }
  };

  const stopWebcam = () => {
    if (webcamStream) {
      webcamStream.getTracks().forEach(track => track.stop());
      setWebcamStream(null);
    }
  };

  const capturePhoto = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = 640;
      canvas.height = 480;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg');
        if (activePhotoIndex !== null) {
          if (isEditingFolio) {
            if (activePhotoIndex < 0) {
              const photoIdx = Math.abs(activePhotoIndex) - 1;
              setEditPhotoUrls(prev => {
                const next = [...prev];
                next[photoIdx] = dataUrl;
                return next;
              });
              if (photoIdx === 0) {
                setEditPhotoUrl(dataUrl);
              }
            } else {
              setEditItems(prev => prev.map((item, idx) => {
                if (idx === activePhotoIndex) {
                  return { ...item, photoUrl: dataUrl };
                }
                return item;
              }));
            }
          } else {
            if (activePhotoIndex < 0) {
              const photoIdx = Math.abs(activePhotoIndex) - 1;
              setFormPhotoUrls(prev => {
                const next = [...prev];
                next[photoIdx] = dataUrl;
                return next;
              });
              if (photoIdx === 0) {
                setFormPhotoUrl(dataUrl);
              }
            } else {
              setFormItems(prev => prev.map((item, idx) => {
                if (idx === activePhotoIndex) {
                  return { ...item, photoUrl: dataUrl };
                }
                return item;
              }));
            }
          }
        }
        stopWebcam();
        setActivePhotoIndex(null);
      }
    }
  };

  // Trigger real notification drafting via server Express endpoint and FCM push
  const triggerBackendNotification = async (folio: Folio, targetStage: ProcessStage, customNotes?: string) => {
    try {
      const clientProfile = clients.find(c => c.id === folio.clientId);
      const email = clientProfile?.contactEmail || 'pedroborja91@gmail.com';
      const phone = clientProfile?.contactPhone || '+52 811 000 0000';

      // 1. Dispatch FCM Push Notification (Firebase Cloud Messaging) for background device alerts
      let pushCategory: 'RETRABAJO' | 'DEVOLUCION' | 'ENTREGA_PLANTA' | 'PREFACTURA' | 'URGENTE' | 'GENERAL' = 'GENERAL';
      let titleEmoji = '🔔';

      if (targetStage === 'RETRABAJO') {
        pushCategory = 'RETRABAJO';
        titleEmoji = '🚨 ALERTA CRÍTICA';
      } else if (targetStage === 'DEVOLUCION') {
        pushCategory = 'DEVOLUCION';
        titleEmoji = '⚠️ INCIDENCIA DEVOLUCIÓN';
      } else if (targetStage === 'ENTREGA_PLANTA') {
        pushCategory = 'ENTREGA_PLANTA';
        titleEmoji = '📦 ENTREGA EN PLANTA';
      } else if (targetStage === 'ENTRADA_CEDIS' || targetStage === 'SALIDA_CEDIS') {
        pushCategory = 'URGENTE';
        titleEmoji = '🚚 ACTUALIZACIÓN LOGÍSTICA';
      }

      const notifTitle = `${titleEmoji}: Folio ${folio.id} en ${targetStage.replace(/_/g, ' ')}`;
      const notifBody = customNotes || `El folio ${folio.id} de ${folio.clientName} ha avanzado a la etapa ${targetStage.replace(/_/g, ' ')}.`;

      fcmService.sendCriticalStatusNotification({
        title: notifTitle,
        body: notifBody,
        folioId: folio.id,
        clientName: folio.clientName,
        newStatus: targetStage,
        category: pushCategory,
        url: '/#trazabilidad'
      }).catch(e => console.warn('[FCM Notification Error]:', e));

      const response = await fetch('/api/notifications/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          folioId: folio.id,
          stage: targetStage,
          clientName: folio.clientName,
          contactEmail: email,
          contactPhone: phone,
          notes: customNotes
        })
      });

      const data = await response.json();
      if (data.success) {
        // Log both Email and SMS
        setNotificationsSent(prev => [
          {
            id: `sms-${Math.random()}`,
            folioId: folio.id,
            stage: targetStage,
            type: 'SMS',
            destination: phone,
            message: data.sms.body,
            time: data.timestamp
          },
          {
            id: `email-${Math.random()}`,
            folioId: folio.id,
            stage: targetStage,
            type: 'Email',
            destination: email,
            message: data.email.body.substring(0, 120) + '...',
            time: data.timestamp
          },
          ...prev
        ]);
      }
    } catch (err) {
      console.error('Error triggering backend notification:', err);
    }
  };

  // Generic Stage Update Function
  const updateFolioStage = (
    targetFolio: Folio,
    nextStage: ProcessStage,
    noteText?: string,
    extraFields: Partial<StageHistory> = {}
  ) => {
    if (isClientUser(currentUser)) {
      window.showAppAlert?.('El perfil de Cliente es exclusivamente de consulta y no permite el avance o modificación de etapas.', 'Acceso Restringido');
      return;
    }
    const now = new Date();
    const lastHistory = targetFolio.history[targetFolio.history.length - 1];
    
    // Calculate elapsed duration for the completed stage
    let durationMs: number | undefined;
    if (lastHistory && lastHistory.timestamp) {
      const prevTime = new Date(lastHistory.timestamp).getTime();
      durationMs = now.getTime() - prevTime;
    }

    // Complete previous stage history item with duration
    const updatedHistory = targetFolio.history.map((h, idx) => {
      if (idx === targetFolio.history.length - 1) {
        return { ...h, durationMs };
      }
      return h;
    });

    // Append new stage
    const newHistoryItem: StageHistory = {
      stage: nextStage,
      timestamp: now.toISOString(),
      operatorName: currentUser.name,
      notes: noteText || `Operación avanzada a ${STAGE_LABELS[nextStage]?.label || nextStage} exitosamente por ${currentUser.name}.`,
      ...extraFields
    };

    // Update active logistics map variables
    let etaMinutes = targetFolio.etaMinutes;
    let currentLat = targetFolio.currentLat;
    let currentLng = targetFolio.currentLng;

    if (nextStage === 'SALIDA_CEDIS') {
      etaMinutes = 25; // 25 minutes ETA
      // Halfway between CEDIS and Client
      const matchedClient = clients.find(c => c.id === targetFolio.clientId);
      if (matchedClient) {
        currentLat = (25.7500 + matchedClient.lat) / 2;
        currentLng = (-100.3100 + matchedClient.lng) / 2;
      }
    } else if (nextStage === 'ENTREGA_PLANTA') {
      etaMinutes = 0;
      const matchedClient = clients.find(c => c.id === targetFolio.clientId);
      if (matchedClient) {
        currentLat = matchedClient.lat;
        currentLng = matchedClient.lng;
      }
      dataService.deleteDriverLocationByFolioId(targetFolio.id);
    } else if (nextStage === 'DEVOLUCION') {
      dataService.deleteDriverLocationByFolioId(targetFolio.id);
    }

    const updatedFolio: Folio = {
      ...targetFolio,
      status: nextStage,
      etaMinutes,
      currentLat,
      currentLng,
      history: [...updatedHistory, newHistoryItem]
    };

    onUpdateFolio(updatedFolio);
    if (selectedFolio?.id === targetFolio.id) {
      setSelectedFolio(updatedFolio);
    }
    triggerBackendNotification(updatedFolio, nextStage, noteText);
  };

  // Advance Stage of Folio (State Machine)
  const handleAdvanceStage = (failInspection: boolean = false) => {
    if (!selectedFolio) return;

    const currentStage = selectedFolio.status;
    let nextStage: ProcessStage = 'ENTRADA_CEDIS';

    // State machine logic
    if (currentStage === 'RECOLECCION') {
      nextStage = 'ENTRADA_CEDIS';
    } else if (currentStage === 'ENTRADA_CEDIS') {
      nextStage = 'DECAPADO_TERMICO_QUIMICO';
    } else if (currentStage === 'DECAPADO_TERMICO_QUIMICO') {
      // Si es Granalladora, pasa directo a Inspección (omite Lavado). Si es Térmico o Químico, pasa a Lavado.
      const isGranallado = selectedFolio.processType === 'DECAPADO_GRANALLADORA' || selectedFolio.hasGranallado;
      nextStage = isGranallado ? 'INSPECCION' : 'DECAPADO_LAVADO';
    } else if (currentStage === 'DECAPADO_LAVADO') {
      nextStage = 'INSPECCION';
    } else if (currentStage === 'INSPECCION') {
      if (failInspection) {
        handleOpenReworkModal(selectedFolio);
        return; // handle inside modal submit
      } else {
        nextStage = 'SALIDA_CEDIS';
      }
    } else if (currentStage === 'RETRABAJO') {
      // From rework it goes back to Inspección CC
      nextStage = 'INSPECCION';
    } else if (currentStage === 'SALIDA_CEDIS') {
      nextStage = 'ENTREGA_PLANTA';
    } else if (currentStage === 'ENTREGA_PLANTA') {
      // Can register returns
      setShowReturnModal(true);
      return;
    }

    applyStageChange(nextStage);
  };

  const applyStageChange = (nextStage: ProcessStage, noteText?: string, extraFields: Partial<StageHistory> = {}) => {
    if (!selectedFolio) return;
    updateFolioStage(selectedFolio, nextStage, noteText, extraFields);
  };

  // Bulk Confirm Entrada CEDIS
  const handleConfirmAllEntradaCedis = () => {
    const pendingFolios = folios.filter(f => f.status === 'ENTRADA_CEDIS' || f.status === 'RECOLECCION');
    if (pendingFolios.length === 0) return;
    pendingFolios.forEach(f => {
      const targetNext = f.status === 'RECOLECCION' ? 'ENTRADA_CEDIS' : 'DECAPADO_TERMICO_QUIMICO';
      updateFolioStage(f, targetNext, `Entrada al CEDIS confirmada por ${currentUser.name}`);
    });
  };

  // Submit Rework (Retrabajo)
  const handleReworkSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reworkReport) return;

    const validPhotos = reworkPhotos.filter(p => !!p);
    const totalPzas = selectedFolio?.items?.reduce((a, b) => a + (Number(b.quantity) || 0), 0) || 1;
    const scopeLabel = reworkType === 'TOTAL' ? `[TODO EL FOLIO - ${totalPzas} pzas]` : `[RETRABAJO PARCIAL - ${reworkQuantity} de ${totalPzas} pzas]`;

    applyStageChange('RETRABAJO', 'Inspección de calidad fallida. Requiere retrabajo.', {
      incidenceReport: `${scopeLabel} Defecto: ${reworkReport} | Componentes: ${reworkPiecesName || 'General'} (${reworkQuantity} pzas)`,
      reworkPiecesName: reworkPiecesName || 'Piezas con defecto de inspección',
      affectedPieces: reworkQuantity || 1,
      photos: validPhotos
    });
    
    setReworkReport('');
    setReworkPiecesName('');
    setReworkQuantity(1);
    setReworkPhotos(['', '', '', '', '', '', '']);
    setShowReworkModal(false);
  };

  // Submit Return (Devolución)
  const handleReturnSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFolio) return;

    let formattedReturnDetails = '';
    let returnedItemsList: Array<{ goodsItemId?: string; description: string; quantityReturned: number; originalQuantity: number }> = [];

    if (returnType === 'TOTAL') {
      const totalPieces = selectedFolio.items?.reduce((acc, curr) => acc + (Number(curr.quantity) || 0), 0) || 0;
      formattedReturnDetails = `[DEVOLUCIÓN TOTAL] Se registra devolución completa del folio (${totalPieces} piezas).\nMotivo / Detalle: ${returnDetails}`;
      returnedItemsList = selectedFolio.items?.map(it => ({
        goodsItemId: it.goodsItemId,
        description: it.description,
        quantityReturned: Number(it.quantity) || 0,
        originalQuantity: Number(it.quantity) || 0
      })) || [];
    } else {
      // Parcial
      const selectedEntries = Object.entries(partialReturnItems).filter(([_, val]: [string, { selected: boolean; quantity: number }]) => val.selected && val.quantity > 0);
      if (selectedEntries.length === 0) {
        alert('Por favor seleccione al menos un material/partida para registrar la devolución parcial.');
        return;
      }

      const returnedDescLines: string[] = [];
      selectedFolio.items?.forEach((it, idx) => {
        const key = it.goodsItemId || `item-${idx}`;
        const itemState = partialReturnItems[key];
        if (itemState?.selected && itemState.quantity > 0) {
          returnedItemsList.push({
            goodsItemId: it.goodsItemId,
            description: it.description,
            quantityReturned: itemState.quantity,
            originalQuantity: Number(it.quantity) || 0
          });
          returnedDescLines.push(`• ${it.description}: ${itemState.quantity} de ${it.quantity} ${it.unit || 'piezas'}`);
        }
      });

      formattedReturnDetails = `[DEVOLUCIÓN PARCIAL] Materiales devueltos:\n${returnedDescLines.join('\n')}\n\nMotivo / Detalle: ${returnDetails}`;
    }

    updateFolioStage(selectedFolio, 'DEVOLUCION', `Devolución ${returnType === 'TOTAL' ? 'Total' : 'Parcial'} registrada en entrega a planta`, {
      returnDetails: formattedReturnDetails,
      returnType,
      returnedItems: returnedItemsList
    });

    setReturnDetails('');
    setShowReturnModal(false);
  };

  // Complete return to final Plant Delivery (clean exit)
  const handleFinalizePlantDelivery = () => {
    applyStageChange('ENTREGA_PLANTA', 'Operación logística completada de forma satisfactoria.');
  };

  // Printable Area representation
  const handlePrintLabel = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow && selectedFolio) {
      printWindow.document.write(`
        <html>
        <head><title>Imprimir Etiqueta QR - SNF - SIC</title></head>
        <body style="font-family: Arial, sans-serif; text-align: center; padding: 40px;">
          <h2>ETIQUETA DE TRAZABILIDAD QR</h2>
          <div style="border: 2px dashed #000; padding: 20px; display: inline-block; border-radius: 10px;">
            <p><strong>FOLIO:</strong> ${selectedFolio.id}</p>
            <p><strong>CLIENTE:</strong> ${selectedFolio.clientName}</p>
            <p><strong>C. COSTOS:</strong> ${selectedFolio.costCenterName}</p>
            <p><strong>PROCESO:</strong> ${selectedFolio.status}</p>
            <div style="background-color: #f3f4f6; width: 150px; height: 150px; margin: 20px auto; display: flex; flex-direction: column; items: center; justify-content: center; border: 1px solid #ccc; border-radius: 8px;">
              <span style="font-size: 11px; font-weight: bold; margin-bottom: 5px;">QR SNF - SIC</span>
              <span style="font-size: 24px;">⚓ ${selectedFolio.id}</span>
            </div>
            <p style="font-size: 11px; color: #666;">Escanee en el portal interno de SNF - SIC para actualización de estatus</p>
          </div>
          <script>window.print();</script>
        </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  return (
    <div className={`grid grid-cols-1 ${currentUser?.role === 'CHOFER' ? 'max-w-4xl mx-auto' : 'lg:grid-cols-3'} gap-4`} id="folio-manager-container">
      
      {/* LEFT COLUMN: Folios List & Creation triggers */}
      <div className="space-y-4" id="folios-list-panel">
        
        {!isOnline && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 p-3.5 rounded-xl text-xs space-y-1 shadow-xs flex items-start gap-2.5" id="folios-offline-warning-banner">
            <AlertTriangle className="h-4.5 w-4.5 text-amber-600 shrink-0 mt-0.5 animate-pulse" />
            <div className="space-y-0.5">
              <span className="font-black text-[10px] uppercase tracking-wider text-amber-900 block font-sans">Visualizando Datos Fuera de Línea</span>
              <p className="text-[11px] text-amber-700 leading-normal font-medium">
                La información de los folios proviene de la base de datos local (localStorage). Puede estar desactualizada respecto al servidor hasta que se restablezca la conexión.
              </p>
            </div>
          </div>
        )}

        {/* Main List Container Card */}
        <div className="bg-white p-4 md:p-5 rounded-2xl border border-slate-200/90 shadow-sm space-y-4">
          
          {/* Top Title & Location Bar */}
          <div className="flex items-start justify-between pb-3 border-b border-slate-100 gap-2">
            <div>
              <span className="text-[10px] uppercase font-black tracking-widest text-red-600 bg-red-50 border border-red-200/80 px-2.5 py-0.5 rounded-full inline-block mb-1">
                MIS CARGAS Y ENVÍOS OUT
              </span>
              <h3 className="text-sm md:text-base font-extrabold text-slate-900 flex flex-wrap items-center gap-1.5">
                <span>📍</span>
                <span>
                  {selectedCostCenter?.name?.toUpperCase() || 
                   (costCenters && costCenters.length > 0 
                     ? costCenters.map(cc => cc.name.replace(/^Sede\s+/i, '').replace(/^Planta\s+/i, '')).join(' / ').toUpperCase()
                     : (currentSelectedClient?.name ? `${currentSelectedClient.name.toUpperCase()}` : 'SAN LUIS POTOSÍ'))}
                </span>
                <span className="text-slate-300">•</span>
                <span>🏢</span>
                <span className="text-red-700 font-black">{currentSelectedClient?.name?.toUpperCase() || 'CLIENTE SELECCIONADO'}</span>
              </h3>
            </div>
            
            <button
              onClick={handleManualRefresh}
              className={`p-2 hover:bg-slate-100 rounded-xl text-slate-500 hover:text-slate-800 transition-all cursor-pointer shrink-0 ${isRefreshing ? 'animate-spin text-red-600' : ''}`}
              title="Actualizar Historial y Sincronizar"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>

          {/* Detailed Data Synchronization & Storage Status Banner */}
          <div className="flex flex-wrap items-center justify-between bg-slate-50 p-2.5 rounded-xl border border-slate-200 gap-2 text-xs">
            <div className="flex items-center gap-2">
              <span className={`h-2.5 w-2.5 rounded-full ${isOnline ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
              <span className="text-slate-700 font-mono text-[11px] font-semibold">
                {isOnline ? 'Origen: Firestore DB (Sincronización Activa)' : 'Origen: Caché Local (Modo Offline / Dual-Cache)'}
              </span>
            </div>
            {(isLoading || isSyncing || isRefreshing) && (
              <div className="flex items-center gap-1.5 text-red-600 text-[11px] font-bold animate-pulse">
                <RefreshCw className="h-3 w-3 animate-spin" />
                <span>Recuperando y Sincronizando Folios...</span>
              </div>
            )}
          </div>

          {/* Subheader Title */}
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider">
              Catálogo de Mercancías y Folios • {currentSelectedClient?.name || 'Cliente'}
            </h4>
            <span className="text-[11px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md font-mono">
              {filteredFolios.length} {filteredFolios.length === 1 ? 'folio' : 'folios'}
            </span>
          </div>

          {/* Driver & Operator 4-Stage Quick Navigation Bar */}
          <div className="space-y-1.5 pt-1">
            <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-wider text-slate-500">
              <span>Fases de Operación y Trazabilidad:</span>
              {currentUser?.role === 'CHOFER' && (
                <span className="text-amber-700 bg-amber-100/80 px-2 py-0.5 rounded-full border border-amber-300 font-extrabold flex items-center gap-1">
                  🚚 ROL CHOFER
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5">
              <button
                type="button"
                onClick={() => setStatusFilter('ALL')}
                className={`px-2.5 py-1.5 rounded-xl text-[11px] font-extrabold transition-all border flex items-center justify-center gap-1 cursor-pointer col-span-2 sm:col-span-1 ${
                  statusFilter === 'ALL'
                    ? 'bg-slate-900 text-white border-slate-950 shadow-xs ring-2 ring-slate-400/20'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                }`}
              >
                <span>📋 Ver Todos ({clientFolios.length})</span>
              </button>

              <button
                type="button"
                onClick={() => setStatusFilter('RECOLECCION')}
                className={`px-2.5 py-1.5 rounded-xl text-[11px] font-extrabold transition-all border flex items-center justify-center gap-1 cursor-pointer ${
                  statusFilter === 'RECOLECCION'
                    ? 'bg-blue-600 text-white border-blue-700 shadow-xs'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                }`}
              >
                <span>🚚 1. Recolección</span>
              </button>

              <button
                type="button"
                onClick={() => setStatusFilter('ENTRADA_CEDIS')}
                className={`px-2.5 py-1.5 rounded-xl text-[11px] font-extrabold transition-all border flex items-center justify-center gap-1 cursor-pointer ${
                  statusFilter === 'ENTRADA_CEDIS'
                    ? 'bg-purple-600 text-white border-purple-700 shadow-xs'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                }`}
              >
                <span>🏭 2. Entrada CEDIS</span>
              </button>

              <button
                type="button"
                onClick={() => setStatusFilter('SALIDA_CEDIS')}
                className={`px-2.5 py-1.5 rounded-xl text-[11px] font-extrabold transition-all border flex items-center justify-center gap-1 cursor-pointer ${
                  statusFilter === 'SALIDA_CEDIS' || statusFilter === 'EN_TRANSITO'
                    ? 'bg-amber-600 text-white border-amber-700 shadow-xs'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                }`}
              >
                <span>🚛 3. Salida CEDIS</span>
              </button>

              <button
                type="button"
                onClick={() => setStatusFilter('ENTREGADO')}
                className={`px-2.5 py-1.5 rounded-xl text-[11px] font-extrabold transition-all border flex items-center justify-center gap-1 cursor-pointer ${
                  statusFilter === 'ENTREGADO' || statusFilter === 'ENTREGA_PLANTA'
                    ? 'bg-emerald-600 text-white border-emerald-700 shadow-xs'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                }`}
              >
                <span>🏢 4. Entrega Planta</span>
              </button>
            </div>
          </div>

          {/* Search and Filter Row with Intelligent Folio Filter */}
          <div className="space-y-2">
            <div className="flex flex-col sm:flex-row gap-2">
              {/* Intelligent Folio Search Input */}
              <div className="relative flex-1">
                <div className="absolute left-3 top-2.5 flex items-center gap-1 text-slate-400">
                  <Search className="h-4 w-4" />
                  <Sparkles className="h-3 w-3 text-amber-500" />
                </div>
                <input 
                  type="text" 
                  placeholder="🔍 Búsqueda Inteligente: Folio (ej. F-10254, 10254), PO, Mercancía, Chofer..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 pr-8 py-2 bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 focus:border-red-500 rounded-xl text-xs w-full focus:outline-none text-slate-900 placeholder-slate-400 font-medium transition-all shadow-2xs"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2.5 top-2.5 p-0.5 hover:bg-slate-200 rounded-full text-slate-400 hover:text-slate-700 transition-colors cursor-pointer text-xs"
                    title="Limpiar búsqueda"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {/* Client Filter Dropdown / Badge (Strict Isolation) */}
              {(isClientUser(currentUser) || isChoferUser(currentUser) || Boolean(activeClient)) ? (
                <div className="flex items-center gap-1.5 bg-red-50 border border-red-200/80 rounded-xl px-3 py-1.5 text-xs text-red-900 font-bold shrink-0">
                  <Building2 className="h-4 w-4 text-red-600 shrink-0" />
                  <span className="truncate max-w-[200px]">{currentSelectedClient?.name || activeClient?.name || 'Cliente Asignado'}</span>
                  <span className="bg-red-600 text-white font-mono text-[9px] px-1.5 py-0.5 rounded font-black">
                    {isChoferUser(currentUser) ? 'SEDE CHOFER' : 'EXCLUSIVO'}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-xl px-2.5 py-1 shrink-0">
                  <Building2 className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                  <select
                    value={clientFilter}
                    onChange={(e) => {
                      setClientFilter(e.target.value);
                      setSearchQuery('');
                    }}
                    className="bg-transparent text-xs font-bold text-slate-800 focus:outline-none cursor-pointer py-1 max-w-[200px] truncate"
                    title="Seleccionar Cliente"
                  >
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>🏢 {c.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Status Filter Dropdown */}
              <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-xl px-2.5 py-1 shrink-0">
                <Filter className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                <select 
                  value={statusFilter} 
                  onChange={(e) => setStatusFilter(e.target.value)} 
                  className="bg-transparent text-xs font-bold text-slate-700 focus:outline-none cursor-pointer py-1"
                >
                  <option value="ALL">Ver Todos los Folios</option>
                  <option value="NOT_INTEGRATED_PO">⚠️ No Integrados a PO</option>
                  <option value="INTEGRATED_PO">✓ Integrados a PO</option>
                  <option value="RECOLECCION">1. RECOLECCIÓN</option>
                  <option value="ENTRADA_CEDIS">2. ENTRADA CEDIS</option>
                  <option value="SALIDA_CEDIS">3. SALIDA CEDIS (EN TRÁNSITO)</option>
                  <option value="ENTREGADO">4. ENTREGA PLANTA (ENTREGADOS)</option>
                  <option value="EN_PROCESO">• EN PROCESO DECAPADO</option>
                  <option value="DEVOLUCION">• DEVOLUCIÓN</option>
                </select>
              </div>
            </div>

            {/* Quick Folios Intelligent Chips Bar */}
            {clientFolios.length > 0 && (
              <div className="flex items-center justify-between gap-2 pt-0.5 overflow-x-auto text-[11px] no-scrollbar">
                <div className="flex items-center gap-1.5 shrink-0 text-slate-500 font-medium">
                  <Hash className="h-3.5 w-3.5 text-red-500" />
                  <span className="font-bold text-slate-700">Folios rápidos ({currentSelectedClient?.name || 'Cliente'}):</span>
                </div>
                <div className="flex items-center gap-1 overflow-x-auto py-0.5">
                  {clientFolios.slice(0, 10).map(f => {
                    const isAct = selectedFolio?.id === f.id || searchQuery.trim().toLowerCase() === f.id.toLowerCase();
                    return (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => {
                          setSelectedFolio(f);
                          setSearchQuery(f.id);
                        }}
                        className={`px-2 py-0.5 rounded-lg font-mono text-[10px] font-bold border transition-all cursor-pointer whitespace-nowrap ${
                          isAct
                            ? 'bg-red-600 text-white border-red-700 shadow-2xs'
                            : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200'
                        }`}
                        title={`Ver folio #${f.id} (${f.status})`}
                      >
                        #{f.id}
                      </button>
                    );
                  })}
                  {clientFolios.length > 10 && (
                    <span className="text-[10px] text-slate-400 font-mono pl-1">+{clientFolios.length - 10} más</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Dedicated Chofer Encapsulation & Delivery Control Banner */}
          {selectedFoliosForRoute.length > 0 && (
            <div className="bg-slate-900 text-white p-4 rounded-2xl border-2 border-amber-500/60 shadow-xl space-y-3 animate-in fade-in duration-200">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 pb-2.5">
                <div className="flex items-center gap-2">
                  <div className="bg-amber-500 text-slate-950 p-2 rounded-xl shrink-0">
                    <Truck className="h-5 w-5 stroke-[2.5]" />
                  </div>
                  <div>
                    <h4 className="font-extrabold text-sm text-amber-400 flex items-center gap-2">
                      <span>🚚 PANEL CHOFER: ENCAPSULADO DE RUTA</span>
                      <span className="bg-amber-400 text-slate-950 font-mono text-[10px] font-black px-2 py-0.5 rounded-full">
                        {selectedFoliosForRoute.length} Folio(s)
                      </span>
                    </h4>
                    <p className="text-[11px] text-slate-300">
                      Transmite un solo rastreo GPS unificado para los folios seleccionados que entregará en planta
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setSelectedFoliosForRoute([])}
                  className="text-xs text-slate-400 hover:text-white underline cursor-pointer"
                >
                  Limpiar Selección
                </button>
              </div>

              {/* List of Encapsulated Folios Pills */}
              <div className="flex flex-wrap gap-1.5">
                {selectedFoliosForRoute.map(id => {
                  const f = folios.find(item => item.id === id);
                  const firstF = folios.find(item => item.id === selectedFoliosForRoute[0]);
                  const isMismatched = f && firstF && selectedFoliosForRoute.length > 1 &&
                    getFolioDestKey(f) !== getFolioDestKey(firstF);

                  return (
                    <span 
                      key={id} 
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-mono font-bold flex items-center gap-1.5 border transition-all ${
                        isMismatched
                          ? 'bg-red-900/90 text-red-200 border-red-500 shadow-sm animate-pulse'
                          : 'bg-slate-800 text-amber-300 border-slate-700'
                      }`}
                    >
                      <span>#{id}</span>
                      {f && <span className="text-slate-400 font-sans text-[10px]">({getFolioDestLabel(f)})</span>}
                      <button
                        type="button"
                        onClick={() => toggleSelectFolioForRoute(id)}
                        className="hover:text-red-400 ml-1 cursor-pointer font-sans"
                        title="Quitar del listado"
                      >
                        ✕
                      </button>
                    </span>
                  );
                })}
              </div>

              {/* Mismatch Warning Callout */}
              {(() => {
                if (selectedFoliosForRoute.length <= 1) return null;
                const firstF = folios.find(item => item.id === selectedFoliosForRoute[0]);
                if (!firstF) return null;
                const firstKey = getFolioDestKey(firstF);
                const firstLabel = getFolioDestLabel(firstF);

                const mismatched = selectedFoliosForRoute
                  .map(id => folios.find(item => item.id === id))
                  .filter((f): f is Folio => f !== undefined && getFolioDestKey(f) !== firstKey);

                if (mismatched.length === 0) return null;

                return (
                  <div className="bg-red-950/90 border-2 border-red-500 text-red-100 p-3.5 rounded-xl text-xs space-y-1.5 shadow-lg">
                    <div className="font-extrabold text-red-400 flex items-center gap-1.5 text-xs">
                      <span>⚠️ ATENCIÓN CHOFER: DESTINO NO CORRESPONDE</span>
                    </div>
                    <p className="text-xs">
                      El o los folios <strong className="text-white font-mono bg-red-900/80 px-1.5 py-0.5 rounded border border-red-600">{mismatched.map(f => `#${f.id}`).join(', ')}</strong> tienen un destino distinto al destino principal (<strong className="text-amber-300 font-extrabold">"{firstLabel}"</strong>).
                    </p>
                    <p className="text-[11px] font-bold text-amber-200">
                      👉 Por favor quítalos del listado desmarcándolos (o haciendo clic en la ✕ de la etiqueta) para continuar.
                    </p>
                  </div>
                );
              })()}

              {/* Action Buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleStartChoferLocationSharing}
                  className={`py-2.5 px-3 rounded-xl font-extrabold text-xs flex items-center justify-center gap-2 transition-all cursor-pointer ${
                    isChoferTrackingActive
                      ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg ring-2 ring-emerald-400 animate-pulse'
                      : 'bg-amber-500 hover:bg-amber-400 text-slate-950 shadow-md'
                  }`}
                >
                  <span className="relative flex h-2 w-2">
                    {isChoferTrackingActive && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>}
                    <span className={`relative inline-flex rounded-full h-2 w-2 ${isChoferTrackingActive ? 'bg-white' : 'bg-slate-950'}`}></span>
                  </span>
                  <span>{isChoferTrackingActive ? '🟢 Transmitiendo GPS Live' : '📡 Compartir Mi Ubicación (GPS)'}</span>
                </button>

                <button
                  type="button"
                  onClick={handleBulkDeliverSelectedFolios}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white py-2.5 px-3 rounded-xl font-extrabold text-xs flex items-center justify-center gap-2 shadow-md cursor-pointer transition-all border border-emerald-400/30"
                >
                  <span>✅ Registrar Entrega en Planta</span>
                </button>

                <button
                  type="button"
                  onClick={() => setShowEncapsulatedMapModal(true)}
                  className="bg-slate-800 hover:bg-slate-700 text-amber-300 py-2.5 px-3 rounded-xl font-extrabold text-xs flex items-center justify-center gap-2 cursor-pointer transition-all border border-slate-700"
                >
                  <span>🗺️ Ver Mapa Encapsulado</span>
                </button>
              </div>
            </div>
          )}

          {/* Entrada CEDIS Phase Banner */}
          {statusFilter === 'ENTRADA_CEDIS' && (
            <div className="bg-purple-900 text-white p-3.5 rounded-2xl border-2 border-purple-500/60 shadow-lg flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <div className="bg-purple-500 text-purple-950 p-2 rounded-xl shrink-0 font-extrabold">
                  <Truck className="h-5 w-5 stroke-[2.5]" />
                </div>
                <div>
                  <h4 className="font-extrabold text-xs text-purple-200 flex items-center gap-2">
                    <span>🏭 CONFIRMACIÓN DE ENTRADA AL CEDIS</span>
                  </h4>
                  <p className="text-[11px] text-purple-200">
                    Confirma el ingreso formal de los lotes recolectados a las instalaciones de CEDIS.
                  </p>
                </div>
              </div>
              {folios.filter(f => f.status === 'ENTRADA_CEDIS' || f.status === 'RECOLECCION').length > 0 && (
                <button
                  type="button"
                  onClick={handleConfirmAllEntradaCedis}
                  className="bg-purple-400 hover:bg-purple-300 text-slate-950 font-black text-xs py-2 px-3.5 rounded-xl shadow-md transition-all cursor-pointer whitespace-nowrap"
                >
                  <span>Confirmar Entrada al CEDIS 🏭</span>
                </button>
              )}
            </div>
          )}

          {/* Select All in Salida CEDIS Toggle Banner */}
          {foliosInSalidaCedis.length > 0 && (
            <div className="bg-amber-50/90 p-2.5 rounded-xl border border-amber-200 flex items-center justify-between gap-2 text-xs">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="chk-select-all-salida"
                  checked={selectedFoliosForRoute.length > 0 && selectedFoliosForRoute.length === foliosInSalidaCedis.length}
                  onChange={toggleSelectAllSalidaCedis}
                  className="w-4 h-4 rounded text-red-600 border-slate-300 focus:ring-red-500 cursor-pointer"
                />
                <label htmlFor="chk-select-all-salida" className="font-bold text-amber-950 cursor-pointer select-none">
                  {selectedFoliosForRoute.length === foliosInSalidaCedis.length
                    ? `Desmarcar todos en Salida CEDIS (${foliosInSalidaCedis.length})`
                    : `Seleccionar todos en Salida CEDIS (${foliosInSalidaCedis.length} folios para entrega)`}
                </label>
              </div>

              {selectedFoliosForRoute.length > 0 && (
                <span className="bg-amber-200 text-amber-900 font-mono font-black px-2 py-0.5 rounded-full text-[10px]">
                  {selectedFoliosForRoute.length} seleccionado(s)
                </span>
              )}
            </div>
          )}

          {/* Primary Action Button: + Nuevo Envío Logístico (Solo personal interno y en Fase de Recolección) */}
          {!isClientUser(currentUser) && (statusFilter === 'RECOLECCION' || (statusFilter === 'ALL' && currentUser?.role !== 'CHOFER')) && (
            <button
              onClick={() => { setIsCreating(true); setIsEditingFolio(false); }}
              className="bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wider text-xs md:text-sm py-3 px-4 rounded-xl shadow-sm w-full flex items-center justify-center gap-2 cursor-pointer transition-all active:scale-[0.99] border border-red-700"
              id="btn-create-nuevo-envio-logistico"
            >
              <Plus className="h-4 w-4 stroke-[3]" />
              <span>+ Nuevo Envío Logístico</span>
            </button>
          )}

          {/* Client Consultation Notice */}
          {isClientUser(currentUser) && (
            <div className="bg-slate-100 border border-slate-200 rounded-xl p-2.5 text-center text-xs text-slate-600 font-semibold flex items-center justify-center gap-1.5 shadow-2xs">
              <Eye className="h-4 w-4 text-slate-500" />
              <span>Modo Consulta: Historial de Envíos y Trazabilidad</span>
            </div>
          )}

          {/* Folio Cards Vertical List or Skeleton Screen */}
          <div className="space-y-3 max-h-[620px] overflow-y-auto pr-1">
            {(isLoading || isSyncing || isRefreshing) ? (
              /* SKELETON SCREEN FOR FOLIOS */
              <div className="space-y-3 animate-pulse">
                {[1, 2, 3, 4].map((n) => (
                  <div key={n} className="p-4 rounded-2xl border border-slate-200 bg-white space-y-3 shadow-2xs">
                    <div className="flex items-center justify-between">
                      <div className="h-4 bg-slate-200 rounded-md w-1/3"></div>
                      <div className="h-5 bg-slate-200 rounded-full w-24"></div>
                    </div>
                    <div className="h-3 bg-slate-100 rounded-md w-3/4"></div>
                    <div className="grid grid-cols-2 gap-2 pt-1">
                      <div className="h-3 bg-slate-100 rounded-md w-full"></div>
                      <div className="h-3 bg-slate-100 rounded-md w-full"></div>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                      <div className="h-3 bg-slate-200 rounded-md w-1/4"></div>
                      <div className="h-3 bg-slate-200 rounded-md w-1/5"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredFolios.length > 0 ? (
              filteredFolios.map((folio) => {
                const currentStageInfo = STAGE_LABELS[folio.status] || { label: folio.status, color: 'bg-slate-100 text-slate-700 border-slate-200' };
                const isSelected = selectedFolio?.id === folio.id;

                // Total weight calculation
                const totalWeightKg = folio.items.reduce((sum, item) => {
                  const goodsItem = goods.find(g => g.id === item.goodsItemId);
                  const w = goodsItem?.weightKg || 0;
                  return sum + (w * item.quantity);
                }, 0);

                const itemsText = folio.items && folio.items.length > 0 
                  ? folio.items.map(i => `${i.quantity}x ${i.description}`).join(', ') 
                  : (folio.notes || `Envío Logístico ${folio.id}`);

                return (
                  <div
                    key={folio.id}
                    onClick={() => { setSelectedFolio(folio); setIsCreating(false); }}
                    className={`p-4 rounded-2xl border transition-all cursor-pointer space-y-2.5 relative ${
                      isSelected 
                        ? 'bg-red-50/20 border-red-400 shadow-md ring-2 ring-red-500/20' 
                        : 'bg-white border-slate-200/90 shadow-2xs hover:border-slate-300 hover:shadow-md'
                    }`}
                  >
                    {/* Checkbox for Multi-Folio Delivery Encapsulation */}
                    {folio.status === 'SALIDA_CEDIS' && (
                      <div 
                        onClick={(e) => { e.stopPropagation(); toggleSelectFolioForRoute(folio.id); }}
                        className={`p-2 rounded-xl border transition-all flex items-center justify-between cursor-pointer ${
                          selectedFoliosForRoute.includes(folio.id)
                            ? 'bg-amber-100/90 border-amber-400 text-amber-950 shadow-xs'
                            : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-amber-50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedFoliosForRoute.includes(folio.id)}
                            onChange={() => {}}
                            className="w-4 h-4 rounded text-red-600 border-slate-300 focus:ring-red-500 cursor-pointer"
                          />
                          <span className="text-[11px] font-bold">
                            {selectedFoliosForRoute.includes(folio.id) ? '☑️ Encapsulado para Entrega' : '☐ Seleccionar para Entrega en Planta'}
                          </span>
                        </div>
                        <span className="text-[10px] font-mono text-amber-800 font-extrabold">Salida CEDIS</span>
                      </div>
                    )}

                    {/* Top Row: Folio ID Badge + Status Pill */}
                    <div className="flex items-center justify-between gap-2">
                      <span className="bg-slate-100 text-slate-800 font-mono text-xs font-black px-2.5 py-1 rounded-md border border-slate-200/80 shrink-0">
                        {folio.id}
                      </span>
                      
                      {/* Status Pill Badge: Shows 'NO INTEGRADO A PO' until user integrates the folio to a PO */}
                      {!isFolioIntegratedToPo(folio) ? (
                        <span className="bg-amber-100 text-amber-900 font-black text-[10px] md:text-[11px] px-2.5 py-1 rounded-full border border-amber-300 flex items-center gap-1.5 shrink-0 shadow-2xs">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-600"></span>
                          • NO INTEGRADO A PO
                        </span>
                      ) : folio.status === 'ENTREGA_PLANTA' ? (
                        <span className="bg-emerald-100/90 text-emerald-800 font-black text-[11px] px-3 py-1 rounded-full border border-emerald-200 flex items-center gap-1.5 shrink-0">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                          • ENTREGADO
                        </span>
                      ) : folio.status === 'SALIDA_CEDIS' ? (
                        <span className="bg-amber-100/90 text-amber-800 font-black text-[11px] px-3 py-1 rounded-full border border-amber-200 flex items-center gap-1.5 shrink-0">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-600 animate-pulse"></span>
                          • EN TRÁNSITO
                        </span>
                      ) : folio.status === 'DEVOLUCION' ? (
                        <span className="bg-rose-100/90 text-rose-800 font-black text-[11px] px-3 py-1 rounded-full border border-rose-200 flex items-center gap-1.5 shrink-0">
                          <span className="w-1.5 h-1.5 rounded-full bg-rose-600"></span>
                          • DEVOLUCIÓN
                        </span>
                      ) : (
                        <span className="bg-blue-100/90 text-blue-800 font-black text-[11px] px-3 py-1 rounded-full border border-blue-200 flex items-center gap-1.5 shrink-0">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse"></span>
                          • {currentStageInfo.label.toUpperCase()}
                        </span>
                      )}
                    </div>

                    {/* Material / Description Text */}
                    <h4 className="text-xs md:text-sm font-bold text-slate-900 leading-snug line-clamp-2 pt-0.5">
                      {itemsText}
                    </h4>

                    {/* Dotted Divider */}
                    <div className="border-b border-dashed border-slate-200 my-1"></div>

                    {/* Metadata Grid */}
                    <div className="grid grid-cols-3 gap-2 text-xs font-sans">
                      <div>
                        <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block font-mono">CLIENTE</span>
                        <span className="text-xs font-extrabold text-slate-900 mt-0.5 block truncate">{folio.clientName || 'BMW GROUP'}</span>
                      </div>
                      <div>
                        <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block font-mono">C. COSTOS</span>
                        <span className="text-xs font-extrabold text-slate-900 mt-0.5 block truncate" title={getCostCenterNameForFolio(folio)}>
                          {getCostCenterNameForFolio(folio)}
                        </span>
                      </div>
                      <div>
                        {isFolioIntegratedToPo(folio) ? (
                          <>
                            <span className="text-[9px] text-purple-700 font-bold uppercase tracking-wider block font-mono">PO / OC INCLUIDA</span>
                            <span className="text-xs font-black text-purple-900 font-mono mt-0.5 block truncate" title={folio.poNumber}>
                              {folio.poNumber}
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="text-[9px] text-amber-700 font-bold uppercase tracking-wider block font-mono">PO / OC</span>
                            <span className="text-[11px] font-bold text-amber-800 mt-0.5 block truncate">
                              No integrado a PO
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Footer Row */}
                    <div className="border-t border-dashed border-slate-200 pt-2 mt-2 flex items-center justify-between">
                      <span className="text-xs text-slate-600 font-medium truncate max-w-[200px]">
                        Destino: <strong className="text-slate-800 font-bold">{folio.destination || folio.costCenterName || 'PUEBLA'}</strong>
                      </span>

                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        {!isClientUser(currentUser) && currentUser?.role !== 'CHOFER' && (
                          <button
                            onClick={() => startEditingFolio(folio)}
                            className="p-1.5 text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
                            title="Editar Folio"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        )}
                        {!isClientUser(currentUser) && currentUser?.role !== 'CHOFER' && onDeleteFolio && (
                          <button
                            onClick={() => handleDeleteClick(folio.id)}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all cursor-pointer"
                            title="Eliminar Folio"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                        {currentUser?.role !== 'CHOFER' && (
                          <button
                            onClick={() => { setSelectedFolio(folio); setIsCreating(false); }}
                            className="p-1.5 text-slate-500 hover:text-slate-950 hover:bg-slate-100 rounded-lg transition-all cursor-pointer font-bold"
                            title="Ver Detalles y Trazabilidad"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Driver & Operator Direct Phase Confirmation Action Buttons (Hidden for Client Consultation Users) */}
                    {!isClientUser(currentUser) && (
                      <>
                        {folio.status === 'RECOLECCION' && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateFolioStage(folio, 'ENTRADA_CEDIS', `Entrada al CEDIS confirmada por ${currentUser.name}`);
                            }}
                            className="w-full mt-2.5 bg-purple-600 hover:bg-purple-700 text-white font-extrabold text-xs py-2 px-3 rounded-xl shadow-xs flex items-center justify-center gap-1.5 transition-all cursor-pointer border border-purple-700 active:scale-[0.98]"
                          >
                            <Truck className="h-4 w-4" />
                            <span>Confirmar Entrada al CEDIS 🏭</span>
                          </button>
                        )}

                        {folio.status === 'ENTRADA_CEDIS' && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateFolioStage(folio, 'DECAPADO_TERMICO_QUIMICO', `Ingreso a proceso CEDIS confirmado por ${currentUser.name}`);
                            }}
                            className="w-full mt-2.5 bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-xs py-2 px-3 rounded-xl shadow-xs flex items-center justify-center gap-1.5 transition-all cursor-pointer border border-blue-700 active:scale-[0.98]"
                          >
                            <CheckCircle2 className="h-4 w-4" />
                            <span>Confirmar Entrada al CEDIS 🏭</span>
                          </button>
                        )}

                        {folio.status === 'SALIDA_CEDIS' && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateFolioStage(folio, 'ENTREGA_PLANTA', `Entrega en planta del cliente confirmada por ${currentUser.name}`);
                            }}
                            className="w-full mt-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs py-2 px-3 rounded-xl shadow-xs flex items-center justify-center gap-1.5 transition-all cursor-pointer border border-emerald-700 active:scale-[0.98]"
                          >
                            <CheckCircle2 className="h-4 w-4" />
                            <span>Confirmar Entrega en Planta 🏁</span>
                          </button>
                        )}

                        {folio.status === 'ENTREGA_PLANTA' && (
                          <div className="w-full mt-2 flex flex-col sm:flex-row gap-2">
                            <div className="flex-1 bg-emerald-50 text-emerald-800 font-extrabold text-[11px] py-1.5 px-2.5 rounded-xl border border-emerald-200 text-center flex items-center justify-center gap-1">
                              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                              <span>Entrega Confirmada 🏁</span>
                            </div>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenReturnModal(folio);
                              }}
                              className="bg-orange-600 hover:bg-orange-700 text-white font-extrabold text-[11px] py-1.5 px-3 rounded-xl shadow-xs flex items-center justify-center gap-1 transition-all cursor-pointer border border-orange-700 active:scale-[0.98] shrink-0"
                              title="Registrar devolución total o parcial por rechazo en planta"
                            >
                              <Undo className="h-3.5 w-3.5" />
                              <span>Devolución 📦</span>
                            </button>
                          </div>
                        )}

                        {folio.status === 'DEVOLUCION' && (
                          <div className="w-full mt-2 bg-red-50 text-red-800 font-extrabold text-[11px] py-1.5 px-3 rounded-xl border border-red-200 text-center flex items-center justify-center gap-1.5">
                            <Undo className="h-3.5 w-3.5 text-red-600" />
                            <span>Devolución Registrada en Planta 🚨</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8 text-xs text-slate-400 bg-slate-50 rounded-2xl border border-dashed border-slate-200 space-y-1">
                <p className="font-bold text-slate-600">No se encontraron folios en el historial.</p>
                <p className="text-[11px] text-slate-400">Intente cambiar los filtros o realizar una nueva búsqueda.</p>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* CENTER & RIGHT COLUMNS: Folio details, workflow and Maps */}
      {currentUser?.role !== 'CHOFER' && (
        <div className="lg:col-span-2 space-y-6" id="folio-display-details-panel">
        
                {selectedFolio ? (

          isEditingFolio ? (
            /* CASE: EDIT FORM COMPONENT */
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white p-6 rounded-2xl border border-gray-100 shadow-xs space-y-6"
              id="form-edit-reception-folio"
            >
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-amber-50 text-amber-600 rounded-lg animate-pulse">
                    <Edit className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">Editar Folio {selectedFolio.id}</h3>
                    <p className="text-xs text-gray-500">Actualiza la información general, destino o ítems del folio</p>
                  </div>
                </div>
                <button 
                  onClick={() => setIsEditingFolio(false)}
                  className="text-gray-400 hover:text-gray-600 text-sm font-semibold transition-all cursor-pointer"
                >
                  Cancelar
                </button>
              </div>

              <form onSubmit={handleEditFolioSubmit} className="space-y-4">
                
                {/* Context Client & Cost Center Summary Header */}
                <div className="bg-slate-50 border border-slate-100 p-3.5 rounded-xl text-xs space-y-1">
                  <span className="text-[9px] uppercase tracking-wider text-slate-400 font-bold block">Socio Comercial Registrado</span>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-extrabold text-slate-800 text-sm">
                      {selectedFolio.clientName}
                    </span>
                    <span className="text-[10px] bg-slate-200/80 text-slate-700 px-2 py-0.5 rounded-md font-bold font-mono">
                      {selectedFolio.costCenterName}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-500 font-medium">
                    📍 Planta Origen: <span className="font-semibold text-slate-600">{selectedFolio.origin || 'Dirección de Planta'}</span>
                  </p>
                </div>

                {/* Folio ID (Editable), Shipment Date & Delivery to Plant Date */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-gray-700">Número de Folio *</label>
                    <input
                      type="text"
                      value={editFolioId}
                      onChange={(e) => setEditFolioId(e.target.value)}
                      required
                      placeholder="Ej. F-102546"
                      className="w-full bg-white border border-gray-200 text-gray-900 rounded-xl px-3 py-2 text-sm focus:outline-hidden focus:border-blue-500 font-mono font-bold"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-gray-700">Fecha de Envío</label>
                    <input
                      type="date"
                      value={editShipmentDate}
                      onChange={(e) => setEditShipmentDate(e.target.value)}
                      required
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-hidden focus:border-blue-500"
                    />
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold text-emerald-800 flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5 text-emerald-600" />
                        <span>Fecha Entrega a Planta</span>
                      </label>
                      {editDeliveryDate && (
                        <button
                          type="button"
                          onClick={() => setEditDeliveryDate('')}
                          className="text-[10px] text-slate-400 hover:text-red-500 cursor-pointer underline"
                          title="Limpiar fecha de entrega"
                        >
                          Limpiar
                        </button>
                      )}
                    </div>
                    <input
                      type="datetime-local"
                      value={editDeliveryDate}
                      onChange={(e) => setEditDeliveryDate(e.target.value)}
                      className="w-full bg-emerald-50/50 border border-emerald-300 text-emerald-950 rounded-xl px-3 py-2 text-sm focus:outline-hidden focus:border-emerald-600 font-medium"
                      title="Cuándo se entregó a planta"
                    />
                    <p className="text-[10px] text-slate-500">
                      Fecha y hora real en que se entregó en planta cliente.
                    </p>
                  </div>
                </div>

                {/* Route Configuration: Origin & Destination */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-700 flex items-center gap-1 text-blue-700 font-bold">
                      <MapPin className="h-3.5 w-3.5 text-blue-600" /> Origen de Partida
                    </label>
                    <input
                      type="text"
                      value={editOrigin}
                      onChange={(e) => setEditOrigin(e.target.value)}
                      placeholder="Ej: CEDIS Escobedo NL..."
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-hidden focus:border-blue-500 font-medium"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-700 flex items-center gap-1 text-red-700 font-bold">
                      <Navigation className="h-3.5 w-3.5 text-red-600" /> Destino de Entrega
                    </label>
                    <input
                      type="text"
                      value={editDestination}
                      onChange={(e) => setEditDestination(e.target.value)}
                      required
                      placeholder="Escriba la dirección de entrega o CEDIS..."
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-hidden focus:border-blue-500 font-medium"
                    />
                  </div>
                </div>

                {/* Driver Name & Economic Number (Edit Mode) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-gray-700">Nombre del Chofer</label>
                    <input
                      type="text"
                      value={editDriverName}
                      onChange={(e) => setEditDriverName(e.target.value)}
                      placeholder="Nombre completo del chofer"
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-hidden focus:border-blue-500"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-gray-700">N° Económico de Unidad</label>
                    <input
                      type="text"
                      value={editEconomicNumber}
                      onChange={(e) => setEditEconomicNumber(e.target.value)}
                      placeholder="Ej. ECO-142"
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-hidden focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Tipo de Proceso de Decapado */}
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/90 space-y-3">
                  <div className="flex items-center justify-between border-b border-slate-200/80 pb-2">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-amber-100 text-amber-800 rounded-lg">
                        <Flame className="h-4 w-4" />
                      </div>
                      <div>
                        <h5 className="text-xs font-bold text-slate-900 uppercase tracking-wide">Tipo de Proceso de Decapado *</h5>
                        <p className="text-[10px] text-slate-500">Define el flujo de tratamiento asignado a este folio</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                    {/* Opción 1: DECAPADO TÉRMICO */}
                    <button
                      type="button"
                      onClick={() => {
                        setEditProcessType('DECAPADO_TERMICO');
                        setEditHasGranallado(false);
                      }}
                      className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer space-y-1.5 ${
                        editProcessType === 'DECAPADO_TERMICO'
                          ? 'bg-amber-500/10 border-amber-500 ring-2 ring-amber-400/20 shadow-2xs'
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <Flame className={`h-3.5 w-3.5 ${editProcessType === 'DECAPADO_TERMICO' ? 'text-amber-600' : 'text-slate-400'}`} />
                          <span className="font-extrabold text-[11px] text-slate-900">DECAPADO TÉRMICO</span>
                        </div>
                        <input
                          type="radio"
                          name="editProcessTypeRadio"
                          checked={editProcessType === 'DECAPADO_TERMICO'}
                          onChange={() => {}}
                          className="accent-amber-600 h-3.5 w-3.5 cursor-pointer"
                        />
                      </div>
                      <p className="text-[9px] text-slate-500 leading-tight">
                        Horno térmico. Fluye al proceso de <strong>LAVADO</strong> antes de inspección.
                      </p>
                      <span className="inline-block text-[8.5px] font-bold bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded font-mono">
                        ➔ Lavado ➔ Inspección
                      </span>
                    </button>

                    {/* Opción 2: DECAPADO QUÍMICO */}
                    <button
                      type="button"
                      onClick={() => {
                        setEditProcessType('DECAPADO_QUIMICO');
                        setEditHasGranallado(false);
                      }}
                      className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer space-y-1.5 ${
                        editProcessType === 'DECAPADO_QUIMICO'
                          ? 'bg-blue-500/10 border-blue-500 ring-2 ring-blue-400/20 shadow-2xs'
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <FlaskConical className={`h-3.5 w-3.5 ${editProcessType === 'DECAPADO_QUIMICO' ? 'text-blue-600' : 'text-slate-400'}`} />
                          <span className="font-extrabold text-[11px] text-slate-900">DECAPADO QUÍMICO</span>
                        </div>
                        <input
                          type="radio"
                          name="editProcessTypeRadio"
                          checked={editProcessType === 'DECAPADO_QUIMICO'}
                          onChange={() => {}}
                          className="accent-blue-600 h-3.5 w-3.5 cursor-pointer"
                        />
                      </div>
                      <p className="text-[9px] text-slate-500 leading-tight">
                        Tina química. Fluye al proceso de <strong>LAVADO</strong> antes de inspección.
                      </p>
                      <span className="inline-block text-[8.5px] font-bold bg-blue-100 text-blue-900 px-1.5 py-0.5 rounded font-mono">
                        ➔ Lavado ➔ Inspección
                      </span>
                    </button>

                    {/* Opción 3: DECAPADO POR GRANALLADORA */}
                    <button
                      type="button"
                      onClick={() => {
                        setEditProcessType('DECAPADO_GRANALLADORA');
                        setEditHasGranallado(true);
                      }}
                      className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer space-y-1.5 ${
                        editProcessType === 'DECAPADO_GRANALLADORA'
                          ? 'bg-purple-500/10 border-purple-500 ring-2 ring-purple-400/20 shadow-2xs'
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <Sparkles className={`h-3.5 w-3.5 ${editProcessType === 'DECAPADO_GRANALLADORA' ? 'text-purple-600' : 'text-slate-400'}`} />
                          <span className="font-extrabold text-[11px] text-slate-900">GRANALLADORA</span>
                        </div>
                        <input
                          type="radio"
                          name="editProcessTypeRadio"
                          checked={editProcessType === 'DECAPADO_GRANALLADORA'}
                          onChange={() => {}}
                          className="accent-purple-600 h-3.5 w-3.5 cursor-pointer"
                        />
                      </div>
                      <p className="text-[9px] text-slate-500 leading-tight">
                        Abrasivo mecánico. Pasa <strong>DIRECTO A INSPECCIÓN</strong> (omite lavado).
                      </p>
                      <span className="inline-block text-[8.5px] font-bold bg-purple-100 text-purple-900 px-1.5 py-0.5 rounded font-mono">
                        ➔ Directo a Inspección
                      </span>
                    </button>
                  </div>
                </div>

                {/* Folio Evidence Photo */}
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-3">
                  <div className="space-y-0.5">
                    <h5 className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                      <Camera className="h-4 w-4 text-slate-600" />
                      <span>FOTOGRAFÍA DE EVIDENCIA DE RECOLECCIÓN</span>
                    </h5>
                    <p className="text-[10px] text-slate-500">Suba o tome una foto del material completo recolectado como prueba física oficial.</p>
                  </div>

                  {editPhotoUrl ? (
                    <div className="relative rounded-xl overflow-hidden border border-slate-200 aspect-video max-w-xs mx-auto group bg-slate-900 shadow-tiny">
                      <img
                        src={editPhotoUrl}
                        alt="Evidencia del Folio"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => setEditPhotoUrl('')}
                        className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white p-1.5 rounded-full shadow-lg transition-all"
                        title="Quitar foto"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2 justify-center max-w-sm mx-auto">
                      <button
                        type="button"
                        onClick={() => {
                          setActivePhotoIndex(-1);
                          startWebcam();
                        }}
                        className="flex-1 bg-white hover:bg-slate-50 text-slate-700 font-bold py-2 px-3 border border-slate-200 rounded-xl text-xs transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                      >
                        <Camera className="h-4 w-4 text-blue-600" /> Tomar Foto
                      </button>
                      <label className="flex-1 bg-white hover:bg-slate-50 text-slate-700 font-bold py-2 px-3 border border-slate-200 rounded-xl text-xs transition-all flex items-center justify-center gap-1.5 cursor-pointer">
                        <Upload className="h-4 w-4 text-blue-600" /> Subir Archivo
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileUpload(e, -1)}
                        />
                      </label>
                    </div>
                  )}
                </div>

                {/* Goods multiple picker & layout */}
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-4">
                  <div className="flex items-center justify-between border-b border-slate-200/60 pb-2">
                    <label className="text-xs font-bold text-slate-800 flex items-center gap-1">
                      <span>📦 EDITAR ÍTEMS DEL FOLIO</span>
                    </label>
                    <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">
                      {editItems.length} producto(s) en lista
                    </span>
                  </div>

                  {/* Insertion controls */}
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end bg-white p-3 rounded-xl border border-slate-100">
                    <div className="md:col-span-8 space-y-1">
                      <label className="text-[10px] font-bold text-slate-500 uppercase">Catálogo del Cliente</label>
                      <select
                        value={selectedItemToAdd}
                        onChange={(e) => setSelectedItemToAdd(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-hidden focus:border-blue-500 disabled:opacity-50"
                      >
                        <option value="">Seleccione un producto...</option>
                        {goods.filter(g => !g.clientId || g.clientId === selectedFolio?.clientId).map(g => (
                          <option key={g.id} value={g.id}>{g.description} ({g.code})</option>
                        ))}
                      </select>
                    </div>

                    <div className="md:col-span-2 space-y-1">
                      <label className="text-[10px] font-bold text-slate-500 uppercase">Cantidad</label>
                      <input
                        type="number"
                        min="1"
                        value={quantityToAdd}
                        onChange={(e) => setQuantityToAdd(Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-center focus:outline-hidden focus:border-blue-500 font-bold"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <button
                        type="button"
                        onClick={() => {
                          if (!selectedItemToAdd) return;
                          const exists = editItems.find(item => item.goodsItemId === selectedItemToAdd);
                          if (exists) {
                            setEditItems(prev => prev.map(item => {
                              if (item.goodsItemId === selectedItemToAdd) {
                                return { ...item, quantity: item.quantity + quantityToAdd };
                              }
                              return item;
                            }));
                          } else {
                            setEditItems([...editItems, { goodsItemId: selectedItemToAdd, quantity: quantityToAdd }]);
                          }
                          setSelectedItemToAdd('');
                          setQuantityToAdd(1);
                        }}
                        disabled={!selectedItemToAdd}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 rounded-lg text-xs transition-all flex items-center justify-center gap-1 cursor-pointer disabled:opacity-50"
                      >
                        <Plus className="h-3.5 w-3.5" /> Agregar
                      </button>
                    </div>
                  </div>

                  {/* Previsualización of items loaded */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">
                      🔍 Vista Previa de Ítems Cargados
                    </label>

                    {editItems.length === 0 ? (
                      <div className="border border-dashed border-slate-200 rounded-xl p-6 text-center text-xs text-slate-400 bg-white">
                        Ningún ítem seleccionado. Seleccione un producto arriba y haga clic en Agregar.
                      </div>
                    ) : (
                      <div className="bg-white border border-slate-200/80 rounded-xl divide-y divide-slate-100 overflow-hidden">
                        {editItems.map((item, idx) => {
                          const gItem = goods.find(g => g.id === item.goodsItemId);
                          if (!gItem) return null;

                          return (
                            <div key={idx} className="p-3 text-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white hover:bg-slate-50/50 transition-colors">
                              <div className="space-y-0.5 flex-1 min-w-0">
                                <p className="font-mono text-[9px] text-slate-400 font-bold uppercase">{gItem.code} (ID: {gItem.id})</p>
                                <p className="font-bold text-slate-900 truncate">{gItem.description}</p>
                                <p className="text-[10px] text-slate-500">Unidad: {gItem.unit || 'PZA'}</p>
                              </div>

                              <div className="flex items-center gap-3 shrink-0">
                                <div className="text-center bg-slate-50 border border-slate-100 rounded-md px-2 py-0.5 min-w-[50px]">
                                  <span className="text-[8px] text-slate-400 font-bold block uppercase leading-none">Cant</span>
                                  <span className="font-black text-slate-800 text-xs">{item.quantity}</span>
                                </div>

                                {/* Photo options */}
                                <div className="flex items-center gap-2">
                                  {item.photoUrl ? (
                                    <div className="relative group">
                                      <img
                                        src={item.photoUrl}
                                        alt="Carga"
                                        onClick={() => setSelectedPhotoView(item.photoUrl || null)}
                                        className="h-10 w-10 object-cover rounded-lg border border-slate-200 cursor-zoom-in hover:brightness-90 transition-all"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setEditItems(prev => prev.map((it, i) => {
                                            if (i === idx) {
                                              const { photoUrl, ...rest } = it;
                                              return rest;
                                            }
                                            return it;
                                          }));
                                        }}
                                        className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full p-0.5 shadow-xs hover:bg-red-700 transition-colors"
                                        title="Quitar foto"
                                      >
                                        <span className="text-[8px] font-bold block leading-none w-3.5 h-3.5 text-center">✕</span>
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="flex gap-1">
                                      {/* Camera trigger */}
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setActivePhotoIndex(idx);
                                          startWebcam();
                                        }}
                                        className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 rounded-lg text-[10px] font-bold transition-all cursor-pointer flex items-center gap-1"
                                      >
                                        <Camera className="h-3 w-3" /> Foto
                                      </button>

                                      {/* File upload trigger */}
                                      <label className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 rounded-lg text-[10px] font-bold transition-all cursor-pointer flex items-center gap-1">
                                        <Upload className="h-3 w-3" /> Subir
                                        <input
                                          type="file"
                                          accept="image/*"
                                          className="hidden"
                                          onChange={(e) => handleFileUpload(e, idx)}
                                        />
                                      </label>
                                    </div>
                                  )}
                                </div>

                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditItems(editItems.filter((_, i) => i !== idx));
                                  }}
                                  className="text-red-500 hover:text-red-700 p-1.5 rounded-full hover:bg-red-50 transition-colors"
                                  title="Quitar item"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                        {/* Summary Bar for Edit Items */}
                        {(() => {
                          const totalEditPieces = editItems.reduce((acc, it) => acc + (Number(it.quantity) || 0), 0);
                          const totalEditAmount = editItems.reduce((acc, it) => {
                            const gItem = goods.find(g => g.id === it.goodsItemId);
                            const numPrice = Number((it as any).customPrice ?? gItem?.priceMXN ?? 0) || 0;
                            return acc + (numPrice * (Number(it.quantity) || 0));
                          }, 0);

                          return (
                            <div className="p-3 bg-slate-100/90 border-t-2 border-slate-200 text-xs flex flex-col sm:flex-row sm:items-center justify-between gap-2 font-bold text-slate-800">
                              <span className="text-slate-900 font-black">
                                TOTALES ({editItems.length} {editItems.length === 1 ? 'partida' : 'partidas'})
                              </span>
                              <div className="flex items-center gap-3">
                                <span className="bg-blue-100 text-blue-900 border border-blue-300 px-2.5 py-0.5 rounded-md font-black font-mono">
                                  Total Piezas: {totalEditPieces.toLocaleString()}
                                </span>
                                {currentUser?.role !== 'CHOFER' && (
                                  <span className="font-mono font-black text-slate-900 text-xs sm:text-sm">
                                    Total: ${totalEditAmount.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-gray-700">Notas de Recepción / Instrucciones Especiales</label>
                  <textarea
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    placeholder="Escriba aquí instrucciones..."
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-blue-500 h-20"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-4 rounded-xl text-sm transition-all shadow-xs flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    Guardar Cambios
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsEditingFolio(false)}
                    className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-2.5 px-4 rounded-xl text-sm transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    Cancelar
                  </button>
                </div>

              </form>
            </motion.div>
          ) : (
            // MAIN ACTIVE FOLIO WORKFLOW DISPLAY
            <div className="space-y-6">
              
              {/* Upper Info Row */}
              <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-xs space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-100 pb-3">
                  <div className="space-y-1">
                    <span className="text-xs font-mono font-bold text-gray-400 uppercase tracking-wider">Trazabilidad de Logística Activa</span>
                    <h3 className="text-xl font-black text-gray-900 tracking-tight flex items-center gap-2">
                      {selectedFolio.id}
                      <span className="text-xs bg-slate-100 text-slate-700 font-semibold px-2 py-0.5 rounded-full border border-slate-200">
                        QR ACTIVO
                      </span>
                      {!isOnline && (
                        <span className="text-xs bg-amber-50 text-amber-600 font-bold px-2 py-0.5 rounded-full border border-amber-200/60 animate-pulse flex items-center gap-1" title="Esta información proviene del almacenamiento local y puede no estar sincronizada con el servidor">
                          OFFLINE
                        </span>
                      )}
                      {!isFolioIntegratedToPo(selectedFolio) ? (
                        <span className="text-xs bg-amber-100 text-amber-900 font-black px-2.5 py-0.5 rounded-full border border-amber-300 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-600"></span>
                          NO INTEGRADO A PO
                        </span>
                      ) : (
                        <span className="text-xs bg-purple-100 text-purple-900 font-bold px-2.5 py-0.5 rounded-full border border-purple-300 flex items-center gap-1 font-mono">
                          <Link2 className="h-3 w-3 text-purple-700" />
                          PO: {selectedFolio.poNumber}
                        </span>
                      )}
                    </h3>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                    {!isClientUser(currentUser) && (
                      <button
                        onClick={() => handleOpenIntegratePoModal(selectedFolio)}
                        className={`p-2 rounded-xl border transition-all flex items-center gap-1.5 text-xs font-bold cursor-pointer ${
                          isFolioIntegratedToPo(selectedFolio)
                            ? 'text-purple-700 bg-purple-50 border-purple-200 hover:bg-purple-100'
                            : 'text-amber-900 bg-amber-200/80 border-amber-400 hover:bg-amber-300 shadow-2xs'
                        }`}
                        title={isFolioIntegratedToPo(selectedFolio) ? 'Modificar PO / OC Asignada' : 'Integrar Folio a una Orden de Compra'}
                      >
                        <Link2 className="h-4 w-4" />
                        {isFolioIntegratedToPo(selectedFolio) ? 'Cambiar PO / OC' : 'Integrar a PO'}
                      </button>
                    )}
                    <button
                      onClick={handlePrintLabel}
                      className="p-2 text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all flex items-center gap-1.5 text-xs font-semibold cursor-pointer"
                    >
                      <Printer className="h-4 w-4" /> Imprimir Etiqueta
                    </button>
                    <button
                      onClick={async () => {
                        navigator.clipboard.writeText(selectedFolio.id);
                        await window.showAppAlert('Código de folio QR copiado al portapapeles', 'Portapapeles');
                      }}
                      className="p-2 text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all flex items-center gap-1.5 text-xs font-semibold cursor-pointer"
                    >
                      <Copy className="h-4 w-4" /> Copiar Código
                    </button>

                    {!isClientUser(currentUser) && (
                      <>
                        <button
                          onClick={() => startEditingFolio(selectedFolio)}
                          className="p-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-xl hover:bg-amber-100 hover:border-amber-300 transition-all flex items-center gap-1.5 text-xs font-semibold cursor-pointer"
                        >
                          <Edit className="h-4 w-4" /> Editar Folio
                        </button>

                        {onDeleteFolio && (
                          <button
                            onClick={async () => {
                              const confirmed = await window.showAppConfirm(`¿Está seguro de que desea eliminar permanentemente el Folio ${selectedFolio.id}? Esta acción no se puede deshacer.`, 'Confirmar Eliminación');
                              if (confirmed) {
                                onDeleteFolio(selectedFolio.id);
                                setSelectedFolio(folios.filter(f => f.id !== selectedFolio.id)[0] || null);
                              }
                            }}
                            className="p-2 text-red-700 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 hover:border-red-300 transition-all flex items-center gap-1.5 text-xs font-semibold cursor-pointer"
                          >
                            <Trash2 className="h-4 w-4" /> Eliminar
                          </button>
                        )}
                      </>
                    )}
                  </div>
              </div>

              {/* Logistics grid info */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-xs border-b border-gray-100 pb-3">
                <div className="space-y-2">
                  <div>
                    <span className="text-gray-400 block uppercase font-bold text-[9px] tracking-wider">Cliente Razón Social</span>
                    <p className="font-semibold text-gray-900">{selectedFolio.clientName}</p>
                  </div>
                  <div>
                    <span className="text-gray-400 block uppercase font-bold text-[9px] tracking-wider">Centro de Costos</span>
                    <p className="font-semibold text-gray-800">{getCostCenterNameForFolio(selectedFolio)}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div>
                    <span className="text-gray-400 block uppercase font-bold text-[9px] tracking-wider">Fecha Creación</span>
                    <p className="font-semibold text-gray-800">{new Date(selectedFolio.createdDate).toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-gray-400 block uppercase font-bold text-[9px] tracking-wider">Operador Creador</span>
                    <p className="font-semibold text-gray-800">{selectedFolio.createdBy}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div>
                    <span className="text-gray-400 block uppercase font-bold text-[9px] tracking-wider">Fecha de Envío</span>
                    <p className="font-semibold text-blue-700 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-md w-fit">{selectedFolio.shipmentDate || 'Inmediato'}</p>
                  </div>
                  <div>
                    <span className="text-gray-400 block uppercase font-bold text-[9px] tracking-wider">Entrega a Planta</span>
                    <p className="font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-md w-fit text-xs">
                      {selectedFolio.deliveryDate 
                        ? (selectedFolio.deliveryDate.includes('T') ? selectedFolio.deliveryDate.replace('T', ' ') : selectedFolio.deliveryDate)
                        : (selectedFolio.history?.find(h => h.stage === 'ENTREGA_PLANTA' || h.stage === 'ENTREGA')?.timestamp 
                            ? selectedFolio.history?.find(h => h.stage === 'ENTREGA_PLANTA' || h.stage === 'ENTREGA')!.timestamp.slice(0, 16).replace('T', ' ')
                            : 'Pendiente')}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-400 block uppercase font-bold text-[9px] tracking-wider">Destino de Entrega</span>
                    <p className="font-semibold text-gray-800 truncate" title={selectedFolio.destination || getCostCenterNameForFolio(selectedFolio)}>{selectedFolio.destination || 'CEDIS'}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div>
                    <span className="text-gray-400 block uppercase font-bold text-[9px] tracking-wider">Chofer Asignado</span>
                    <p className="font-semibold text-gray-800">{selectedFolio.driverName || 'No asignado'}</p>
                  </div>
                  <div>
                    <span className="text-gray-400 block uppercase font-bold text-[9px] tracking-wider">N° Económico de Unidad</span>
                    <p className="font-semibold text-gray-800 font-mono">{selectedFolio.economicNumber || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Process Type Badge Banner */}
              <div className="bg-slate-50 border border-slate-200/80 p-3 rounded-xl flex flex-wrap items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-2.5">
                  <div className={`p-2 rounded-lg ${
                    selectedFolio.processType === 'DECAPADO_QUIMICO'
                      ? 'bg-blue-100 text-blue-800'
                      : (selectedFolio.processType === 'DECAPADO_GRANALLADORA' || selectedFolio.hasGranallado)
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-amber-100 text-amber-800'
                  }`}>
                    {selectedFolio.processType === 'DECAPADO_QUIMICO' ? (
                      <FlaskConical className="h-4 w-4" />
                    ) : (selectedFolio.processType === 'DECAPADO_GRANALLADORA' || selectedFolio.hasGranallado) ? (
                      <Sparkles className="h-4 w-4" />
                    ) : (
                      <Flame className="h-4 w-4" />
                    )}
                  </div>
                  <div>
                    <span className="text-[9px] uppercase font-black tracking-wider text-slate-400 block">Proceso Asignado de Tratamiento</span>
                    <span className="font-extrabold text-slate-900 text-xs">
                      {selectedFolio.processType === 'DECAPADO_QUIMICO'
                        ? 'DECAPADO QUÍMICO'
                        : (selectedFolio.processType === 'DECAPADO_GRANALLADORA' || selectedFolio.hasGranallado)
                          ? 'DECAPADO POR GRANALLADORA'
                          : 'DECAPADO TÉRMICO'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-bold text-slate-500">Flujo:</span>
                  <span className={`px-2.5 py-1 rounded-md text-[10px] font-bold font-mono border ${
                    (selectedFolio.processType === 'DECAPADO_GRANALLADORA' || selectedFolio.hasGranallado)
                      ? 'bg-purple-50 text-purple-900 border-purple-200'
                      : selectedFolio.processType === 'DECAPADO_QUIMICO'
                        ? 'bg-blue-50 text-blue-900 border-blue-200'
                        : 'bg-amber-50 text-amber-900 border-amber-200'
                  }`}>
                    {(selectedFolio.processType === 'DECAPADO_GRANALLADORA' || selectedFolio.hasGranallado)
                      ? 'Decapado ➔ Inspección (Directo)'
                      : 'Decapado ➔ Lavado ➔ Inspección'}
                  </span>
                </div>
              </div>

              {/* PO / Orden de Compra Linked Info Banner */}
              {isFolioIntegratedToPo(selectedFolio) ? (
                <div className="bg-gradient-to-r from-purple-900/10 via-indigo-900/10 to-purple-900/10 border border-purple-200/90 p-3 rounded-xl text-xs flex flex-wrap items-center justify-between gap-2 shadow-2xs">
                  <div className="flex items-center gap-2.5">
                    <div className="p-2 bg-purple-600 text-white rounded-lg shrink-0">
                      <Receipt className="h-4 w-4" />
                    </div>
                    <div>
                      <span className="text-purple-900 text-[9px] uppercase font-black tracking-wider block font-mono">
                        📋 Orden de Compra (PO / OC) Asignada:
                      </span>
                      <span className="font-black text-purple-950 font-mono text-sm">
                        {selectedFolio.poNumber}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="bg-purple-100 text-purple-900 border border-purple-300 font-mono text-[10px] font-black px-3 py-1 rounded-lg">
                      ✓ PO VINCULADA
                    </span>
                    {!isClientUser(currentUser) && (
                      <button
                        onClick={() => handleOpenIntegratePoModal(selectedFolio)}
                        className="px-2.5 py-1 bg-white hover:bg-purple-50 text-purple-700 border border-purple-200 rounded-lg font-bold text-[11px] transition-all cursor-pointer"
                      >
                        Modificar
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-gradient-to-r from-amber-500/10 via-amber-400/10 to-amber-500/10 border border-amber-300 p-3.5 rounded-xl text-xs flex flex-wrap items-center justify-between gap-3 shadow-2xs">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-500 text-slate-950 rounded-lg shrink-0 font-bold">
                      <AlertCircle className="h-4 w-4" />
                    </div>
                    <div>
                      <span className="text-amber-900 text-[10px] uppercase font-black tracking-wider block font-mono">
                        ⚠️ Estado de Vinculación a Orden de Compra:
                      </span>
                      <span className="font-extrabold text-amber-950 text-xs">
                        Este folio no está integrado a ninguna Orden de Compra (PO).
                      </span>
                      <p className="text-[10px] text-amber-800 font-medium">
                        El estado del pedido permanecerá como "No integrado a PO" hasta que se asigne una Orden de Compra.
                      </p>
                    </div>
                  </div>
                  {!isClientUser(currentUser) && (
                    <button
                      onClick={() => handleOpenIntegratePoModal(selectedFolio)}
                      className="px-3.5 py-2 bg-amber-500 hover:bg-amber-600 text-slate-950 font-black rounded-xl text-xs transition-all shadow-xs flex items-center gap-1.5 cursor-pointer shrink-0"
                    >
                      <Link2 className="h-4 w-4" />
                      Integrar a Orden de Compra
                    </button>
                  )}
                </div>
              )}

              {/* Origin banner */}
              <div className="bg-slate-50 border border-slate-200/60 p-2.5 rounded-xl text-xs flex flex-col gap-1 md:flex-row md:items-center justify-between">
                <div>
                  <span className="text-slate-400 text-[9px] uppercase font-bold tracking-wider leading-none">Ruta de Despacho Entendida</span>
                  <p className="text-[11px] text-slate-700 font-medium">
                    📍 <span className="font-semibold text-slate-800">Origen:</span> {selectedFolio.origin || 'Planta Principal Registrada'} 
                    <span className="text-slate-400 mx-2">➔</span> 
                    🏁 <span className="font-semibold text-slate-800">Destino:</span> {selectedFolio.destination || 'CEDIS Destino'}
                  </p>
                </div>
              </div>

              {/* Items Table with Photo Column */}
              <div className="border border-gray-100 rounded-xl overflow-hidden mt-2">
                <div className="bg-slate-100/80 px-3.5 py-2 text-[10px] font-extrabold text-slate-600 uppercase tracking-wider grid grid-cols-12 items-center border-b border-slate-200">
                  <span className={shouldHidePricesForUser(currentUser as any) ? "col-span-7" : "col-span-5"}>Mercancía y SKU</span>
                  <span className="col-span-2 text-center">Evidencia</span>
                  <span className={shouldHidePricesForUser(currentUser as any) ? "col-span-3 text-right" : "col-span-2 text-center"}>Cantidad</span>
                  {!shouldHidePricesForUser(currentUser as any) && (
                    <span className="col-span-3 text-right">
                      {currentUser?.role === 'CHOFER' ? 'Monto' : 'Total MXN'}
                    </span>
                  )}
                </div>
                <div className="divide-y divide-gray-100 bg-white">
                  {selectedFolio.items.map((item, idx) => (
                    <div key={idx} className="px-3.5 py-2.5 text-xs grid grid-cols-12 items-center gap-2 hover:bg-slate-50/50 transition-colors">
                      <div className={`${shouldHidePricesForUser(currentUser as any) ? "col-span-7" : "col-span-5"} min-w-0 pr-1`}>
                        <p className="font-bold text-slate-900 truncate text-xs" title={item.description}>{item.description}</p>
                        {item.goodsItemId && (
                          <p className="text-[10px] text-slate-400 font-mono font-medium truncate">{item.goodsItemId}</p>
                        )}
                      </div>
                      
                      <div className="col-span-2 flex justify-center">
                        {item.photoUrl ? (
                          <img
                            src={item.photoUrl}
                            alt="Evidencia"
                            onClick={() => setSelectedPhotoView(item.photoUrl || null)}
                            className="h-8 w-8 object-cover rounded-md border border-gray-200 hover:scale-105 hover:brightness-90 transition-all cursor-zoom-in shrink-0"
                          />
                        ) : (
                          <span className="text-[10px] text-gray-400 italic">Sin foto</span>
                        )}
                      </div>

                      <div className={`${shouldHidePricesForUser(currentUser as any) ? "col-span-3 flex justify-end" : "col-span-2 flex justify-center"}`}>
                        <span className="text-slate-800 font-bold bg-slate-100 px-2 py-0.5 rounded text-[11px] font-mono whitespace-nowrap">
                          {item.quantity} pza{item.quantity > 1 ? 's' : ''}
                        </span>
                      </div>
                      
                      {!shouldHidePricesForUser(currentUser as any) && (
                        <div className="col-span-3 text-right min-w-0">
                          {currentUser?.role === 'CHOFER' ? (
                            <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded border border-slate-200 inline-block">
                              🔒 Restringido
                            </span>
                          ) : (
                            <div className="truncate">
                              <p className="font-extrabold text-slate-900 text-xs">${(parseFloat(item.totalMXN.toString()) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                              <p className="text-[10px] text-slate-400 font-medium">${(parseFloat(item.totalUSD.toString()) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} USD</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="bg-slate-50 px-3.5 py-2 text-xs border-t border-slate-200 flex justify-between items-center font-bold text-slate-800">
                  <span className="text-[11px] text-slate-500 font-medium">Partidas registradas: {selectedFolio.items.length}</span>
                  <span className="font-mono bg-white px-2 py-0.5 rounded border border-slate-200 text-red-700 font-black text-xs">
                    {selectedFolio.items.reduce((s, it) => s + (Number(it.quantity) || 0), 0)} PZAS TOTALES
                  </span>
                </div>
              </div>

              {/* SECCIÓN DE SEGUIMIENTO Y REMISIÓN (QR + PDF) */}
              <div className="bg-gradient-to-br from-slate-900 to-slate-950 text-white p-5 rounded-2xl border border-slate-800 shadow-md space-y-4">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-cyan-950 text-cyan-400 rounded-lg border border-cyan-800/40">
                    <QrCode className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold tracking-tight">Seguimiento Directo del Cliente</h4>
                    <p className="text-[9px] text-slate-400 font-mono">Canal Certificado (Sin sesión activa necesaria)</p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-4 bg-slate-900/60 p-4 rounded-xl border border-slate-800/60">
                  {/* QR Box */}
                  <div className="bg-white p-2.5 rounded-lg shrink-0 flex flex-col items-center justify-center border border-slate-250">
                    <img 
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(`${window.location.origin}${window.location.pathname}?track=${selectedFolio.id}`)}`}
                      alt="Código QR de Seguimiento"
                      className="w-24 h-24 sm:w-28 sm:h-28"
                    />
                    <span className="text-[9px] text-slate-600 font-extrabold tracking-wider mt-1.5 font-mono">FOLIO: {selectedFolio.id}</span>
                  </div>

                  <div className="space-y-3 flex-1 text-center sm:text-left">
                    <p className="text-xs text-slate-300 leading-relaxed">
                      Este código QR enlaza directamente al <span className="text-cyan-400 font-semibold">canal de trazabilidad pública</span> del folio. El cliente puede consultar el estado de su carga en tiempo real sin iniciar sesión.
                    </p>
                    
                    <div className="flex flex-wrap gap-2 justify-center sm:justify-start pt-1">
                      <button
                        onClick={() => downloadFolioPDF(selectedFolio)}
                        className="bg-red-600 hover:bg-red-500 text-white text-xs font-bold px-3.5 py-2 rounded-xl transition-all flex items-center gap-1.5 cursor-pointer shadow-md shadow-red-600/10"
                      >
                        <FileDown className="h-4 w-4" /> Descargar PDF Remisión
                      </button>

                      <a
                        href={`${window.location.origin}${window.location.pathname}?track=${selectedFolio.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-750 text-xs font-semibold px-3.5 py-2 rounded-xl transition-all flex items-center gap-1.5 cursor-pointer"
                      >
                        <ExternalLink className="h-4 w-4 text-cyan-400" /> Abrir Enlace de Consulta
                      </a>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {/* VISUAL STEP STATE MACHINE */}
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-xs space-y-4">
              <h4 className="text-sm font-bold text-gray-900">Estatus de Trazabilidad y Workflow Logístico</h4>
              
              <div className="relative">
                {/* Visual connecting line */}
                <div className="absolute left-4 top-1.5 bottom-1.5 w-0.5 bg-gray-100"></div>

                <div className="space-y-4">
                  {STAGE_ORDER.map((stage) => {
                    // Check if we skip lavado
                    if (stage === 'DECAPADO_LAVADO' && selectedFolio.hasGranallado) {
                      return null;
                    }

                    const isCurrent = selectedFolio.status === stage;
                    const isCompleted = selectedFolio.history.some(h => h.stage === stage);
                    const historyEntry = selectedFolio.history.find(h => h.stage === stage);
                    const stageInfo = STAGE_LABELS[stage];

                    return (
                      <div key={stage} className="flex gap-4 items-start relative pl-1">
                        
                        {/* Bullet circle */}
                        <div className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 border z-10 ${
                          isCurrent 
                            ? 'bg-blue-600 border-blue-600 text-white shadow-xs' 
                            : isCompleted 
                              ? 'bg-emerald-100 border-emerald-300 text-emerald-700' 
                              : 'bg-white border-gray-200 text-gray-400'
                        }`}>
                          {isCompleted && !isCurrent ? (
                            <CheckCircle className="h-4 w-4" />
                          ) : (
                            <span className="text-xs font-bold font-mono">
                              {STAGE_ORDER.indexOf(stage) + 1}
                            </span>
                          )}
                        </div>

                        {/* Stage details */}
                        <div className="flex-1 space-y-1 bg-gray-50/60 p-3 rounded-xl border border-gray-100/80">
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-xs text-gray-900">{stageInfo.label}</span>
                            {historyEntry && (
                              <span className="text-[9px] text-gray-400 font-medium">
                                {new Date(historyEntry.timestamp).toLocaleTimeString()}
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-gray-500">{stageInfo.desc}</p>
                          
                          {/* Duration Display if completed */}
                          {historyEntry?.durationMs && (
                            <div className="flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-sm w-fit font-medium">
                              <Clock className="h-3 w-3" />
                              <span>Completado en {(historyEntry.durationMs / 60000).toFixed(1)} mins</span>
                            </div>
                          )}

                          {/* Operator note */}
                          {historyEntry?.notes && (
                            <p className="text-[10px] text-gray-500 italic bg-white border border-gray-100 p-1.5 rounded-md mt-1">
                              "{historyEntry.notes}"
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Optional Rework Display inside steps */}
                  {selectedFolio.status === 'RETRABAJO' && (
                    <div className="flex gap-4 items-start relative pl-1">
                      <div className="h-7 w-7 rounded-full bg-red-600 border-red-600 text-white flex items-center justify-center shrink-0 z-10 shadow-xs">
                        <AlertTriangle className="h-4 w-4" />
                      </div>
                      <div className="flex-1 space-y-1 bg-red-50 p-3 rounded-xl border border-red-100">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-xs text-red-900">RETRABAJO EN CURSO</span>
                          <span className="text-[9px] text-red-400">FASE ACTIVA</span>
                        </div>
                        <p className="text-[11px] text-red-700">La pieza ha fallado la inspección y está en retrabajo mecánico residual.</p>
                        {selectedFolio.history.find(h => h.stage === 'RETRABAJO')?.incidenceReport && (
                          <div className="bg-white border border-red-100 p-2 rounded-lg text-xs text-gray-700 space-y-1">
                            <p className="font-bold text-red-800 text-[10px] uppercase">Reporte de Incidencia:</p>
                            <p className="italic">"{selectedFolio.history.find(h => h.stage === 'RETRABAJO')?.incidenceReport}"</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Optional Devolucion Display inside steps */}
                  {selectedFolio.status === 'DEVOLUCION' && (
                    <div className="flex gap-4 items-start relative pl-1">
                      <div className="h-7 w-7 rounded-full bg-orange-600 border-orange-600 text-white flex items-center justify-center shrink-0 z-10 shadow-xs">
                        <Undo className="h-4 w-4" />
                      </div>
                      <div className="flex-1 space-y-1 bg-orange-50 p-3 rounded-xl border border-orange-100">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-xs text-orange-900">DEVOLUCIÓN REGISTRADA</span>
                        </div>
                        <p className="text-[11px] text-orange-700">Se reportó inconformidad por el cliente durante la entrega.</p>
                        {selectedFolio.history.find(h => h.stage === 'DEVOLUCION')?.returnDetails && (
                          <div className="bg-white border border-orange-100 p-2 rounded-lg text-xs text-gray-700">
                            <p className="font-bold text-orange-800 text-[10px] uppercase">Detalles de Devolución:</p>
                            <p className="italic">"{selectedFolio.history.find(h => h.stage === 'DEVOLUCION')?.returnDetails}"</p>
                          </div>
                        )}
                        {!isClientUser(currentUser) && (
                          <button
                            onClick={handleFinalizePlantDelivery}
                            className="mt-2 bg-orange-600 hover:bg-orange-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
                          >
                            Resolver Devolución y Finalizar
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                </div>
              </div>

              {/* ADVANCE PROCESS ACTION PANEL */}
              {!isClientUser(currentUser) && selectedFolio.status !== 'ENTREGA_PLANTA' && selectedFolio.status !== 'DEVOLUCION' && (
                <div className="pt-4 border-t border-gray-100 flex flex-wrap gap-2 justify-end" id="process-advance-actions">
                  
                  {selectedFolio.status === 'INSPECCION' && (
                    <button
                      onClick={() => handleAdvanceStage(true)}
                      className="bg-red-50 hover:bg-red-100 text-red-700 px-4 py-2 rounded-xl text-xs font-bold transition-all border border-red-200"
                    >
                      Falla Inspección (Rework)
                    </button>
                  )}

                  <button
                    onClick={() => handleAdvanceStage(false)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5"
                  >
                    Avanzar Proceso <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>

            {/* REAL-TIME MAP ROUTING COMPONENT */}
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-xs space-y-4" id="map-routing-section">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h4 className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
                    <Map className="h-4 w-4 text-blue-500" /> Monitoreo y Ruta en Tiempo Real
                  </h4>
                  <p className="text-[11px] text-gray-500">Localización geográfica activa en tiempo real estilo WhatsApp Live entre el CEDIS y su planta</p>
                </div>
                {selectedFolio.etaMinutes ? (
                  <div className="bg-blue-50 text-blue-700 border border-blue-100 px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1">
                    <Truck className="h-4 w-4 animate-bounce" />
                    <span>ETA: {selectedFolio.etaMinutes} Mins</span>
                  </div>
                ) : (
                  <span className="text-xs text-gray-400 font-medium">Logística Estática</span>
                )}
              </div>

              {/* Map Type Selector */}
              <div className="flex bg-slate-100 p-1 rounded-lg w-fit text-[10px] font-bold border border-slate-200">
                <button
                  type="button"
                  onClick={() => setMapViewMode('interactive')}
                  className={`px-3 py-1 rounded-md transition-all flex items-center gap-1 cursor-pointer ${
                    mapViewMode === 'interactive'
                      ? 'bg-slate-900 text-white shadow-xs'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  📡 Satelital GPS (Leaflet Live)
                </button>
                <button
                  type="button"
                  onClick={() => setMapViewMode('vector')}
                  className={`px-3 py-1 rounded-md transition-all flex items-center gap-1 cursor-pointer ${
                    mapViewMode === 'vector'
                      ? 'bg-slate-900 text-white shadow-xs'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  📐 Esquema Vectorial (SVG)
                </button>
              </div>

              {mapViewMode === 'interactive' ? (
                <div className="h-80 border border-slate-200 rounded-xl overflow-hidden shadow-inner relative">
                  <LogisticsMap
                    folios={folios.filter(f => f.id === selectedFolio.id)}
                    costCenters={costCenters.filter(cc => cc.clientId === selectedFolio.clientId)}
                    currentUser={currentUser as any}
                    destinations={destinations}
                    hideSidebarConsole={true}
                    previewDestination={{
                      name: selectedFolio.destination || selectedFolio.costCenterName || 'Planta Destino',
                      address: selectedFolio.destination || selectedFolio.costCenterName || '',
                      lat: selectedFolio.destinationLat ? Number(selectedFolio.destinationLat) : (destinations.find(d => d.id === selectedFolio.destinationId)?.lat || 25.4316),
                      lng: selectedFolio.destinationLng ? Number(selectedFolio.destinationLng) : (destinations.find(d => d.id === selectedFolio.destinationId)?.lng || -101.0062),
                      originLat: selectedFolio.originLat ? Number(selectedFolio.originLat) : 25.7820,
                      originLng: selectedFolio.originLng ? Number(selectedFolio.originLng) : -100.4050,
                      originName: selectedFolio.origin || '🏢 CEDIS Escobedo',
                      routeNotes: selectedFolio.routeNotes,
                      estimatedKm: selectedFolio.estimatedKm
                    }}
                  />
                </div>
              ) : (
                /* Interactive Vector SVG Map Grid */
                <div className="h-64 bg-slate-900 rounded-xl relative overflow-hidden border border-slate-800 flex items-center justify-center">
                  
                  {/* Abstract road paths inside grid */}
                  <svg className="absolute inset-0 w-full h-full text-slate-800" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="3 6">
                    <line x1="10%" y1="20%" x2="90%" y2="80%" />
                    <line x1="20%" y1="80%" x2="80%" y2="30%" />
                    <path d="M 50,50 Q 150,120 250,50 T 450,150" fill="none" strokeWidth="1" />
                  </svg>

                  {/* Pin CEDIS */}
                  <div className="absolute left-[30%] top-[45%] flex flex-col items-center">
                    <div className="h-8 w-8 bg-blue-600 text-white rounded-full flex items-center justify-center border-2 border-white shadow-md">
                      <MapPin className="h-4 w-4" />
                    </div>
                    <span className="text-[9px] font-bold text-white bg-slate-950 px-1 py-0.5 rounded-sm mt-1">CEDIS LOGÍSTICA</span>
                  </div>

                  {/* Pin Client */}
                  <div className="absolute left-[70%] top-[60%] flex flex-col items-center">
                    <div className="h-8 w-8 bg-emerald-600 text-white rounded-full flex items-center justify-center border-2 border-white shadow-md">
                      <MapPin className="h-4 w-4" />
                    </div>
                    <span className="text-[9px] font-bold text-white bg-slate-950 px-1 py-0.5 rounded-sm mt-1 truncate max-w-[80px]">CLIENTE PLANTA</span>
                  </div>

                  {/* Moving Truck if in routing state */}
                  {selectedFolio.status === 'RECOLECCION' && (
                    <motion.div 
                      animate={{ x: [-40, 20, -40], y: [-10, 5, -10] }}
                      transition={{ repeat: Infinity, duration: 8, ease: "linear" }}
                      className="absolute left-[45%] top-[50%] bg-indigo-600 text-white p-1.5 rounded-lg flex items-center gap-1 shadow-md border border-white"
                    >
                      <Truck className="h-4 w-4" />
                      <span className="text-[8px] font-bold">RECOLECTANDO</span>
                    </motion.div>
                  )}

                  {selectedFolio.status === 'SALIDA_CEDIS' && (
                    <motion.div 
                      animate={{ x: [-15, 60, -15], y: [-10, 15, -10] }}
                      transition={{ repeat: Infinity, duration: 10, ease: "linear" }}
                      className="absolute left-[45%] top-[50%] bg-amber-500 text-gray-950 p-1.5 rounded-lg flex items-center gap-1 shadow-md border border-white"
                    >
                      <Truck className="h-4 w-4" />
                      <span className="text-[8px] font-bold">RUTA ENTREGA</span>
                    </motion.div>
                  )}

                  {selectedFolio.status === 'ENTREGA_PLANTA' && (
                    <div className="absolute left-[70%] top-[55%] bg-emerald-600 text-white p-1 rounded-sm text-[8px] font-bold">
                      ENTREGADO Satis.
                    </div>
                  )}
                  
                  {/* HUD Coordinates indicator */}
                  <div className="absolute bottom-2 left-2 bg-slate-950/80 border border-slate-800 px-2 py-1 rounded-md text-[9px] font-mono text-gray-400">
                    LAT: {selectedFolio.currentLat ? Number(selectedFolio.currentLat).toFixed(4) : '25.6866'} | LNG: {selectedFolio.currentLng ? Number(selectedFolio.currentLng).toFixed(4) : '-100.3161'}
                  </div>
                </div>
              )}
            </div>

            {/* BITÁCORA CRONOLÓGICA DE AUDITORÍA Y CAMBIOS INTERNOS */}
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-xs space-y-4" id="chronological-internal-audit-log">
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <h4 className="text-sm font-bold text-gray-900 flex items-center gap-1.5 font-sans uppercase tracking-tight">
                  📜 Historial Completo y Auditoría de Cambios Internos
                </h4>
                <span className="text-[10px] bg-blue-50 border border-blue-200 text-blue-700 font-bold px-2 py-0.5 rounded-full font-mono">
                  {selectedFolio.history.length} Eventos Sincronizados
                </span>
              </div>
              <p className="text-[11px] text-gray-500">
                Trazabilidad transparente en tiempo real de cada cambio de estatus, operario de planta y nota técnica registrada por nuestro personal interno de SNF.
              </p>

              <div className="space-y-3.5 relative pl-1">
                {/* Visual vertical line for timeline */}
                <div className="absolute left-3.5 top-2 bottom-2 w-0.5 bg-slate-100"></div>

                {selectedFolio.history.map((entry, idx) => {
                  const stageInfo = STAGE_LABELS[entry.stage] || { label: entry.stage, color: 'bg-slate-100 text-slate-700' };
                  return (
                    <div key={idx} className="flex gap-4 items-start relative">
                      {/* Timeline Dot */}
                      <div className="h-6.5 w-6.5 rounded-full bg-slate-900 text-white flex items-center justify-center font-mono text-[9px] font-bold shrink-0 border-2 border-white shadow-xs z-10">
                        {selectedFolio.history.length - idx}
                      </div>

                      {/* Timeline Card */}
                      <div className="flex-1 bg-slate-50/50 p-3 rounded-xl border border-slate-100/80 space-y-2">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-bold text-xs text-slate-800">
                              {entry.operatorName || 'Operador de Planta'}
                            </span>
                            <span className={`text-[8px] px-1.5 py-0.2 rounded-sm font-bold border ${stageInfo.color}`}>
                              {stageInfo.label}
                            </span>
                          </div>
                          <span className="text-[9px] font-mono font-bold text-slate-400">
                            {new Date(entry.timestamp).toLocaleString()}
                          </span>
                        </div>

                        <p className="text-[11px] text-slate-600 font-medium">
                          {entry.notes}
                        </p>

                        {/* Special Reports (Rework / Returns) */}
                        {entry.incidenceReport && (
                          <div className="bg-red-50/50 border border-red-100/80 p-2 rounded-lg text-xs text-slate-700 space-y-1">
                            <p className="font-black text-red-800 text-[9px] uppercase tracking-wider">🚨 Reporte Técnico de Control de Calidad / Defecto:</p>
                            <p className="italic font-medium text-red-700">"{entry.incidenceReport}"</p>
                          </div>
                        )}

                        {entry.returnDetails && (
                          <div className="bg-orange-50/50 border border-orange-100/80 p-2 rounded-lg text-xs text-slate-700 space-y-1">
                            <p className="font-black text-orange-800 text-[9px] uppercase tracking-wider">🔄 Detalle de Devolución de Cliente:</p>
                            <p className="italic font-medium text-orange-700">"{entry.returnDetails}"</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* NOTIFICATIONS LOG */}
            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-xs space-y-3">
              <h4 className="text-sm font-bold text-gray-900">Bitácora de Notificaciones Automáticas</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                {notificationsSent.filter(n => n.folioId === selectedFolio.id).length > 0 ? (
                  notificationsSent.filter(n => n.folioId === selectedFolio.id).map((notif) => (
                    <div key={notif.id} className="p-2.5 rounded-xl border border-gray-100 bg-gray-50/50 flex gap-3 text-xs items-start">
                      <div className={`p-1.5 rounded-lg ${notif.type === 'SMS' ? 'bg-orange-50 text-orange-600' : 'bg-blue-50 text-blue-600'} shrink-0`}>
                        {notif.type === 'SMS' ? <MessageSquare className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
                      </div>
                      <div className="space-y-0.5 flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-gray-800 text-[10px] uppercase">{notif.type} ➔ {notif.destination}</span>
                          <span className="text-[9px] text-gray-400">{notif.time}</span>
                        </div>
                        <p className="text-[11px] text-gray-600 line-clamp-2">"{notif.message}"</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-gray-400 text-center py-4">No se han disparado alertas automatizadas para este folio aún.</p>
                )}
              </div>
            </div>

          </div>
        )) : (
          <div className="text-center py-20 bg-gray-50 rounded-2xl border border-dashed border-gray-200 text-gray-400">
            Cargando detalles de trazabilidad...
          </div>
        )}

      </div>
      )}

      {/* MODAL: INSPECTION FAIL (REWORK FORM EN FASE DE INSPECCIÓN) */}
      <AnimatePresence>
        {showReworkModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-xs flex items-center justify-center p-4 z-50">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-3xl max-w-2xl w-full p-6 space-y-5 shadow-2xl border border-gray-200 max-h-[90vh] overflow-y-auto custom-scrollbar"
            >
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-red-100 text-red-700 rounded-xl">
                    <AlertTriangle className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-black text-gray-900">Formulario de Retrabajo - Fase de Inspección CC</h3>
                    <p className="text-xs text-gray-500">Reporte de fallas y captura de 7 fotos para evidencia fotográfica de calidad.</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowReworkModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* 📋 DETALLE DEL FOLIO */}
              {selectedFolio && (() => {
                const totalFolioPieces = selectedFolio.items?.reduce((a, b) => a + (Number(b.quantity) || 0), 0) || 1;

                return (
                  <div className="bg-red-50/70 border border-red-200 rounded-2xl p-4 text-xs space-y-3">
                    <div className="flex items-center justify-between font-bold border-b border-red-100 pb-2">
                      <span className="text-red-900 text-sm font-black font-mono flex items-center gap-2">
                        <Receipt className="h-4 w-4 text-red-600" />
                        Detalle de Folio: #{selectedFolio.id}
                      </span>
                      <span className="bg-red-200 text-red-900 font-extrabold px-2 py-0.5 rounded-md text-[10px] uppercase tracking-wider">
                        FASE 4: INSPECCIÓN CC
                      </span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 text-gray-700 font-medium">
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 block uppercase">Cliente:</span>
                        <span className="font-bold text-gray-900">{selectedFolio.clientName || 'Cliente'}</span>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 block uppercase">Destino:</span>
                        <span className="font-bold text-gray-900">{selectedFolio.destinationCity || 'San Luis Potosí'}</span>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 block uppercase">Piezas Totales en Folio:</span>
                        <span className="font-black text-red-700 font-mono text-xs">
                          {totalFolioPieces} pzas
                        </span>
                      </div>
                    </div>

                    {/* 📦 DESGROSE DE MERCANCÍAS DEL FOLIO */}
                    <div className="border-t border-red-200/80 pt-2.5 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-black uppercase text-red-900 tracking-wider">
                          Mercancías / Productos Registrados ({selectedFolio.items?.length || 0}):
                        </span>
                        <span className="text-[9.5px] font-bold text-red-700 bg-red-100/80 px-2 py-0.5 rounded-full font-mono">
                          Total: {totalFolioPieces} piezas
                        </span>
                      </div>

                      <div className="bg-white/90 rounded-xl border border-red-200/60 overflow-hidden divide-y divide-red-100 shadow-2xs max-h-36 overflow-y-auto custom-scrollbar">
                        {selectedFolio.items && selectedFolio.items.length > 0 ? (
                          selectedFolio.items.map((item, idx) => (
                            <div key={idx} className="p-2 flex items-center justify-between text-[11px]">
                              <div className="font-semibold text-gray-800 flex items-center gap-2 pr-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0"></span>
                                <span>{item.description}</span>
                              </div>
                              <div className="font-mono font-bold text-red-800 bg-red-50 px-2 py-0.5 rounded-md border border-red-100 shrink-0">
                                {item.quantity} {item.unit || 'pzas'}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="p-2 text-gray-500 italic text-[11px] text-center">
                            1 Lote General de Mercancía ({totalFolioPieces} pzas)
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}

              <form onSubmit={handleReworkSubmit} className="space-y-4">
                
                {/* 🔧 ALCANCE Y CANTIDAD A RETRABAJAR */}
                {selectedFolio && (() => {
                  const totalFolioPieces = selectedFolio.items?.reduce((a, b) => a + (Number(b.quantity) || 0), 0) || 1;
                  const allItemsDesc = selectedFolio.items?.map(i => `${i.description} (${i.quantity} pzas)`).join(', ') || 'Todo el lote del folio';

                  return (
                    <div className="bg-gray-50 border border-gray-200 rounded-2xl p-3.5 space-y-3">
                      <label className="text-[11px] font-black text-gray-800 uppercase tracking-wider block">
                        Alcance del Retrabajo *
                      </label>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setReworkType('TOTAL');
                            setReworkQuantity(totalFolioPieces);
                            setReworkPiecesName(`Todo el Folio #${selectedFolio.id} (${allItemsDesc})`);
                          }}
                          className={`p-3 rounded-xl border text-left flex items-start gap-2.5 transition-all cursor-pointer ${
                            reworkType === 'TOTAL'
                              ? 'bg-red-50 border-red-500 text-red-900 ring-2 ring-red-400/20 shadow-xs'
                              : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="reworkScopeModal"
                            checked={reworkType === 'TOTAL'}
                            onChange={() => {}}
                            className="mt-0.5 text-red-600 focus:ring-red-500 cursor-pointer"
                          />
                          <div>
                            <div className="font-black text-xs text-gray-900">Mandar TODO el Folio Completo</div>
                            <div className="text-[10px] text-gray-500 mt-0.5 font-medium">
                              Se envían las <strong className="text-red-700 font-mono">{totalFolioPieces} piezas</strong> registradas.
                            </div>
                          </div>
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setReworkType('PARCIAL');
                            if (reworkQuantity >= totalFolioPieces) {
                              setReworkQuantity(Math.max(1, totalFolioPieces - 1));
                            }
                            if (reworkPiecesName.startsWith('Todo el Folio')) {
                              setReworkPiecesName('');
                            }
                          }}
                          className={`p-3 rounded-xl border text-left flex items-start gap-2.5 transition-all cursor-pointer ${
                            reworkType === 'PARCIAL'
                              ? 'bg-red-50 border-red-500 text-red-900 ring-2 ring-red-400/20 shadow-xs'
                              : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="reworkScopeModal"
                            checked={reworkType === 'PARCIAL'}
                            onChange={() => {}}
                            className="mt-0.5 text-red-600 focus:ring-red-500 cursor-pointer"
                          />
                          <div>
                            <div className="font-black text-xs text-gray-900">Mandar Piezas / Cantidad Parcial</div>
                            <div className="text-[10px] text-gray-500 mt-0.5 font-medium">
                              Especifique solo la cantidad de piezas o componentes con falla.
                            </div>
                          </div>
                        </button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                        <div className="sm:col-span-2 space-y-1">
                          <label className="text-[10px] font-black text-gray-700 uppercase tracking-wider block">
                            Piezas / Componentes a Retrabajar *
                          </label>
                          <input
                            type="text"
                            required
                            value={reworkPiecesName}
                            onChange={(e) => setReworkPiecesName(e.target.value)}
                            placeholder="Ej. Ganchos laterales de estantería, rejillas de decapado..."
                            className="w-full border border-gray-200 bg-white rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-red-500 font-semibold text-gray-900"
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="text-[10px] font-black text-gray-700 uppercase tracking-wider block">
                            Cantidad de Piezas *
                          </label>
                          <div className="relative">
                            <input
                              type="number"
                              required
                              min="1"
                              max={totalFolioPieces}
                              value={reworkQuantity}
                              onChange={(e) => {
                                const val = Number(e.target.value);
                                setReworkQuantity(val);
                                if (val >= totalFolioPieces) {
                                  setReworkType('TOTAL');
                                } else {
                                  setReworkType('PARCIAL');
                                }
                              }}
                              className="w-full border border-gray-200 bg-white rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-red-500 font-bold font-mono text-red-700 pr-12"
                            />
                            <span className="absolute right-2.5 top-2 text-[10px] font-bold text-gray-400">
                              / {totalFolioPieces}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* 📝 DESCRIPCIÓN DEL DEFECTO */}
                <div className="space-y-1">
                  <label className="text-[11px] font-extrabold text-gray-700 uppercase tracking-wider block">
                    Detalle del Defecto / Falla en Inspección *
                  </label>
                  <textarea
                    required
                    rows={2}
                    value={reworkReport}
                    onChange={(e) => setReworkReport(e.target.value)}
                    placeholder="Detalles del defecto: Exceso de escoria térmica, picaduras por decapado ácido, residuos de pintura o falta de micraje..."
                    className="w-full border border-gray-200 rounded-xl p-3 text-xs focus:outline-hidden focus:border-red-500 font-medium"
                  />
                </div>

                {/* 📸 FOTOS DE EVIDENCIA DE RETRABAJO (SIN TÍTULOS PREDEFINIDOS) */}
                <div className="space-y-2 border-t border-gray-100 pt-3">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-black text-gray-800 uppercase tracking-wider font-mono flex items-center gap-1.5">
                      <Camera className="h-4 w-4 text-red-600" />
                      <span>Evidencia Fotográfica de Retrabajo (Hasta 7 Fotos)</span>
                    </label>
                    <span className="text-[10px] font-bold text-gray-500 font-mono">
                      {reworkPhotos.filter(p => !!p).length} / 7 fotos cargadas
                    </span>
                  </div>

                  {/* 7 Clean Photo Upload Slots without titles */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                    {[0, 1, 2, 3, 4, 5, 6].map((idx) => {
                      const photoUrl = reworkPhotos[idx];

                      return (
                        <div key={idx} className="bg-gray-50 border border-gray-200 rounded-xl p-2 flex flex-col justify-between relative">
                          <div className="h-20 bg-white border border-dashed border-gray-300 rounded-lg overflow-hidden flex items-center justify-center relative">
                            {photoUrl ? (
                              <>
                                <img src={photoUrl} alt={`Evidencia ${idx + 1}`} className="w-full h-full object-cover" />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const next = [...reworkPhotos];
                                    next[idx] = '';
                                    setReworkPhotos(next);
                                  }}
                                  className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full hover:bg-red-700 shadow-md cursor-pointer"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </>
                            ) : (
                              <label className="flex flex-col items-center justify-center space-y-1 text-gray-400 hover:text-red-600 cursor-pointer p-1 text-center w-full h-full">
                                <Upload className="h-4 w-4" />
                                <span className="text-[9px] font-bold">Foto #{idx + 1}</span>
                                <input
                                  type="file"
                                  accept="image/*"
                                  capture="environment"
                                  className="hidden"
                                  onChange={(e) => {
                                    const files = e.target.files;
                                    if (files && files[0]) {
                                      const reader = new FileReader();
                                      reader.onloadend = () => {
                                        const next = [...reworkPhotos];
                                        next[idx] = reader.result as string;
                                        setReworkPhotos(next);
                                      };
                                      reader.readAsDataURL(files[0]);
                                    }
                                  }}
                                />
                              </label>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Form Action Buttons */}
                <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-3">
                  <button
                    type="button"
                    onClick={() => setShowReworkModal(false)}
                    className="px-4 py-2 text-xs font-bold text-gray-500 hover:text-gray-800 rounded-xl"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="bg-red-600 hover:bg-red-700 text-white font-extrabold px-5 py-2.5 rounded-xl text-xs shadow-md transition-all flex items-center gap-2 cursor-pointer"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    <span>Enviar a Retrabajo e Inscribir Reporte</span>
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL: DEVOLUCIONES */}
      <AnimatePresence>
        {showReturnModal && selectedFolio && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 z-50">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4 shadow-2xl border border-gray-100 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <h3 className="text-base font-bold text-orange-600 flex items-center gap-2">
                  <Undo className="h-5 w-5" /> Registrar Devolución en Planta ({selectedFolio.id})
                </h3>
                <button
                  type="button"
                  onClick={() => setShowReturnModal(false)}
                  className="text-slate-400 hover:text-slate-600 cursor-pointer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <p className="text-xs text-slate-500">
                Especifique si la devolución por inconformidad del cliente es del folio completo o solo una parte parcial de los materiales.
              </p>
              
              <form onSubmit={handleReturnSubmit} className="space-y-4">
                {/* Selector de Tipo de Devolución */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-slate-700">Tipo de Devolución:</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setReturnType('TOTAL')}
                      className={`p-2.5 rounded-xl border text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${
                        returnType === 'TOTAL'
                          ? 'bg-orange-50 border-orange-500 text-orange-900 shadow-xs ring-2 ring-orange-400/20'
                          : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <span>📦 Devolución Total</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setReturnType('PARCIAL')}
                      className={`p-2.5 rounded-xl border text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${
                        returnType === 'PARCIAL'
                          ? 'bg-orange-50 border-orange-500 text-orange-900 shadow-xs ring-2 ring-orange-400/20'
                          : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <span>📑 Devolución Parcial</span>
                    </button>
                  </div>
                </div>

                {/* Banner Devolución Total */}
                {returnType === 'TOTAL' && (
                  <div className="bg-orange-50/80 p-3 rounded-xl border border-orange-200 text-xs text-orange-800 space-y-1">
                    <p className="font-bold">⚠️ Se registrará la devolución completa del Folio {selectedFolio.id}.</p>
                    <p className="text-[11px] text-orange-700">
                      Todas las partidas e ítems ({selectedFolio.items?.reduce((a, b) => a + (Number(b.quantity) || 0), 0) || 0} piezas en total) se marcarán como devueltas.
                    </p>
                  </div>
                )}

                {/* Selector de Materiales y Cantidades para Devolución Parcial */}
                {returnType === 'PARCIAL' && (
                  <div className="space-y-2">
                    <label className="block text-xs font-bold text-slate-700">
                      Materiales y Cantidades a Devolver:
                    </label>
                    <div className="space-y-2 max-h-52 overflow-y-auto pr-1 bg-slate-50 p-2.5 rounded-xl border border-slate-200">
                      {selectedFolio.items && selectedFolio.items.length > 0 ? (
                        selectedFolio.items.map((item, idx) => {
                          const itemKey = item.goodsItemId || `item-${idx}`;
                          const state = partialReturnItems[itemKey] || { selected: false, quantity: Number(item.quantity) || 1 };
                          return (
                            <div key={itemKey} className="flex items-center justify-between gap-2 p-2 bg-white rounded-lg border border-slate-200 text-xs shadow-2xs">
                              <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                                <input
                                  type="checkbox"
                                  checked={state.selected}
                                  onChange={(e) => {
                                    setPartialReturnItems(prev => ({
                                      ...prev,
                                      [itemKey]: { ...state, selected: e.target.checked }
                                    }));
                                  }}
                                  className="rounded border-slate-300 text-orange-600 focus:ring-orange-500 h-4 w-4 shrink-0"
                                />
                                <div className="truncate">
                                  <p className="font-bold text-slate-800 truncate" title={item.description}>
                                    {item.description}
                                  </p>
                                  <p className="text-[10px] text-slate-400">Total en folio: {item.quantity} {item.unit || 'piezas'}</p>
                                </div>
                              </label>

                              <div className="flex items-center gap-1.5 shrink-0">
                                <span className="text-[10px] text-slate-500 font-bold">Devolver:</span>
                                <input
                                  type="number"
                                  min={1}
                                  max={item.quantity}
                                  disabled={!state.selected}
                                  value={state.quantity}
                                  onChange={(e) => {
                                    const val = Math.min(Number(item.quantity), Math.max(1, parseInt(e.target.value) || 1));
                                    setPartialReturnItems(prev => ({
                                      ...prev,
                                      [itemKey]: { ...state, quantity: val }
                                    }));
                                  }}
                                  className="w-16 border border-slate-300 rounded-lg px-2 py-1 text-center font-bold text-slate-900 text-xs focus:border-orange-500 focus:outline-hidden disabled:opacity-40"
                                />
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-xs text-slate-400 text-center py-2">No hay mercancía registrada en este folio.</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Motivo u Observaciones */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Motivo y Observaciones de la Devolución:
                  </label>
                  <textarea
                    required
                    value={returnDetails}
                    onChange={(e) => setReturnDetails(e.target.value)}
                    placeholder="Especifique las causas del rechazo: Piezas oxidadas, dimensiones fuera de tolerancia, recubrimiento incompleto..."
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-orange-500 h-24 text-slate-800"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setShowReturnModal(false)}
                    className="px-4 py-2 text-xs text-slate-600 hover:text-slate-800 font-bold rounded-xl cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="bg-orange-600 hover:bg-orange-700 text-white font-extrabold px-5 py-2 rounded-xl text-xs shadow-sm transition-all cursor-pointer active:scale-[0.98]"
                  >
                    Registrar Incidencia de Devolución
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* PHOTO CAPTURE AND SIMULATION MODAL */}
      <AnimatePresence>
        {activePhotoIndex !== null && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-xl border border-gray-100 space-y-4"
            >
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <h4 className="text-sm font-bold text-gray-900">
                  {activePhotoIndex < 0 ? `Evidencia de Carga - Foto #${Math.abs(activePhotoIndex)}` : `Evidencia de Carga - Ítem #${activePhotoIndex + 1}`}
                </h4>
                <button
                  type="button"
                  onClick={() => {
                    stopWebcam();
                    setActivePhotoIndex(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-sm cursor-pointer"
                >
                  ✕
                </button>
              </div>

              {/* Live Webcam Stream or Fallback Message */}
              <div className="bg-slate-900 rounded-xl overflow-hidden aspect-video flex flex-col items-center justify-center text-white relative">
                {webcamStream ? (
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="p-4 text-center space-y-2">
                    <p className="text-xs text-slate-400">
                      Cámara desactivada o no disponible en este dispositivo/iframe.
                    </p>
                    <button
                      type="button"
                      onClick={startWebcam}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition-all cursor-pointer"
                    >
                      Iniciar Cámara Web Live
                    </button>
                    {webcamError && (
                      <p className="text-[10px] text-red-400 font-medium px-2 max-w-xs mx-auto leading-normal">
                        {webcamError}
                      </p>
                    )}
                  </div>
                )}

                {webcamStream && (
                  <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2 px-2">
                    <button
                      type="button"
                      onClick={capturePhoto}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-1.5 px-3 rounded-lg text-xs transition-all shadow-md cursor-pointer"
                    >
                      Capturar Foto
                    </button>
                    <button
                      type="button"
                      onClick={toggleCamera}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-1.5 px-3 rounded-lg text-xs transition-all flex items-center gap-1 cursor-pointer"
                    >
                      <RefreshCw className="h-3.5 w-3.5" /> Cambiar Cámara
                    </button>
                    <button
                      type="button"
                      onClick={stopWebcam}
                      className="bg-slate-700 hover:bg-slate-800 text-white font-bold py-1.5 px-3 rounded-lg text-xs transition-all cursor-pointer"
                    >
                      Detener Cámara
                    </button>
                  </div>
                )}
              </div>

              {/* Demo Simulators */}
              <div className="space-y-2">
                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block">
                  Simular Captura Industrial (Pruebas Rápidas)
                </span>
                <div className="grid grid-cols-2 gap-2">
                  {SIMULATED_PHOTOS.map((sim, sIdx) => (
                    <button
                      key={sIdx}
                      type="button"
                      onClick={() => {
                        setFormItems(prev => prev.map((item, i) => {
                          if (i === activePhotoIndex) {
                            return { ...item, photoUrl: sim.url };
                          }
                          return item;
                        }));
                        stopWebcam();
                        setActivePhotoIndex(null);
                      }}
                      className="text-left p-1.5 border border-slate-200 rounded-xl hover:border-blue-500 hover:bg-blue-50/20 transition-all text-[11px] font-medium text-slate-700 flex items-center gap-2 cursor-pointer"
                    >
                      <img
                        src={sim.url}
                        alt="Sim"
                        className="h-8 w-8 object-cover rounded-md border border-slate-100 shrink-0"
                      />
                      <span className="truncate">{sim.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
                <button
                  type="button"
                  onClick={() => {
                    stopWebcam();
                    setActivePhotoIndex(null);
                  }}
                  className="px-4 py-1.5 border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-lg text-xs font-semibold cursor-pointer"
                >
                  Cerrar
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* REGISTRAR NUEVO ENVÍO LOGÍSTICO OVERLAY MODAL */}
      <AnimatePresence>
        {isCreating && (
          <div className="fixed inset-0 bg-slate-950/65 backdrop-blur-xs flex items-center justify-center z-40 p-4 overflow-y-auto">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl max-w-4xl w-full shadow-2xl border border-slate-100 overflow-hidden flex flex-col my-8 max-h-[90vh]"
            >
              {/* Modal Header */}
              <div className="bg-slate-900 text-white p-4 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-blue-600 rounded-lg text-white">
                    <FilePlus className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold">Registrar Nuevo Envío Logístico</h3>
                    <p className="text-[11px] text-slate-300">Formulario profesional de trazabilidad y evidencia SNF • Servicios Nuevos De Filtración Hispanomexicános</p>
                  </div>
                </div>
                <button 
                  onClick={() => setIsCreating(false)}
                  className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-all cursor-pointer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Scrollable Form Body */}
              <div className="p-6 overflow-y-auto space-y-6 flex-1">
                {/* Step 1: Socio Comercial / Cliente Selection */}
                <div className="bg-blue-50/70 border border-blue-200/90 rounded-2xl p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 shadow-2xs">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-blue-600 text-white rounded-xl shadow-xs">
                      <Building2 className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <label className="text-xs font-bold text-slate-900 uppercase tracking-wide">
                          Socio Comercial / Cliente Destinatario *
                        </label>
                        {formClientId && (
                          <span className="text-[10px] bg-blue-600 text-white font-bold px-2 py-0.5 rounded-full font-mono">
                            {clients.find(c => c.id === formClientId)?.code || 'Seleccionado'}
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-slate-500">
                        Al seleccionar el cliente se filtrarán y priorizarán sus rutas, destinos y centros de costo correspondientes.
                      </p>
                    </div>
                  </div>

                  <div className="w-full md:w-80">
                    <select
                      value={formClientId}
                      onChange={(e) => {
                        const newCId = e.target.value;
                        setFormClientId(newCId);
                        setSelectedDestinationId('');
                        setSelectedOriginId('');
                        // Auto-assign first cost center if exists
                        const clientCCs = costCenters.filter(cc => 
                          cc.clientId === newCId || 
                          (cc.clientIds && cc.clientIds.includes(newCId))
                        );
                        if (clientCCs.length > 0) {
                          setFormCostCenterId(clientCCs[0].id);
                          setFormOrigin(`${clientCCs[0].name} - ${clientCCs[0].address}`);
                          if (clientCCs[0].lat && clientCCs[0].lng) {
                            setFormOriginLat(clientCCs[0].lat);
                            setFormOriginLng(clientCCs[0].lng);
                          }
                        }
                      }}
                      disabled={isClientUser(currentUser)}
                      className="w-full bg-white border border-blue-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-900 focus:outline-hidden focus:border-blue-600 shadow-2xs cursor-pointer"
                    >
                      <option value="">-- Seleccionar Socio Comercial --</option>
                      {clients.map(c => (
                        <option key={c.id} value={c.id}>
                          🏢 {c.name} ({c.code})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Logistics Header Fields */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Shipment Date */}
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-700">Fecha de Embarque *</label>
                    <input
                      type="date"
                      required
                      value={formShipmentDate}
                      onChange={(e) => setFormShipmentDate(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-blue-500 font-medium"
                    />
                  </div>

                  {/* Sello / Folio ID Input */}
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-700">Sello o Folio de Recepción</label>
                    <input
                      type="text"
                      value={formFolioId}
                      onChange={(e) => setFormFolioId(e.target.value)}
                      placeholder="Autogenerado si se deja vacío"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-blue-500 font-medium"
                    />
                  </div>

                  {/* Nombre del chofer */}
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-700">Nombre del Chofer *</label>
                    <input
                      type="text"
                      required
                      placeholder="Nombre completo del transportista"
                      value={formDriverName}
                      onChange={(e) => setFormDriverName(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-blue-500 font-medium"
                    />
                  </div>

                  {/* N° Económico */}
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-700">N° Económico de Unidad *</label>
                    <input
                      type="text"
                      required
                      placeholder="Unidad de transporte"
                      value={formEconomicNumber}
                      onChange={(e) => setFormEconomicNumber(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-blue-500 font-medium"
                    />
                  </div>
                </div>

                {/* TIPO DE PROCESO DE DECAPADO / TRATAMIENTO DE ÍTEMS */}
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200/90 space-y-3">
                  <div className="flex items-center justify-between border-b border-slate-200/80 pb-2">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-amber-100 text-amber-800 rounded-lg">
                        <Flame className="h-4 w-4" />
                      </div>
                      <div>
                        <h5 className="text-xs font-bold text-slate-900 uppercase tracking-wide">TIPO DE PROCESO DE DECAPADO / TRATAMIENTO DE ÍTEMS *</h5>
                        <p className="text-[10px] text-slate-500">Seleccione el proceso asignado a las piezas de este envío para definir su flujo en la planta</p>
                      </div>
                    </div>
                    <span className="text-[10px] font-mono font-bold bg-slate-200/80 text-slate-700 px-2 py-0.5 rounded-full">
                      Flujo Automatizado
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {/* Opción 1: DECAPADO TÉRMICO */}
                    <button
                      type="button"
                      onClick={() => {
                        setFormProcessType('DECAPADO_TERMICO');
                        setFormHasGranallado(false);
                      }}
                      className={`p-3.5 rounded-xl border text-left transition-all cursor-pointer space-y-2 relative ${
                        formProcessType === 'DECAPADO_TERMICO'
                          ? 'bg-amber-500/10 border-amber-500 shadow-xs ring-2 ring-amber-400/20'
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded-lg ${formProcessType === 'DECAPADO_TERMICO' ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-600'}`}>
                            <Flame className="h-4 w-4" />
                          </div>
                          <span className="font-extrabold text-xs text-slate-900">DECAPADO TÉRMICO</span>
                        </div>
                        <input
                          type="radio"
                          name="createProcessType"
                          checked={formProcessType === 'DECAPADO_TERMICO'}
                          onChange={() => {}}
                          className="accent-amber-600 h-4 w-4 cursor-pointer"
                        />
                      </div>
                      <p className="text-[10px] text-slate-600 font-medium leading-relaxed">
                        Tratamiento en horno térmico. Fluye al proceso de <strong>LAVADO</strong> antes de inspección.
                      </p>
                      <div className="inline-flex items-center gap-1 text-[9px] font-bold bg-amber-100 text-amber-900 px-2 py-0.5 rounded-md font-mono">
                        <span>Flujo: Decapado ➔ Lavado ➔ Inspección</span>
                      </div>
                    </button>

                    {/* Opción 2: DECAPADO QUÍMICO */}
                    <button
                      type="button"
                      onClick={() => {
                        setFormProcessType('DECAPADO_QUIMICO');
                        setFormHasGranallado(false);
                      }}
                      className={`p-3.5 rounded-xl border text-left transition-all cursor-pointer space-y-2 relative ${
                        formProcessType === 'DECAPADO_QUIMICO'
                          ? 'bg-blue-500/10 border-blue-500 shadow-xs ring-2 ring-blue-400/20'
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded-lg ${formProcessType === 'DECAPADO_QUIMICO' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
                            <FlaskConical className="h-4 w-4" />
                          </div>
                          <span className="font-extrabold text-xs text-slate-900">DECAPADO QUÍMICO</span>
                        </div>
                        <input
                          type="radio"
                          name="createProcessType"
                          checked={formProcessType === 'DECAPADO_QUIMICO'}
                          onChange={() => {}}
                          className="accent-blue-600 h-4 w-4 cursor-pointer"
                        />
                      </div>
                      <p className="text-[10px] text-slate-600 font-medium leading-relaxed">
                        Tratamiento en tina ácida/inmersión. Fluye al proceso de <strong>LAVADO</strong> antes de inspección.
                      </p>
                      <div className="inline-flex items-center gap-1 text-[9px] font-bold bg-blue-100 text-blue-900 px-2 py-0.5 rounded-md font-mono">
                        <span>Flujo: Decapado ➔ Lavado ➔ Inspección</span>
                      </div>
                    </button>

                    {/* Opción 3: DECAPADO POR GRANALLADORA */}
                    <button
                      type="button"
                      onClick={() => {
                        setFormProcessType('DECAPADO_GRANALLADORA');
                        setFormHasGranallado(true);
                      }}
                      className={`p-3.5 rounded-xl border text-left transition-all cursor-pointer space-y-2 relative ${
                        formProcessType === 'DECAPADO_GRANALLADORA'
                          ? 'bg-purple-500/10 border-purple-500 shadow-xs ring-2 ring-purple-400/20'
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded-lg ${formProcessType === 'DECAPADO_GRANALLADORA' ? 'bg-purple-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
                            <Sparkles className="h-4 w-4" />
                          </div>
                          <span className="font-extrabold text-xs text-slate-900">DECAPADO POR GRANALLADORA</span>
                        </div>
                        <input
                          type="radio"
                          name="createProcessType"
                          checked={formProcessType === 'DECAPADO_GRANALLADORA'}
                          onChange={() => {}}
                          className="accent-purple-600 h-4 w-4 cursor-pointer"
                        />
                      </div>
                      <p className="text-[10px] text-slate-600 font-medium leading-relaxed">
                        Tratamiento abrasivo de alta presión. Pasa <strong>DIRECTO A INSPECCIÓN</strong> (omite lavado).
                      </p>
                      <div className="inline-flex items-center gap-1 text-[9px] font-bold bg-purple-100 text-purple-900 px-2 py-0.5 rounded-md font-mono">
                        <span>Flujo Directo: Decapado ➔ Inspección</span>
                      </div>
                    </button>
                  </div>
                </div>

                {/* CONFIGURACIÓN DE RUTA LOGÍSTICA: ORIGEN ➔ DESTINO */}
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200/90 space-y-4">
                  <div className="flex items-center justify-between border-b border-slate-200/80 pb-2">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-blue-100 text-blue-700 rounded-lg">
                        <MapPin className="h-4 w-4" />
                      </div>
                      <div>
                        <h5 className="text-xs font-bold text-slate-900 uppercase tracking-wide">CONFIGURACIÓN DE RUTA LOGÍSTICA (ORIGEN Y DESTINO)</h5>
                        <p className="text-[10px] text-slate-500">Selecciona o escribe el Origen y el Destino para el trazado de ruta y rastreo GPS en tiempo real</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* COLUMNA ORIGEN */}
                    <div className="bg-white p-3.5 rounded-xl border border-blue-200/90 space-y-3 shadow-2xs flex flex-col justify-between">
                      <div className="space-y-2.5">
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1.5 text-xs font-bold text-blue-900 uppercase tracking-wider">
                            <MapPin className="h-4 w-4 text-blue-600" /> Origen (Punto de Partida) *
                          </span>
                          <span className="text-[10px] bg-blue-100 text-blue-800 font-bold px-2 py-0.5 rounded-full font-mono">
                            PUNTO A
                          </span>
                        </div>

                        {/* SELECTOR DE ORIGENES (ÚNICAMENTE CATÁLOGO DE RUTAS CREADAS) */}
                        <div className="space-y-1">
                          <label className="text-[10px] font-bold text-slate-500 uppercase">
                            Seleccionar Origen de Rutas Registradas:
                          </label>
                          <select
                            value={selectedOriginId}
                            onChange={(e) => {
                              const val = e.target.value;
                              setSelectedOriginId(val);
                              if (val === 'CUSTOM') {
                                // Keep manual input
                              } else {
                                const dest = (destinations || []).find(d => d.id === val);
                                if (dest) {
                                  setFormOrigin(`${dest.name} (${dest.address})`);
                                  setFormOriginLat(dest.lat);
                                  setFormOriginLng(dest.lng);
                                }
                              }
                            }}
                            className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:outline-hidden focus:border-blue-600 shadow-2xs cursor-pointer"
                          >
                            <option value="">-- Seleccionar Origen (Panel de Destinos y Rutas) --</option>
                            {destinations && destinations.length > 0 ? (
                              <>
                                {formClientId && clientSpecificDestinations.length > 0 && (
                                  <optgroup label={`🏢 Rutas de ${clients.find(c => c.id === formClientId)?.name || 'Cliente Seleccionado'}`}>
                                    {clientSpecificDestinations.map(dest => (
                                      <option key={dest.id} value={dest.id}>
                                        📍 {dest.name} - {dest.address}
                                      </option>
                                    ))}
                                  </optgroup>
                                )}
                                {generalDestinations.length > 0 && (
                                  <optgroup label="🌐 Destinos y Plantas Generales">
                                    {generalDestinations.map(dest => (
                                      <option key={dest.id} value={dest.id}>
                                        📍 {dest.name} - {dest.address}
                                      </option>
                                    ))}
                                  </optgroup>
                                )}
                                {formClientId && otherClientDestinations.length > 0 && (
                                  <optgroup label="🏢 Rutas de Otros Clientes">
                                    {otherClientDestinations.map(dest => {
                                      const clientLabel = dest.clientIds && dest.clientIds.length > 0
                                        ? ` [${dest.clientIds.map(cid => clients.find(c => c.id === cid || c.code === cid)?.code || cid).join(', ')}]`
                                        : (dest.clientName ? ` [${dest.clientName}]` : '');
                                      return (
                                        <option key={dest.id} value={dest.id}>
                                          📍 {dest.name}{clientLabel} - {dest.address}
                                        </option>
                                      );
                                    })}
                                  </optgroup>
                                )}
                                {!formClientId && (
                                  destinations.map(dest => {
                                    const clientLabel = dest.clientIds && dest.clientIds.length > 0
                                      ? ` [🏢 ${dest.clientIds.map(cid => clients.find(c => c.id === cid || c.code === cid)?.code || cid).join(', ')}]`
                                      : (dest.clientName ? ` [🏢 ${dest.clientName}]` : ' [🌐 General]');
                                    return (
                                      <option key={dest.id} value={dest.id}>
                                        📍 {dest.name}{clientLabel} - {dest.address}
                                      </option>
                                    );
                                  })
                                )}
                              </>
                            ) : (
                              <option value="" disabled>⚠️ No hay rutas ni destinos creados aún</option>
                            )}
                            <option value="CUSTOM">✏️ Escribir Dirección Personalizada</option>
                          </select>
                        </div>

                        {/* DIRECCIÓN DE ORIGEN */}
                        <div className="space-y-1">
                          <label className="text-[11px] font-bold text-slate-700 flex items-center justify-between">
                            <span>Nombre / Dirección de Origen *</span>
                          </label>
                          <input
                            type="text"
                            required
                            value={formOrigin}
                            onChange={(e) => {
                              const val = e.target.value;
                              setFormOrigin(val);
                              const lower = val.toLowerCase();
                              if (lower.includes('escobedo')) { setFormOriginLat(25.7820); setFormOriginLng(-100.4050); }
                              else if (lower.includes('saltillo')) { setFormOriginLat(25.4316); setFormOriginLng(-101.0062); }
                              else if (lower.includes('potos') || lower.includes('slp')) { setFormOriginLat(22.1565); setFormOriginLng(-100.9855); }
                              else if (lower.includes('ramos')) { setFormOriginLat(25.5420); setFormOriginLng(-100.9310); }
                              else if (lower.includes('apodaca')) { setFormOriginLat(25.7800); setFormOriginLng(-100.1800); }
                            }}
                            placeholder="Ej: CEDIS Escobedo NL, Av. Las Torres #200..."
                            className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-blue-600 font-medium"
                          />
                        </div>
                      </div>

                      {formOriginLat !== undefined && formOriginLng !== undefined && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-[10px] text-blue-900 font-mono flex items-center justify-between mt-2">
                          <span>📍 Coordenadas GPS Iniciales:</span>
                          <span className="font-bold">{formOriginLat.toFixed(4)}, {formOriginLng.toFixed(4)}</span>
                        </div>
                      )}
                    </div>

                    {/* COLUMNA DESTINO */}
                    <div className="bg-white p-3.5 rounded-xl border border-red-200/90 space-y-3 shadow-2xs flex flex-col justify-between">
                      <div className="space-y-2.5">
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1.5 text-xs font-bold text-red-900 uppercase tracking-wider">
                            <Navigation className="h-4 w-4 text-red-600" /> Destino (Punto de Llegada) *
                          </span>
                          <span className="text-[10px] bg-red-100 text-red-800 font-bold px-2 py-0.5 rounded-full font-mono">
                            PUNTO B
                          </span>
                        </div>

                        {/* SELECTOR DE DESTINOS (ÚNICAMENTE CATÁLOGO DE RUTAS CREADAS) */}
                        <div className="space-y-1">
                          <div className="flex items-center justify-between">
                            <label className="text-[10px] font-bold text-slate-500 uppercase">
                              Seleccionar Destino de Rutas Registradas:
                            </label>
                            {formClientId && (
                              <span className="text-[9px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                                {clientSpecificDestinations.length} del cliente • {generalDestinations.length} generales
                              </span>
                            )}
                          </div>
                          <select
                            value={selectedDestinationId}
                            onChange={(e) => {
                              const val = e.target.value;
                              setSelectedDestinationId(val);
                              if (val === 'CUSTOM') {
                                // Keep manual input
                              } else {
                                const dest = (destinations || []).find(d => d.id === val);
                                if (dest) {
                                  setFormDestination(`${dest.name} (${dest.address})`);
                                  setFormDestinationLat(dest.lat);
                                  setFormDestinationLng(dest.lng);
                                  setFormRouteNotes(dest.routeNotes || '');
                                  setFormEstimatedKm(dest.estimatedKm);
                                }
                              }
                            }}
                            className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:outline-hidden focus:border-red-600 shadow-2xs cursor-pointer"
                          >
                            <option value="">-- Seleccionar Destino (Panel de Destinos y Rutas) --</option>
                            {destinations && destinations.length > 0 ? (
                              <>
                                {formClientId && clientSpecificDestinations.length > 0 && (
                                  <optgroup label={`🏢 Destinos de ${clients.find(c => c.id === formClientId)?.name || 'Cliente Seleccionado'}`}>
                                    {clientSpecificDestinations.map(dest => (
                                      <option key={dest.id} value={dest.id}>
                                        📍 {dest.name} - {dest.address}
                                      </option>
                                    ))}
                                  </optgroup>
                                )}
                                {generalDestinations.length > 0 && (
                                  <optgroup label="🌐 Destinos y Plantas Generales">
                                    {generalDestinations.map(dest => (
                                      <option key={dest.id} value={dest.id}>
                                        📍 {dest.name} - {dest.address}
                                      </option>
                                    ))}
                                  </optgroup>
                                )}
                                {formClientId && otherClientDestinations.length > 0 && (
                                  <optgroup label="🏢 Rutas de Otros Clientes">
                                    {otherClientDestinations.map(dest => {
                                      const clientLabel = dest.clientIds && dest.clientIds.length > 0
                                        ? ` [${dest.clientIds.map(cid => clients.find(c => c.id === cid || c.code === cid)?.code || cid).join(', ')}]`
                                        : (dest.clientName ? ` [${dest.clientName}]` : '');
                                      return (
                                        <option key={dest.id} value={dest.id}>
                                          📍 {dest.name}{clientLabel} - {dest.address}
                                        </option>
                                      );
                                    })}
                                  </optgroup>
                                )}
                                {!formClientId && (
                                  destinations.map(dest => {
                                    const clientLabel = dest.clientIds && dest.clientIds.length > 0
                                      ? ` [🏢 ${dest.clientIds.map(cid => clients.find(c => c.id === cid || c.code === cid)?.code || cid).join(', ')}]`
                                      : (dest.clientName ? ` [🏢 ${dest.clientName}]` : ' [🌐 General]');
                                    return (
                                      <option key={dest.id} value={dest.id}>
                                        📍 {dest.name}{clientLabel} - {dest.address}
                                      </option>
                                    );
                                  })
                                )}
                              </>
                            ) : (
                              <option value="" disabled>⚠️ No hay rutas ni destinos creados aún</option>
                            )}
                            <option value="CUSTOM">✏️ Escribir Dirección Personalizada</option>
                          </select>
                        </div>

                        {/* DIRECCIÓN DE DESTINO */}
                        <div className="space-y-1">
                          <label className="text-[11px] font-bold text-slate-700 flex items-center justify-between">
                            <span>Nombre / Dirección de Destino 1 *</span>
                          </label>
                          <input
                            type="text"
                            required
                            value={formDestination}
                            onChange={(e) => setFormDestination(e.target.value)}
                            placeholder="Planta CEDIS Ramos Arizpe..."
                            className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-red-600 font-medium"
                          />
                        </div>
                      </div>

                      {formDestinationLat !== undefined && formDestinationLng !== undefined && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-[10px] text-red-900 font-mono flex items-center justify-between mt-2">
                          <span>🏁 GPS Destino: <strong className="font-bold">{formDestinationLat.toFixed(4)}, {formDestinationLng.toFixed(4)}</strong></span>
                          <span>{formEstimatedKm || 45} km est.</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* DOBLE DESTINO TOGGLE & SECUNDARIO */}
                  <div className="bg-white p-3.5 rounded-xl border border-slate-200 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h5 className="text-xs font-bold text-slate-800">¿El folio va para 2 destinos diferentes?</h5>
                        <p className="text-[10px] text-slate-500">Divide la entrega en dos plantas o CEDIS de destino</p>
                      </div>
                      <div className="flex rounded-lg overflow-hidden border border-slate-200">
                        <button
                          type="button"
                          onClick={() => setFormIsDoubleDestination(false)}
                          className={`px-4 py-1.5 text-xs font-semibold transition-all cursor-pointer ${
                            !formIsDoubleDestination 
                              ? 'bg-blue-600 text-white font-bold' 
                              : 'bg-white text-slate-600 hover:bg-gray-50'
                          }`}
                        >
                          No
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormIsDoubleDestination(true)}
                          className={`px-4 py-1.5 text-xs font-semibold transition-all cursor-pointer ${
                            formIsDoubleDestination 
                              ? 'bg-blue-600 text-white font-bold' 
                              : 'bg-white text-slate-600 hover:bg-gray-50'
                          }`}
                        >
                          Sí (Doble)
                        </button>
                      </div>
                    </div>

                    {formIsDoubleDestination && (
                      <div className="space-y-1 pt-2 border-t border-slate-100">
                        <label className="text-xs font-bold text-slate-700">Dirección de Destino 2 (Secundario) *</label>
                        <input
                          type="text"
                          required
                          value={formDestinationSecondary}
                          onChange={(e) => setFormDestinationSecondary(e.target.value)}
                          placeholder="Planta Apodaca NL..."
                          className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-blue-500 font-medium animate-fade-in"
                        />
                      </div>
                    )}
                  </div>

                  {/* RUTA ACTIVA INFO & LIVE INTERACTIVE ROUTE PREVIEW MAP */}
                  {formDestinationLat && formDestinationLng && (
                    <div className="bg-white border border-slate-200/90 rounded-2xl p-3.5 space-y-2 shadow-2xs">
                      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-2.5 text-xs text-emerald-900 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Route className="h-4 w-4 text-emerald-600 shrink-0" />
                          <div>
                            <p className="font-bold text-[11px] text-emerald-950">Ruta Trazo Activa: {formOrigin || 'Origen'} ➔ {formDestination || 'Destino'}</p>
                            <p className="text-[10px] text-emerald-700 font-mono">
                              Recorrido Est.: <strong>{formEstimatedKm || 45} km</strong> (~{Math.round((formEstimatedKm || 45) * 1.3)} min)
                            </p>
                            {formRouteNotes && (
                              <p className="text-[9px] text-emerald-700 italic mt-0.5">Vía / Observaciones: {formRouteNotes}</p>
                            )}
                          </div>
                        </div>
                        <span className="bg-emerald-600 text-white font-mono text-[9px] font-black px-2.5 py-1 rounded-md uppercase tracking-wider">
                          RUTA ACTIVA GPS
                        </span>
                      </div>

                      <div className="space-y-1.5 pt-2 border-t border-slate-200/80">
                        <label className="text-xs font-bold text-slate-800 flex items-center justify-between">
                          <span className="flex items-center gap-1.5 text-blue-800 font-mono">
                            <Map className="h-4 w-4 text-blue-600" /> Vista Previa de Ruta Trazada (Origen ➔ Destino GPS Live)
                          </span>
                          <span className="text-[10px] bg-blue-100 text-blue-800 font-bold px-2 py-0.5 rounded-full font-mono">
                            GPS Trazado Automático
                          </span>
                        </label>
                        <div className="h-64 border border-slate-200 rounded-2xl overflow-hidden shadow-inner relative">
                          <LogisticsMap
                            folios={[]}
                            costCenters={costCenters}
                            currentUser={currentUser as any}
                            destinations={destinations}
                            hideSidebarConsole={true}
                            previewDestination={{
                              name: (destinations || []).find(d => d.id === selectedDestinationId)?.name || formDestination,
                              address: formDestination,
                              lat: formDestinationLat,
                              lng: formDestinationLng,
                              originLat: formOriginLat !== undefined ? formOriginLat : 25.7820,
                              originLng: formOriginLng !== undefined ? formOriginLng : -100.4050,
                              originName: formOrigin || '🏢 Origen de Partida',
                              routeNotes: formRouteNotes,
                              estimatedKm: formEstimatedKm
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Items Picker and table */}
                <div className="space-y-3 pt-2 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1">
                      📦 Mercancías en Recepción <span className="bg-blue-500 text-white text-[10px] px-2 py-0.5 rounded-full font-mono font-bold">{formItems.length}</span>
                    </h4>
                    <span className="text-[10px] text-slate-500">Agrega al menos una mercancía para registrar</span>
                  </div>

                  {/* Smart Search Dropdown Frame */}
                  <div className="bg-slate-50/70 p-4 rounded-xl border border-slate-200/60 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      
                      {/* Catalog Search */}
                      <div className="space-y-1 relative">
                        <label className="text-[10px] font-bold text-slate-500 uppercase">1. Buscar en Catálogo de Mercancías</label>
                        <input
                          type="text"
                          value={itemSearchQuery}
                          onChange={(e) => setItemSearchQuery(e.target.value)}
                          placeholder="🔍 Filtrar catálogo por nombre o código..."
                          className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-hidden focus:border-blue-500"
                        />
                        
                        {/* Suggestions list */}
                        {itemSearchQuery && (
                          <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto z-50">
                            {filteredGoodsForForm.filter(g => 
                              (g.description.toLowerCase().includes(itemSearchQuery.toLowerCase()) || g.code.toLowerCase().includes(itemSearchQuery.toLowerCase()))
                            ).length > 0 ? (
                              filteredGoodsForForm.filter(g => 
                                (g.description.toLowerCase().includes(itemSearchQuery.toLowerCase()) || g.code.toLowerCase().includes(itemSearchQuery.toLowerCase()))
                              ).map(g => (
                                <button
                                  type="button"
                                  key={g.id}
                                  onClick={() => {
                                    setItemSelectedCatalogId(g.id);
                                    setItemConceptName(g.description);
                                    setItemPrice(g.priceMXN);
                                    setItemWeight(g.weightKg || 0);
                                    setItemUnit(g.unit || 'PZA (Pieza)');
                                    setItemSearchQuery(''); // clear
                                  }}
                                  className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 border-b border-slate-100 last:border-0 flex justify-between items-center cursor-pointer"
                                >
                                  <div>
                                    <span className="font-bold text-slate-900">{g.description}</span>
                                    <span className="text-[9px] text-slate-500 ml-1.5 font-mono bg-slate-100 px-1 py-0.2 rounded">{g.code}</span>
                                  </div>
                                  {currentUser?.role === 'CHOFER' ? (
                                    <span className="text-[10px] text-slate-400 font-bold font-mono">🔒 Restringido</span>
                                  ) : (
                                    <span className="text-[10px] text-blue-600 font-bold font-mono">${g.priceMXN} MXN</span>
                                  )}
                                </button>
                              ))
                            ) : (
                              <div className="p-3 text-center text-slate-400 text-xs">
                                No se encontraron items. ¡Escribe abajo para crear uno personalizado!
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Item Concept Name */}
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-500 uppercase">2. Nombre o Concepto de la Mercancía *</label>
                        <input
                          type="text"
                          required
                          value={itemConceptName}
                          onChange={(e) => {
                            setItemConceptName(e.target.value);
                            if (itemSelectedCatalogId) {
                              const match = goods.find(g => g.id === itemSelectedCatalogId);
                              if (match && match.description !== e.target.value) {
                                setItemSelectedCatalogId('');
                              }
                            }
                          }}
                          placeholder="Nombre del producto o nuevo item..."
                          className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-hidden focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Quantity */}
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-500 uppercase">Cantidad</label>
                        <input
                          type="number"
                          min="1"
                          value={itemQuantity}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '') {
                              setItemQuantity('');
                            } else {
                              const parsed = parseInt(val, 10);
                              setItemQuantity(isNaN(parsed) ? '' : parsed);
                            }
                          }}
                          placeholder="Ej. 10"
                          className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs font-semibold focus:outline-hidden focus:border-blue-500"
                        />
                      </div>

                      {/* Unit */}
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-500 uppercase">Unidad</label>
                        <select
                          value={itemUnit}
                          onChange={(e) => setItemUnit(e.target.value)}
                          className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-hidden focus:border-blue-500 font-medium cursor-pointer"
                        >
                          <option value="PZA (Pieza)">PZA (Pieza)</option>
                          <option value="KGS (Kilogramos)">KGS (Kilogramos)</option>
                          <option value="LTS (Litros)">LTS (Litros)</option>
                          <option value="PAQ (Paquete)">PAQ (Paquete)</option>
                          <option value="CAJ (Cajas)">CAJ (Cajas)</option>
                        </select>
                      </div>
                    </div>

                    {!itemSelectedCatalogId && itemConceptName && (
                      <p className="text-[10px] text-amber-600 font-bold bg-amber-50 px-2.5 py-1 rounded-md border border-amber-100 flex items-center gap-1">
                        ✨ Registrando como <strong>"Nuevo Item"</strong> temporal para este envío (No catalogado).
                      </p>
                    )}

                    <div className="flex justify-end pt-1">
                      <button
                        type="button"
                        onClick={() => {
                          if (!itemConceptName.trim()) return;
                          
                          const parsedQty = typeof itemQuantity === 'number' ? itemQuantity : (parseInt(String(itemQuantity), 10) || 1);
                          const qtyNum = parsedQty > 0 ? parsedQty : 1;

                          if (itemSelectedCatalogId) {
                            const exists = formItems.find(it => it.goodsItemId === itemSelectedCatalogId);
                            if (exists) {
                              setFormItems(prev => prev.map(it => {
                                if (it.goodsItemId === itemSelectedCatalogId) {
                                  return { ...it, quantity: it.quantity + qtyNum };
                                }
                                return it;
                              }));
                            } else {
                              setFormItems([...formItems, { goodsItemId: itemSelectedCatalogId, quantity: qtyNum }]);
                            }
                          } else {
                            const customId = `custom_${Date.now()}`;
                            setFormItems([...formItems, {
                              goodsItemId: customId,
                              quantity: qtyNum,
                              customDescription: itemConceptName,
                              customPrice: itemPrice,
                              customWeight: itemWeight,
                              photoUrl: ''
                            }]);
                          }

                          setItemConceptName('');
                          setItemQuantity('');
                          setItemPrice(0);
                          setItemWeight(0);
                          setItemSelectedCatalogId('');
                        }}
                        disabled={!itemConceptName.trim()}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-1.5 px-4 rounded-lg text-xs transition-all flex items-center gap-1.5 cursor-pointer disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed shadow-tiny"
                      >
                        <FilePlus className="h-4 w-4" /> Agregar Producto
                      </button>
                    </div>

                  </div>
                </div>

                {/* Selected Form Items Table */}
                <div className="space-y-2">
                  {formItems.length === 0 ? (
                    <p className="text-xs text-center text-slate-400 py-4 italic bg-slate-50 rounded-xl border border-dashed border-slate-200/80">
                      No se han añadido mercancías de recepción todavía. Agregue productos en el buscador superior.
                    </p>
                  ) : (() => {
                    const totalPieces = formItems.reduce((acc, it) => acc + (Number(it.quantity) || 0), 0);
                    const totalAmount = formItems.reduce((acc, it) => {
                      const catalogItem = goods.find(g => g.id === it.goodsItemId);
                      const numPrice = Number(it.customPrice ?? catalogItem?.priceMXN ?? 0) || 0;
                      return acc + (numPrice * (Number(it.quantity) || 0));
                    }, 0);

                    return (
                      <div className="border border-slate-200 rounded-xl overflow-hidden shadow-tiny">
                        <table className="w-full text-left border-collapse text-xs">
                          <thead>
                            <tr className="bg-slate-100 text-slate-700 font-extrabold border-b border-slate-200">
                              <th className="p-2.5">Mercancía / Concepto</th>
                              <th className="p-2.5 text-center">Cant.</th>
                              <th className="p-2.5 text-center">Unidad</th>
                              {currentUser?.role !== 'CHOFER' && (
                                <>
                                  <th className="p-2.5 text-right">Precio Unit.</th>
                                  <th className="p-2.5 text-right">Total</th>
                                </>
                              )}
                              <th className="p-2.5 text-center">Acciones</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 bg-white">
                            {formItems.map((item, idx) => {
                              const catalogItem = goods.find(g => g.id === item.goodsItemId);
                              const desc = item.customDescription || catalogItem?.description || 'Mercancía';
                              const numPrice = Number(item.customPrice ?? catalogItem?.priceMXN ?? 0) || 0;
                              const unit = catalogItem?.unit || 'Pza';
                              const numTotal = numPrice * (Number(item.quantity) || 0);
                              
                              return (
                                <tr key={idx} className="hover:bg-slate-50/50">
                                  <td className="p-2.5 font-semibold text-slate-800">
                                    {desc}
                                    {!catalogItem && (
                                      <span className="text-[8px] bg-amber-100 text-amber-800 ml-1.5 px-1 rounded font-extrabold uppercase">Nuevo</span>
                                    )}
                                  </td>
                                  <td className="p-2.5 text-center font-bold text-blue-700 w-24">
                                    <input
                                      type="number"
                                      min="1"
                                      value={item.quantity === 0 ? '' : item.quantity}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        const numVal = val === '' ? 0 : (parseInt(val, 10) || 0);
                                        setFormItems(prev => prev.map((it, i) => i === idx ? { ...it, quantity: numVal } : it));
                                      }}
                                      placeholder="Cant."
                                      className="w-16 mx-auto bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 text-center text-xs font-bold text-blue-700 focus:outline-hidden focus:border-blue-500"
                                    />
                                  </td>
                                  <td className="p-2.5 text-center text-slate-500">{unit}</td>
                                  {currentUser?.role !== 'CHOFER' && (
                                    <>
                                      <td className="p-2.5 text-right font-mono text-slate-600">
                                        {numPrice > 0 ? `$${numPrice.toFixed(2)}` : '—'}
                                      </td>
                                      <td className="p-2.5 text-right font-mono font-bold text-slate-900">
                                        {numTotal > 0 ? `$${numTotal.toFixed(2)}` : '—'}
                                      </td>
                                    </>
                                  )}
                                  <td className="p-2.5 text-center">
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveFormItem(idx)}
                                      className="p-1 hover:bg-red-50 text-red-500 hover:text-red-700 rounded transition-colors cursor-pointer"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot className="bg-slate-100/90 border-t-2 border-slate-300 font-bold text-slate-800">
                            <tr>
                              <td className="p-2.5 font-black text-slate-900 text-xs">
                                TOTALES ({formItems.length} {formItems.length === 1 ? 'partida' : 'partidas'})
                              </td>
                              <td className="p-2.5 text-center font-black text-blue-900 font-mono text-xs">
                                <span className="inline-block bg-blue-100 text-blue-900 border border-blue-300 px-2 py-0.5 rounded-md font-black">
                                  {totalPieces.toLocaleString()}
                                </span>
                              </td>
                              <td className="p-2.5 text-center text-slate-400 font-medium">—</td>
                              {currentUser?.role !== 'CHOFER' && (
                                <>
                                  <td className="p-2.5 text-right font-bold text-slate-600 text-[11px]">
                                    Total:
                                  </td>
                                  <td className="p-2.5 text-right font-mono font-black text-slate-900 text-xs sm:text-sm">
                                    ${totalAmount.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                </>
                              )}
                              <td className="p-2.5 text-center"></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    );
                  })()}
                </div>

                {/* Photo evidence for reception (Max 5 photos) */}
                <div className="space-y-2 border-t border-slate-100 pt-4">
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                    📸 Fotografía de Evidencia de Recolección (Máximo 5)
                  </h4>
                  <p className="text-[10px] text-slate-500">
                    Captura hasta 5 fotografías de la mercancía certificada en campo para integrarse automáticamente al PDF oficial.
                  </p>

                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    {Array.from({ length: 5 }).map((_, slotIdx) => {
                      const photoVal = formPhotoUrls[slotIdx];
                      const offsetIdx = -(slotIdx + 1); // negative offsets -1 to -5

                      return (
                        <div 
                          key={slotIdx} 
                          className="border border-dashed border-slate-300 rounded-xl bg-slate-50/50 h-28 flex flex-col items-center justify-center relative overflow-hidden group shadow-tiny hover:bg-slate-100/50 transition-all"
                        >
                          {photoVal ? (
                            <>
                              <img 
                                src={photoVal} 
                                alt={`Evidencia #${slotIdx + 1}`} 
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  setFormPhotoUrls(prev => {
                                    const next = [...prev];
                                    next[slotIdx] = '';
                                    return next;
                                  });
                                  if (slotIdx === 0) {
                                    setFormPhotoUrl('');
                                  }
                                }}
                                className="absolute top-1.5 right-1.5 bg-red-600 text-white p-1 rounded-full shadow-md hover:bg-red-700 transition-colors cursor-pointer"
                                title="Eliminar foto"
                              >
                                <X className="h-3 w-3" />
                              </button>
                              <div className="absolute bottom-1 left-1.5 bg-slate-900/70 text-white text-[8px] font-bold px-1.5 py-0.5 rounded">
                                Foto #{slotIdx + 1}
                              </div>
                            </>
                          ) : (
                            <div className="flex flex-col items-center justify-center p-2 text-center space-y-1">
                              <span className="text-[9px] font-extrabold text-slate-400 uppercase tracking-tight">FOTO #{slotIdx + 1}</span>
                              <div className="flex gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setActivePhotoIndex(offsetIdx);
                                    startWebcam();
                                  }}
                                  className="p-1 bg-white hover:bg-blue-50 text-slate-600 hover:text-blue-600 border border-slate-200 rounded-lg shadow-tiny transition-all cursor-pointer"
                                  title="Tomar Foto"
                                >
                                  <Camera className="h-3.5 w-3.5" />
                                </button>
                                <label 
                                  className="p-1 bg-white hover:bg-blue-50 text-slate-600 hover:text-blue-600 border border-slate-200 rounded-lg shadow-tiny transition-all cursor-pointer"
                                  title="Subir Imagen"
                                >
                                  <Upload className="h-3.5 w-3.5" />
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => handleFileUpload(e, offsetIdx)}
                                  />
                                </label>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-1 border-t border-slate-100 pt-4">
                  <label className="text-xs font-bold text-slate-700">Notas de Recepción / Instrucciones Especiales</label>
                  <textarea
                    value={formNotes}
                    onChange={(e) => setFormNotes(e.target.value)}
                    placeholder="Escribe especificaciones de decapado, estado de carga o indicaciones del cliente..."
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-blue-500 h-20 font-medium"
                  />
                </div>

              </div>

              {/* Modal Footer */}
              <div className="bg-slate-50 p-4 border-t border-slate-100 flex items-center justify-between shrink-0">
                <button
                  type="button"
                  onClick={() => setIsCreating(false)}
                  className="px-4 py-2 border border-slate-200 hover:bg-slate-100 text-slate-600 rounded-xl text-xs font-bold transition-all cursor-pointer"
                >
                  Cancelar y Cerrar
                </button>
                <button
                  type="button"
                  onClick={(e) => handleCreateFolioSubmit(e)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-5 rounded-xl text-xs transition-all shadow-tiny flex items-center gap-1.5 cursor-pointer"
                >
                  Crear Folio QR e Iniciar Logística 🚀
                </button>
              </div>

            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* LIGHTBOX FOR PHOTO VIEW */}
      <AnimatePresence>
        {selectedPhotoView && (
          <div 
            className="fixed inset-0 bg-black/85 backdrop-blur-xs flex items-center justify-center z-50 p-4 cursor-zoom-out"
            onClick={() => setSelectedPhotoView(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-4xl max-h-[85vh] overflow-hidden bg-transparent"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedPhotoView}
                alt="Evidencia Alta Resolución"
                className="max-w-full max-h-[80vh] object-contain rounded-xl shadow-2xl border border-white/10"
              />
              <div className="absolute top-3 right-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedPhotoView(null)}
                  className="bg-black/60 hover:bg-black/80 text-white rounded-full p-2 text-xs font-bold shadow-md cursor-pointer"
                >
                  ✕ Cerrar Vista
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MAP MODAL FOR ENCAPSULATED ROUTE TRACKING */}
      {showEncapsulatedMapModal && (
        <FolioRealtimeMapModal
          foliosList={folios.filter(f => selectedFoliosForRoute.includes(f.id))}
          costCenters={costCenters}
          destinations={destinations}
          currentUser={currentUser}
          onClose={() => setShowEncapsulatedMapModal(false)}
        />
      )}

      {/* PO / ORDEN DE COMPRA INTEGRATION MODAL */}
      <AnimatePresence>
        {isIntegratePoModalOpen && targetFolioForPo && (
          <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-xs flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 10 }}
              className="bg-white rounded-3xl max-w-lg w-full overflow-hidden shadow-2xl border border-slate-200"
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-slate-900 via-purple-950 to-slate-900 text-white p-5 flex items-center justify-between border-b border-purple-900/50">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-purple-600/40 text-purple-300 rounded-2xl border border-purple-500/30">
                    <Link2 className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-black tracking-tight text-white flex items-center gap-2">
                      Integrar Folio a Orden de Compra
                    </h3>
                    <p className="text-xs text-purple-200/80 font-medium">
                      Asigna este folio a una PO para calcular saldos y cambiar el estado del pedido.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setIsIntegratePoModalOpen(false);
                    setTargetFolioForPo(null);
                  }}
                  className="p-1.5 hover:bg-white/10 rounded-xl text-slate-400 hover:text-white transition-colors cursor-pointer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-5 space-y-4">
                {/* Folio Summary Card */}
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3.5 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-wider">Folio Seleccionado</span>
                    <span className="font-mono text-xs font-black bg-slate-200 text-slate-900 px-2 py-0.5 rounded-md">
                      {targetFolioForPo.id}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-[10px] text-slate-400 font-bold uppercase block">Cliente</span>
                      <span className="font-extrabold text-slate-800 truncate block">{targetFolioForPo.clientName || 'General'}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 font-bold uppercase block">Estado Actual de PO</span>
                      {isFolioIntegratedToPo(targetFolioForPo) ? (
                        <span className="font-mono font-black text-purple-700 bg-purple-50 px-2 py-0.5 rounded border border-purple-200 text-[11px] block truncate">
                          PO: {targetFolioForPo.poNumber}
                        </span>
                      ) : (
                        <span className="font-bold text-amber-800 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 text-[11px] block">
                          ⚠️ No integrado a PO
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Selection of PO */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-700 flex items-center justify-between">
                    <span>Seleccionar Orden de Compra Existente</span>
                    <span className="text-[10px] font-medium text-slate-400 font-mono">
                      {poRecordsList.length} PO(s) registradas
                    </span>
                  </label>

                  {poRecordsList.length > 0 ? (
                    <select
                      value={selectedPoOption}
                      onChange={(e) => {
                        setSelectedPoOption(e.target.value);
                        if (e.target.value !== 'CUSTOM') {
                          setCustomPoNumberInput(e.target.value);
                        }
                      }}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-900 focus:outline-none focus:border-purple-500 cursor-pointer"
                    >
                      <option value="CUSTOM">✍️ Ingresar Número de PO Manualmente</option>
                      {poRecordsList.map((po) => (
                        <option key={po.id || po.poNumber} value={po.poNumber}>
                          {po.poNumber} — {po.supplierName || po.clientName || 'Sin Proveedor'} ({po.currency || 'MXN'} ${(po.totalAmount || 0).toLocaleString()})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="text-[11px] text-slate-500 bg-slate-50 p-2.5 rounded-xl border border-slate-200">
                      No hay POs registradas en el catálogo. Puedes escribir el número de PO directamente a continuación.
                    </div>
                  )}
                </div>

                {/* Input for Manual / Selected PO Number */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700">
                    Número de PO / Orden de Compra <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={selectedPoOption === 'CUSTOM' ? customPoNumberInput : selectedPoOption}
                    onChange={(e) => {
                      setSelectedPoOption('CUSTOM');
                      setCustomPoNumberInput(e.target.value);
                    }}
                    placeholder="Ej. PO-2025-BMW-001 o OC-88234"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-mono font-bold text-purple-950 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                  />
                  <p className="text-[10px] text-slate-500">
                    Al integrar la PO, el estado del folio se habilitará con sus fases normales de transporte y entrega.
                  </p>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="bg-slate-50 p-4 border-t border-slate-100 flex items-center justify-between gap-2">
                <div>
                  {isFolioIntegratedToPo(targetFolioForPo) && (
                    <button
                      type="button"
                      onClick={() => handleRemovePoIntegration(targetFolioForPo)}
                      className="px-3 py-2 text-rose-700 hover:bg-rose-50 border border-rose-200 rounded-xl text-xs font-bold transition-all cursor-pointer"
                    >
                      Desvincular PO
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsIntegratePoModalOpen(false);
                      setTargetFolioForPo(null);
                    }}
                    className="px-4 py-2 border border-slate-200 hover:bg-slate-100 text-slate-700 rounded-xl text-xs font-bold transition-all cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleSavePoIntegration}
                    className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-black transition-all shadow-xs flex items-center gap-1.5 cursor-pointer"
                  >
                    <Link2 className="h-4 w-4" />
                    Guardar e Integrar a PO
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}
